import { supabase } from './supabase'
import { getDaysSince, getLocalDateString } from './date'
import { db } from './db'
import { invalidateQuery, invalidateQueryPrefix } from '../hooks/useQueryCore';
import { generateUUID } from './uuid';
import { logVisit, logPrayer, logEvent } from './activity-log';
import { localCache } from './cache';
import { crashLogger } from './crash-logger';

export { localCache };

export async function getCurrentUserId() {
  const { data: { user } } = await supabase.auth.getUser().catch(() => ({ data: {} }));
  return user?.id || null;
}

// ─── Ministry Eligibility — single source of truth ───────────────────────────
// Both pickers (PersonPicker) and mutations (updateDepartmentRole, updatePerson)
// consult this function. Adding a new eligibility condition here propagates everywhere.
const _BAPTIZED_MINISTRY = new Set(['bautizado', 'rebautizado']);
const _DISQUALIFIED_SPIRITUAL = new Set(['a rescatar']);

export function isEligibleForMinistry(person) {
  if (!person) return false;
  return _BAPTIZED_MINISTRY.has(person.baptism_status) &&
         !_DISQUALIFIED_SPIRITUAL.has(person.spiritual_status);
}
// ─────────────────────────────────────────────────────────────────────────────

// Notificador de cambios en la cola offline para la UI (local + cross-tab).
// CustomEvent notifies same-tab listeners (useOnlineSync, SyncStatus).
// BroadcastChannel propagates to sibling tabs so their pendingCount stays accurate
// after Tab B syncs items that were queued in the shared IndexedDB.
const notifyOutboxChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('outbox-changed'));
    try {
      if (!window.__kevesOutboxChannel) {
        window.__kevesOutboxChannel = new BroadcastChannel('keves_outbox');
      }
      window.__kevesOutboxChannel.postMessage({ type: 'outbox-changed' });
    } catch { /* BroadcastChannel unavailable (private mode, old browser) — degrade silently */ }
  }
};
// Helper para cola de sincronización offline (Outbox)
// Campos del payload que pueden contener una referencia a un ID generado offline.
// Usados tanto para el dependency graph como para el remapeo tempId→realId.
//
// IMPORTANTE: Agregar aquí CUALQUIER campo nuevo que contenga un UUID referenciando
// otra tabla. Si se omite un campo, la sincronización offline puede generar FKs rotas
// silenciosas en Supabase.
const RELATIONAL_ID_FIELDS = [
  'target_person_id', 'target_family_id',          // visits → people / families
  'person_id', 'family_id', 'church_id',            // múltiples tablas
  'material_id', 'category_id',                     // materiales
  'loan_id', 'allocation_id', 'subscription_id',    // préstamos / distribuciones / suscripciones
  'roster_id',                                      // preaching_assignments → preaching_rosters
  'reference_id',       // material_movements → church_material_allocations u otros recursos
  'borrower_entity_id', // loans → people / families (entidad prestataria)
];

// Fast-path: skip the Dexie round-trip in mergeOutboxItems when outbox is known empty.
// Reset to false on any push. Self-corrects on next mergeOutboxItems read.
let _outboxKnownEmpty = false;

const localOutbox = {
  // Devuelve items en orden de inserción (timestamp ASC) para respetar causalidad.
  // CRÍTICO: toArray() devuelve por primary key (UUID aleatorio) — NO es orden causal.
  get: async () => await db.outbox.orderBy('timestamp').toArray(),

  push: async (item) => {
    _outboxKnownEmpty = false;
    // Structural guard: reject malformed items before they enter the outbox.
    // A bad action or missing payload will fail at sync time anyway — catching here
    // surfaces the bug at the mutation call site instead of silently corrupting the queue.
    if (!item.table || typeof item.table !== 'string') throw new Error('[outbox] missing table');
    if (!['CREATE', 'UPDATE', 'DELETE'].includes(item.action)) throw new Error(`[outbox] unknown action "${item.action}"`);
    if (item.action !== 'DELETE' && (!item.payload || typeof item.payload !== 'object' || Array.isArray(item.payload))) {
      throw new Error(`[outbox] payload required for ${item.action}`);
    }

    // Dependency graph: detectar si este item depende de un CREATE pendiente.
    // Un item depende de otro si su targetId o cualquier campo relacional del payload
    // contiene el tempId (payload.id) de un CREATE que todavía no se ha sincronizado.
    // Esto evita que UPDATE/CREATE dependientes se procesen antes de que su CREATE padre resuelva.
    const existingItems = await db.outbox.toArray();
    // Recolectar tempIds de CREATEs pendientes para construir el grafo de dependencias.
    // Usamos tempIds lógicos (payload.id de cada CREATE pendiente), NO IDs de outbox.
    // Razón: los IDs de outbox son efímeros — si el app crashea y recarga, los items
    // siguen en Dexie pero el grafo construido con IDs de outbox sobreviviría intacto,
    // mientras que uno basado en tempIds es robusto porque se verifica contra el estado
    // actual del outbox en cada sync (buscamos CREATE con ese payload.id).
    const pendingCreateTempIds = new Set(
      existingItems
        .filter(i => i.action === 'CREATE' && i.payload?.id)
        .map(i => i.payload.id)
    );

    // dependsOnTempIds: tempIds de los CREATE pendientes de los que depende este item.
    const dependsOnTempIds = [];

    // Verificar targetId (UPDATE/DELETE sobre un recurso creado offline)
    if (item.targetId && pendingCreateTempIds.has(item.targetId)) {
      dependsOnTempIds.push(item.targetId);
    }

    // Verificar campos relacionales del payload
    for (const field of RELATIONAL_ID_FIELDS) {
      const val = item.payload?.[field];
      if (val && pendingCreateTempIds.has(val) && !dependsOnTempIds.includes(val)) {
        dependsOnTempIds.push(val);
      }
    }

    await db.outbox.add({
      ...item,
      id: generateUUID(),
      timestamp: Date.now(),
      dependsOnTempIds, // [] si sin dependencias, ['tempId', ...] si tiene
    });
    notifyOutboxChange();
  },

  remove: async (id) => {
    await db.outbox.delete(id);
    notifyOutboxChange();
  }
};

// Tracks outbox item IDs currently in-flight during syncOfflineData.
// mergeOutboxItems reads this to set _syncing on the correct items.
const syncingIds =
  typeof window !== 'undefined'
    ? (window.__kevesSyncingIds ||= new Set())
    : new Set();

export const getOutboxCount = async () => {
  return await db.outbox.count();
};

export const getOutboxStatus = async () => {
  const items = await db.outbox.toArray();
  const now = Date.now();
  const failed = items.filter(i => (i.retries || 0) >= 5).length;
  // Items en backoff: retriables pero esperando su nextRetryAt antes de volver a intentar
  const waiting = items.filter(
    i => (i.retries || 0) < 5 && i.nextRetryAt && i.nextRetryAt > now
  ).length;
  return {
    total: items.length,
    failed,
    waiting,   // en backoff — se reintentarán automáticamente
    pending: items.length - failed - waiting,
  };
};

export const retrySync = async () => {
  const items = await db.outbox.toArray();
  const failedItems = items.filter(i => (i.retries || 0) >= 5);
  for (const item of failedItems) {
    await db.outbox.update(item.id, { retries: 0, lastError: null, nextRetryAt: null });
  }
  if (failedItems.length > 0) notifyOutboxChange();
  await syncOfflineData();
};

// O3: Current sync state for debugging and watchdog detection in useOfflineStatus.
// syncStartedAt is null when idle; non-null while syncOfflineData holds the lock.
export const getSyncDiagnostics = () => ({
  isSyncing,
  syncStartedAt,
  syncDurationMs: syncStartedAt ? Date.now() - syncStartedAt : 0,
  lockHeld: !!safeLocalStorage.getItem(SYNC_LOCK_KEY),
});

// T1: Release the cross-tab lock manually. Use when a tab crashed mid-sync and the
// 45s TTL is too long to wait. Safe to call even when no lock is held.
export const forceUnlock = () => {
  safeLocalStorage.removeItem(SYNC_LOCK_KEY);
};

// T1: Delete all permanently-failed outbox items (retries >= 5). Returns the count removed.
// Throws if a sync cycle is currently running to avoid deleting in-flight items.
export const purgeFailedItems = async () => {
  if (isSyncing) throw new Error('No se puede purgar mientras la sincronización está activa');
  const items = await db.outbox.toArray();
  const failed = items.filter(i => (i.retries || 0) >= 5);
  for (const item of failed) {
    await db.outbox.delete(item.id);
  }
  if (failed.length > 0) notifyOutboxChange();
  return failed.length;
};

// T1: Validate and repair corrupted outbox items in-place without deleting them.
// Marks unknown-action items as permanent failures. Resets non-array dependsOnTempIds.
// Returns an array of repair report entries: { id, issue, action? }.
export const repairOutbox = async () => {
  const items = await db.outbox.toArray();
  const report = [];
  for (const item of items) {
    const updates = {};
    if (!['CREATE', 'UPDATE', 'DELETE'].includes(item.action)) {
      updates.retries = 5;
      updates.lastError = `Acción desconocida reparada: "${item.action}"`;
      report.push({ id: item.id, issue: 'unknown_action', action: item.action });
    }
    if (!Array.isArray(item.dependsOnTempIds)) {
      updates.dependsOnTempIds = [];
      report.push({ id: item.id, issue: 'corrupted_deps' });
    }
    if (!Array.isArray(item.cacheKeys)) {
      updates.cacheKeys = [];
      report.push({ id: item.id, issue: 'missing_cache_keys' });
    }
    if (Object.keys(updates).length > 0) {
      await db.outbox.update(item.id, updates);
    }
  }
  if (report.length > 0) notifyOutboxChange();
  return report;
};

// Helper para limpiar payloads (convertir strings vacíos a null para Supabase)
const sanitizePayload = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  const cleaned = { ...payload };
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === '') {
      cleaned[key] = null;
    }
  });
  return cleaned;
};

// Injects pending outbox changes into query results.
// Each affected item gets three flags:
//   _is_offline: true  → item has local changes not yet confirmed by server
//   _syncing: true     → currently being sent to Supabase right now
//   _error: true       → exceeded max retries, needs manual intervention
const mergeOutboxItems = async (data, table, filterFn) => {
  if (_outboxKnownEmpty) return data;
  const outbox = await localOutbox.get();
  if (outbox.length === 0) {
    _outboxKnownEmpty = true;
    return data;
  }

  const dataMap = new Map(data.map(item => [item.id, item]));

  outbox.forEach(item => {
    if (item.table !== table) return;

    const offlineFlags = {
      _is_offline: true,
      _syncing: syncingIds.has(item.id),
      _error: (item.retries || 0) >= 5,
    };
    const isError = offlineFlags._error;

    if (item.action === 'DELETE') {
  if (isError) {
    // Permanently-failed DELETE: the row still exists on the server. Keep it visible
    // and apply error flags so OfflineItemBadge renders the red error badge.
    if (dataMap.has(item.targetId)) {
      dataMap.set(item.targetId, { ...dataMap.get(item.targetId), ...offlineFlags });
    }
  } else {
    dataMap.delete(item.targetId);
  }

} else if (item.action === 'UPDATE') {
  if (dataMap.has(item.targetId)) {
    if (isError) {
      // R4: Don't apply the stale failed payload — server data shows through.
      // Only add the error flags so the UI can badge the item as needing attention.
      dataMap.set(item.targetId, { ...dataMap.get(item.targetId), ...offlineFlags });
    } else {
      dataMap.set(item.targetId, {
        ...dataMap.get(item.targetId),

        // null is a valid "clear field" signal (sanitizePayload converts '' → null).
        // Only undefined and the special family_role empty-string case are excluded.
        ...Object.fromEntries(
          Object.entries(item.payload).filter(([key, value]) => {
            if (key === "family_role" && !value) return false;
            return value !== undefined;
          })
        ),

        ...offlineFlags,
      });
    }
  }

} else if (item.action === 'CREATE') {
  if (!filterFn || filterFn(item.payload)) {
    const tempId = item.payload.id;
    dataMap.set(tempId, { ...item.payload, id: tempId, ...offlineFlags });
  }
}
  });

  return Array.from(dataMap.values());
};

// Reduces boilerplate in simple GET-by-id functions.
// Pass fallback=[] for functions that return arrays, fallback=null (default) for single items.
const withOfflineCache = async (cacheKey, fetcher, fallback = null) => {
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    // Race against 8s timeout: broken WiFi (navigator.onLine=true but no internet)
    // would otherwise hang for the full Supabase client timeout (~30s).
    const result = await Promise.race([
      fetcher(),
      new Promise((_, reject) =>
        setTimeout(() => reject(Object.assign(new Error('Network timeout'), { __networkTimeout: true })), 8000)
      ),
    ]);

    if (result !== undefined && result !== null) {
      await localCache.set(cacheKey, result);
    }

    return result ?? fallback;

  } catch (err) {
    const cached = await localCache.get(cacheKey);
    return cached !== undefined && cached !== null ? cached : fallback;
  }
};
let lastSyncAt = 0;
const MIN_SYNC_INTERVAL = 5000;
let isSyncing = false;
let syncStartedAt = null; // O3: timestamp when current sync cycle began (null when idle)
// Dev-only structured log for sync events — silent in production.
// Parallel to authLog in App.js: verbose in dev, no-op in prod so no Sentry spam.
const syncLog = process.env.NODE_ENV === 'production'
  ? () => {}
  : (event, data) => console.log(
      `%c[sync] %c${event}`, 'color:#64748b;font-weight:normal', 'color:#0284c7;font-weight:bold',
      { ts: new Date().toISOString().slice(11, 23), ...data }
    );
// Protección multi-tab: usamos localStorage como mutex entre tabs del mismo origin.
// TTL de 45s: si un tab crashea mid-sync, el lock expira y el siguiente tab puede entrar.
const SYNC_LOCK_KEY = 'keves_sync_lock';
const SYNC_LOCK_TTL_MS = 45000;

// safeLocalStorage: wraps every localStorage access so that environments where storage
// is unavailable (Safari Private Browsing throws SecurityError) degrade silently instead
// of crashing syncOfflineData with an unhandled exception.
const safeLocalStorage = {
  getItem(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem(key, value) {
    try { localStorage.setItem(key, value); return true; } catch { return false; }
  },
  removeItem(key) {
    try { localStorage.removeItem(key); return true; } catch { return false; }
  },
};

// withTimeout: prevents Supabase requests from hanging indefinitely (iOS suspend/resume,
// zombie network connections). Per-item 30s cap ensures isSyncing never gets stuck and
// the cross-tab lock TTL (45s) is never breached by a single slow request.
const withTimeout = (promise, ms = 30000) => {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('Request timeout')), ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

export const syncOfflineData = async () => {
  if (isSyncing) return;
  if (!navigator.onLine) return;
  const now = Date.now();

if (now - lastSyncAt < MIN_SYNC_INTERVAL) {
  return;
}

lastSyncAt = now;

  // Cross-tab lock con ownership token: previene double-sync entre tabs.
  // lockValue = null antes del try: si el try lanza antes de asignarlo (ej. localStorage bloqueado
  // en modo privado de Safari), el finally puede verificar con el null guard y no llamar removeItem.
  let lockValue = null;
  // syncStartFired: tracks whether sync-start was dispatched so the finally block
  // can guarantee a matching sync-complete is always dispatched, even on IDB errors.
  let syncStartFired = false;

try {
  // FIX 1: asignar lockValue dentro del try para que el finally siempre pueda limpiar.
  lockValue = `${Date.now()}_${generateUUID()}`;
  const existingLock = safeLocalStorage.getItem(SYNC_LOCK_KEY);
  if (existingLock) {
    const [lockTs] = existingLock.split('_');
    if (Date.now() - Number(lockTs) < SYNC_LOCK_TTL_MS) return; // otro tab es owner
  }
  const lockStored = safeLocalStorage.setItem(SYNC_LOCK_KEY, lockValue);

// Safari private mode / storage denied
if (!lockStored) return;
  // Verificación de ownership: si otro tab escribió entre nuestro check y nuestro set, abortamos
  if (safeLocalStorage.getItem(SYNC_LOCK_KEY) !== lockValue) return;

  isSyncing = true;
  syncStartedAt = Date.now();

  // Refrescar token antes de sync: un JWT expirado tras días offline causa 401 en todos los items.
  // refreshSession() obtiene un JWT fresco si el refresh token sigue válido. Si falla
  // (sesión cerrada, refresh token revocado) avisamos a la UI y abortamos el sync.
  // El return está dentro del try — el finally { localStorage.removeItem } siempre corre.
  let session;
  try {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData?.session) {
      console.warn("[sync] No se pudo refrescar la sesión:", refreshError?.message);
      try {
        const pendingOutbox = await localOutbox.get();
        if (pendingOutbox.length > 0 && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('sync-session-expired', { detail: { count: pendingOutbox.length } }));
        }
      } catch { /* best-effort */ }
      return;
    }
    session = refreshData.session;
  } catch (authErr) {
    console.warn("[sync] Error al refrescar sesión:", authErr?.message);
    return;
  }

if (!session) {
  console.warn("[sync] No hay sesión activa tras refresh");
  return;
}

  const outbox = await localOutbox.get();

if (outbox.length === 0) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sync-complete'));
  }
  return;
}

// Filtramos items que:
// 1. No han alcanzado el límite de retries
// 2. No están en backoff (nextRetryAt expiró o nunca fue seteado)
// Los items en backoff serán procesados en el próximo ciclo cuando su nextRetryAt pase.
const now = Date.now();
const pending = outbox.filter(i =>
  (i.retries || 0) < 5 &&
  (!i.nextRetryAt || i.nextRetryAt <= now)
);

if (pending.length === 0) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sync-complete'));
  }
  return;
}

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sync-start', { detail: { count: pending.length } }));
  }
  syncStartFired = true;

  // Hoist the full outbox scan outside the loop so the dependency check reuses one
  // snapshot instead of doing a fresh db.outbox.toArray() per item. Updated in-place
  // when an item is successfully removed, so dependent items see the correct state
  // on subsequent iterations without an extra IDB round-trip.
  let allOutboxItems = await db.outbox.toArray();
  let itemsProcessed = 0;
  let hit429 = false;

  for (const item of pending) { // ✅ FIX: usar pending
    // 🔥 FIX #1: re-leer el item desde Dexie antes de procesarlo.
    // `pending` es un snapshot en memoria capturado ANTES del loop. Si una iteración
    // anterior remapeó un tempId→realId en Dexie (targetId o campos relacionales),
    // ese cambio NO se refleja en el array en memoria. Re-leer garantiza datos frescos.
    const currentItem = await db.outbox.get(item.id);
    if (!currentItem) continue; // eliminado por una iteración anterior o limpieza externa

    // Dependency graph: verificar si este item tiene dependencias sin resolver.
    // Nuevo formato (dependsOnTempIds): usa tempIds lógicos, robusto a crashes y reloads.
    // Formato antiguo (dependsOn): usa outbox IDs — soportado para backward compat con
    // items creados antes de esta versión que ya estén en el outbox del usuario.
    const tempIdsToCheck = Array.isArray(currentItem.dependsOnTempIds) ? currentItem.dependsOnTempIds : [];
    const legacyDepIds = (!currentItem.dependsOnTempIds && currentItem.dependsOn) ? currentItem.dependsOn : [];

    if (tempIdsToCheck.length > 0) {
      let shouldSkip = false;
      for (const tempId of tempIdsToCheck) {
        // Buscar si existe un CREATE pendiente que aún no se haya sincronizado con este tempId
        const dep = allOutboxItems.find(i => i.id !== currentItem.id && i.action === 'CREATE' && i.payload?.id === tempId);
        if (dep) {
          if ((dep.retries || 0) >= 5) {
            // El padre falló permanentemente → bloquear el hijo para no gastar sus retries
            await db.outbox.update(currentItem.id, {
              retries: 5,
              lastError: `Bloqueado: la operación previa (${dep.action} en ${dep.table}) falló permanentemente. Usa "Reintentar" para ambos.`,
            });
            notifyOutboxChange();
          }
          // Padre pendiente o fallido permanentemente → saltar
          shouldSkip = true;
          break;
        }
        // dep no encontrado → el CREATE con ese tempId ya fue removido del outbox (resuelto) → OK
      }
      if (shouldSkip) continue;
    } else if (legacyDepIds.length > 0) {
      // Backward compat: items pre-migración usan IDs de outbox como dependencias
      let shouldSkip = false;
      for (const depId of legacyDepIds) {
        const dep = await db.outbox.get(depId);
        if (dep) {
          if ((dep.retries || 0) >= 5) {
            await db.outbox.update(currentItem.id, {
              retries: 5,
              lastError: `Bloqueado: la operación previa (${dep.action} en ${dep.table}) falló permanentemente. Usa "Reintentar" para ambos.`,
            });
            notifyOutboxChange();
          }
          shouldSkip = true;
          break;
        }
      }
      if (shouldSkip) continue;
    }

    syncLog('item:start', {
      table: currentItem.table,
      action: currentItem.action,
      id: (currentItem.payload?.id || currentItem.targetId || '').slice(0, 8),
      attempt: (currentItem.retries || 0) + 1,
    });
    syncingIds.add(currentItem.id);
    notifyOutboxChange();

    try {
      await db.outbox.update(currentItem.id, { lastAttemptAt: Date.now() });

      const payload = sanitizePayload(currentItem.payload);
      let result;

      if (!['CREATE', 'UPDATE', 'DELETE'].includes(currentItem.action)) {
        await db.outbox.update(currentItem.id, {
          retries: 5,
          lastError: `Acción desconocida: "${currentItem.action}". Item marcado como fallo permanente.`,
        });
        notifyOutboxChange();
        continue;
      }

      if (currentItem.action === 'CREATE') {
        // ── baptism_records: route to atomic RPC ──────────────────────────
        // The generic upsert only inserts the baptism_records row. The RPC
        // also updates people.baptism_status in the same DB transaction —
        // matching what registerBaptism does on the online path. Without
        // this, Supabase's people.baptism_status stays stale after an
        // offline-queued baptism syncs.
        if (currentItem.table === 'baptism_records') {
          const p = payload;
          const { data: rpcData, error: rpcError } = await withTimeout(
            supabase.rpc('register_baptism', {
              p_id:           p.id,
              p_person_id:    p.person_id,
              p_district_id:  p.district_id,
              p_church_id:    p.church_id    ?? null,
              p_baptism_date: p.baptism_date,
              p_notes:        p.notes        ?? null,
              p_created_by:   p.created_by   ?? null,
            }),
            15000
          );
          if (rpcError) throw rpcError;
          // Wrap in array so the downstream tempId→realId remapping block
          // works identically to the generic CREATE path (reads result.data?.[0]).
          result = { data: rpcData ? [rpcData] : [], error: null };
        } else {
          const { id: _omit, ...payloadWithoutId } = payload;
          // upsert idempotente: client_mutation_id ya está en payloadWithoutId (guardado en el outbox).
          // Si Supabase procesó el INSERT pero la respuesta se perdió por timeout de red,
          // el retry usa el mismo client_mutation_id → onConflict lo detecta → retorna el existente.
          result = await withTimeout(supabase
            .from(currentItem.table)
            .upsert([payloadWithoutId], { onConflict: 'client_mutation_id', ignoreDuplicates: false })
            .select());
        }
      } else if (currentItem.action === 'UPDATE') {
        result = await withTimeout(supabase.from(currentItem.table).update(payload).eq('id', currentItem.targetId).select());
      } else if (currentItem.action === 'DELETE') {
        result = await withTimeout(supabase.from(currentItem.table).delete().eq('id', currentItem.targetId).select());
      }

      if (result && !result.error && Array.isArray(result.data)) {

  // Orphan UPDATE/DELETE defense: Supabase returns { data: [], error: null } when the
  // target row does not exist. For CREATE this cannot happen (upsert always returns the row).
  // For UPDATE/DELETE against a tempId whose CREATE never synced, this is silent data loss —
  // the outbox item would be removed as if it succeeded. Treat 0 rows affected as permanent
  // failure so the item surfaces in the error UI instead of disappearing silently.
  // UPDATE data:[] = permanent failure (orphan row, should surface in error UI).
  // DELETE data:[] = idempotent success (row already gone = desired state — treat as ok).
  if (currentItem.action === 'UPDATE' && result.data.length === 0) {
    await db.outbox.update(currentItem.id, {
      retries: 5,
      lastError: 'Fila no encontrada en servidor (posiblemente eliminada)',
    });
    notifyOutboxChange();
    continue;
  }

  // 🔥 MAPEO DE ID TEMP → REAL
  if (currentItem.action === 'CREATE') {
    const real = result.data?.[0];

if (!real?.id) {
  throw new Error('CREATE sin ID válido');
}

await db.transaction('rw', db.outbox, async () => {

  const remainingItems = await db.outbox.toArray();

  for (const other of remainingItems) {

    if (other.id === currentItem.id) continue;

    const otherUpdates = {};

    // Reparar targetId
    if (other.targetId === currentItem.payload.id) {
      otherUpdates.targetId = real.id;
    }

    // Reparar payload refs
    let payloadChanged = false;
    const updatedPayload = { ...other.payload };

    if (other.payload?.id === currentItem.payload.id) {
      updatedPayload.id = real.id;
      payloadChanged = true;
    }

    for (const field of RELATIONAL_ID_FIELDS) {
      if (other.payload?.[field] === currentItem.payload.id) {
        updatedPayload[field] = real.id;
        payloadChanged = true;
      }
    }

    if (payloadChanged) {
      otherUpdates.payload = updatedPayload;
    }

    if (Object.keys(otherUpdates).length > 0) {
      await db.outbox.update(other.id, otherUpdates);
    }
  }
});

    // actualizar cache donde exista el tempId
    const keys = currentItem.cacheKeys || [];

for (const key of keys) {
      const cached = await localCache.get(key);
      if (!cached) continue;

      if (Array.isArray(cached)) {
        const updated = cached.map(i =>
          i.id === currentItem.payload.id ? { ...i, id: real.id, _is_offline: false } : i
        );
        await localCache.set(key, updated);
      } else if (cached.id === currentItem.payload.id) {
        await localCache.set(key, { ...cached, id: real.id, _is_offline: false });
      }
    }
  }

  await localOutbox.remove(currentItem.id);
  syncLog('item:synced', {
    table: currentItem.table,
    action: currentItem.action,
    id: (currentItem.payload?.id || currentItem.targetId || '').slice(0, 8),
  });

// 🔥 invalidar queries después de sync
(currentItem.cacheKeys || []).forEach(key => {
  invalidateQuery(key);
});
(currentItem.invalidatePrefixes || []).forEach(prefix => {
  try { invalidateQueryPrefix(JSON.parse(prefix)); } catch { /* skip malformed */ }
});
} else {
  // Clasificar el fallo: 4xx (excepto 401/429) son permanentes — nunca se recuperarán
  // con retry. 401/429, 5xx y errores de red son retriables con backoff exponencial.
  const httpStatus = result?.error?.status || result?.status || null;
  const isPermanentFailure =
    httpStatus !== null &&
    httpStatus >= 400 &&
    httpStatus < 500 &&
    httpStatus !== 401 &&
    httpStatus !== 429;

  if (isPermanentFailure) {
    const errMsg = `Error permanente (${httpStatus}): ${result?.error?.message ?? 'Fallo irrecuperable'}`;
    await db.outbox.update(currentItem.id, { retries: 5, lastError: errMsg });
    crashLogger.error('sync_permanent_failure', { table: currentItem.table, action: currentItem.action, status: httpStatus, message: errMsg });
  } else {
    const retryCount = (currentItem.retries || 0) + 1;
    const backoffMs = Math.min(5000 * Math.pow(2, retryCount - 1), 90000) + Math.floor(Math.random() * 2000);
    await db.outbox.update(currentItem.id, {
      retries: retryCount,
      lastError: result?.error?.message ?? 'Respuesta inválida del servidor',
      nextRetryAt: Date.now() + backoffMs,
    });
    syncLog('item:retry', {
      table: currentItem.table,
      action: currentItem.action,
      id: (currentItem.payload?.id || currentItem.targetId || '').slice(0, 8),
      retries: retryCount,
      error: (result?.error?.message ?? 'bad response').slice(0, 80),
      backoffMs,
    });
    if (httpStatus === 429) hit429 = true;
  }
  notifyOutboxChange();
}

    } catch (err) {
      const httpStatus = err?.status || null;
      const isPermanentFailure =
        httpStatus !== null &&
        httpStatus >= 400 &&
        httpStatus < 500 &&
        httpStatus !== 401 &&
        httpStatus !== 429;

      if (isPermanentFailure) {
        const errMsg = `Error permanente (${httpStatus}): ${err.message}`;
        await db.outbox.update(currentItem.id, { retries: 5, lastError: errMsg });
        crashLogger.error('sync_permanent_failure', { table: currentItem.table, action: currentItem.action, status: httpStatus, message: errMsg });
      } else {
        const retryCount = (currentItem.retries || 0) + 1;
        const backoffMs = Math.min(5000 * Math.pow(2, retryCount - 1), 90000) + Math.floor(Math.random() * 2000);
        await db.outbox.update(currentItem.id, {
          retries: retryCount,
          lastError: err.message,
          nextRetryAt: Date.now() + backoffMs,
        });
        syncLog('item:retry', {
          table: currentItem.table,
          action: currentItem.action,
          id: (currentItem.payload?.id || currentItem.targetId || '').slice(0, 8),
          retries: retryCount,
          error: err.message.slice(0, 80),
          backoffMs,
        });
        if (httpStatus === 429) hit429 = true;
      }
      notifyOutboxChange();

    } finally {
      // ✅ GARANTIZA limpieza siempre
      syncingIds.delete(currentItem.id);
      
      // 🔥 FIX: Refrescar el snapshot completo desde Dexie para la siguiente iteración.
      // Garantiza que las evaluaciones de dependencias vean los tempIds remapeados
      // y las propagaciones de errores permanentes (retries: 5) inmediatamente.
      allOutboxItems = await db.outbox.toArray();
    }

    // Fix 22: Stop processing after first 429 — avoids amplifying rate-limit pressure.
    // Fix 19: Refresh the cross-tab lock timestamp every 20 processed items so long syncs
    // don't lose ownership to another tab before the 45s TTL. Only updates lockValue when
    // ownership is confirmed; breaks the loop if another tab has stolen the lock.
    itemsProcessed++;
    if (hit429) break;
    if (itemsProcessed % 20 === 0) {
      const lockUuid = lockValue.split('_')[1];
      const refreshed = `${Date.now()}_${lockUuid}`;
      safeLocalStorage.setItem(SYNC_LOCK_KEY, refreshed);
      if (safeLocalStorage.getItem(SYNC_LOCK_KEY) !== refreshed) {
        break; // ownership lost mid-sync — outer finally handles isSyncing and sync-complete
      }
      lockValue = refreshed;
    }
  }

} finally {
  syncStartedAt = null;
  isSyncing = false;
  // Dispatch sync-complete here (not inside the try) so it fires even if an IDB error
  // throws inside the for loop — preventing the React isSyncing state from getting stuck.
  // Only fires if sync-start was dispatched; avoids a spurious event on early-exit paths
  // (empty outbox, no pending items, auth failure, lock not acquired).
  if (syncStartFired && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sync-complete'));
    if (hit429) window.dispatchEvent(new CustomEvent('keves:rate-limit'));
  }
  // Solo remover el lock si seguimos siendo el owner.
  // Si otro tab expiró nuestro lock (TTL) y adquirió el suyo propio,
  // no debemos sobreescribir su estado eliminando una clave que ya no nos pertenece.
  if (lockValue && safeLocalStorage.getItem(SYNC_LOCK_KEY) === lockValue) {
    safeLocalStorage.removeItem(SYNC_LOCK_KEY);
  }
}
};

// Escuchar cambios de red para sincronizar + medir tiempo offline.
if (typeof window !== 'undefined' && !window.__kevesOnlineListener) {
  window.__kevesOnlineListener = true;
  let _offlineAt = null;

  window.addEventListener('offline', () => {
    _offlineAt = Date.now();
  });

  window.addEventListener('online', () => {
    if (_offlineAt !== null) {
      crashLogger.metric('offline_duration_s', Math.round((Date.now() - _offlineAt) / 1000));
      _offlineAt = null;
    }
    syncOfflineData();
    syncDirtySettings().catch(() => {});
  });
}

// Mobile resume sync: on iOS Safari and Android Chrome the 'online' event does NOT fire
// when the app returns to the foreground after being backgrounded. visibilitychange fires
// instead. Guarded with __kevesVisibilityListener (same HMR-safe pattern as the interval)
// to prevent duplicate listeners across hot-reloads in development.
if (typeof window !== 'undefined' && !window.__kevesVisibilityListener) {
  window.__kevesVisibilityListener = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine && !isSyncing) {
      syncOfflineData();
    }
  });
}

// Retry scheduler periódico: reintenta items en backoff sin requerir evento de red.
// Sin esto, un item que falla cuando el usuario YA está online queda atrapado en el outbox
// hasta la próxima transición offline→online (que puede no ocurrir nunca).
// 60s: frecuente suficiente para UX fluida, raro suficiente para no saturar Supabase.
//
// HMR-safe: en desarrollo, Vite/webpack re-ejecuta este módulo en cada hot reload.
// Un `setInterval` sin guard acumula N intervalos (uno por reload), causando N syncs
// simultáneos que compiten por el cross-tab lock y generan ruido en los logs.
// window.__kevesSyncInterval persiste entre reloads del módulo (window sobrevive HMR),
// por lo que el guard `!window.__kevesSyncInterval` garantiza registro único por sesión.
const SYNC_HUNG_THRESHOLD_MS = 90000; // 90s — well above withTimeout(30s) * worst-case retries

if (typeof window !== 'undefined' && !window.__kevesSyncInterval) {
  window.__kevesSyncInterval = setInterval(() => {
    // Watchdog: if a sync has been running for >90s the finally block likely didn't fire
    // (page was backgrounded mid-request on iOS, or a Promise race silently swallowed).
    // Force-reset so the next tick can acquire the lock and retry.
    if (isSyncing && syncStartedAt !== null && Date.now() - syncStartedAt > SYNC_HUNG_THRESHOLD_MS) {
      isSyncing = false;
      syncStartedAt = null;
      safeLocalStorage.removeItem(SYNC_LOCK_KEY);
    }

    if (!isSyncing && navigator.onLine) {
      // Pre-check the cross-tab lock before calling syncOfflineData to avoid a wasted
      // auth refresh + IDB read on every tick when another tab already owns the lock.
      const existingLock = safeLocalStorage.getItem(SYNC_LOCK_KEY);
      if (existingLock) {
        const [lockTs] = existingLock.split('_');
        if (Date.now() - Number(lockTs) < SYNC_LOCK_TTL_MS) return;
      }
      syncOfflineData();
    }
  }, 60000);
}

// stableStringify: deterministic JSON regardless of property insertion order.
// Movido arriba (antes de executeMutation) para evitar errores TDZ en producción.
const stableStringify = (obj) =>
  JSON.stringify(
    Object.fromEntries(
      Object.entries(obj).sort(([a], [b]) => a.localeCompare(b))
    )
  );

// Helper para ejecutar mutaciones (CRUD) con soporte offline
const executeMutation = async ({
  table,
  action,
  payload,
  targetId,
  cacheKeyToInvalidate,
  cacheKeysToInvalidate,
  invalidatePrefixesToInvalidate,
}) => {

  const sanitizedPayload = sanitizePayload(payload);

  // ✅ definir keys correctamente
  const keysToInvalidate = [
    ...(cacheKeyToInvalidate ? [cacheKeyToInvalidate] : []),
    ...(cacheKeysToInvalidate || []),
  ].filter(Boolean);

  const prefixesToInvalidate = (invalidatePrefixesToInvalidate || []).filter(Boolean);

  // R1: If an UPDATE or DELETE targets a tempId from a pending offline CREATE, the server
  // has no row for that ID yet. Attempting the Supabase call would silently return data:[]
  // and the mutation would be lost. Route directly to the outbox so the dependency graph
  // ensures it executes only after the parent CREATE syncs successfully.
  let skipOnlinePath = false;
  if ((action === 'UPDATE' || action === 'DELETE') && targetId) {
    const outboxSnapshot = await db.outbox.toArray();
    skipOnlinePath = outboxSnapshot.some(i => i.action === 'CREATE' && i.payload?.id === targetId);
  }

  // Hoisted so the catch block can reuse the UUID Supabase already received on timeout.
  let onlineClientMutationId = null;

  try {
    if (skipOnlinePath) throw Object.assign(new Error('offline-tempid'), { __skipOnline: true });

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    let result;

    if (action === 'CREATE') {
      const { id: _omit, ...payloadWithoutId } = sanitizedPayload;

      // FIX 3: fingerprint dedup contra double-submit (doble-click, retry implícito del browser).
      // Reutiliza el mismo client_mutation_id si el mismo payload se envía dentro de 10s.
      // El upsert con onConflict:'client_mutation_id' hace el resto: Supabase devuelve
      // la fila existente sin insertar un duplicado.
      // Solo en memoria (window): no persiste entre reloads, SSR-safe.
      if (typeof window !== 'undefined') {
        window.__kevesMutationCache ||= {};
      }
      const mutationFingerprint = stableStringify({
  table,
  action,
  payload: sanitizedPayload
});
      const nowMs = Date.now();
      let client_mutation_id;

      if (typeof window !== 'undefined') {
        const cached = window.__kevesMutationCache[mutationFingerprint];
        if (cached && nowMs - cached.ts < 10000) {
          client_mutation_id = cached.id;
        } else {
          client_mutation_id = generateUUID();
          window.__kevesMutationCache[mutationFingerprint] = { id: client_mutation_id, ts: nowMs };
          // Prune entries older than the dedup window to prevent unbounded growth.
          const PRUNE_THRESHOLD = 10000;
          for (const key of Object.keys(window.__kevesMutationCache)) {
            if (nowMs - window.__kevesMutationCache[key].ts > PRUNE_THRESHOLD) {
              delete window.__kevesMutationCache[key];
            }
          }
        }
      } else {
        client_mutation_id = generateUUID();
      }

      onlineClientMutationId = client_mutation_id;
      result = await withTimeout(supabase
        .from(table)
        .upsert([{ ...payloadWithoutId, client_mutation_id }], {
          onConflict: 'client_mutation_id',
          ignoreDuplicates: false,
        })
        .select(), 15000);
    }

    else if (action === 'UPDATE') {
      result = await withTimeout(supabase
        .from(table)
        .update(sanitizedPayload)
        .eq('id', targetId)
        .select(), 15000);
    }

    else if (action === 'DELETE') {
      result = await withTimeout(supabase
        .from(table)
        .delete()
        .eq('id', targetId)
        .select(), 15000);
    }

    if (result.error) {
      // 🔥 FIX #4: errores de cliente (4xx) son permanentes — no se resuelven con retry.
      // result.status contiene el HTTP status code de la respuesta de Supabase.
      // 403 Forbidden, 409 Conflict, 422 Unprocessable → devolver inmediatamente sin tocar outbox.
      // 500+ (server errors) y sin status (timeouts, red caída) → lanzar al catch para retry via outbox.
      if (result.status >= 400 && result.status < 500 && result.status !== 401 && result.status !== 429) {
        return { data: null, error: result.error, isOffline: false };
      }
      throw result.error;
    }

    // An online UPDATE that matched 0 rows is silent data loss — same guard as syncOfflineData line 468.
    // Most common cause: record was created offline (tempId), the background sync promoted it to a real
    // ID before this form submission ran, so skipOnlinePath found no pending CREATE but the real row
    // has a different ID. Without this guard, the caller receives { data: null, error: null } and
    // silently discards the user's edits. Do NOT invalidate cache (nothing changed server-side).
    if (action === 'UPDATE' && Array.isArray(result.data) && result.data.length === 0) {
      return { data: null, error: new Error('Registro no encontrado en el servidor'), isOffline: false };
    }

    // ✅ invalidación online (cache + react)
    for (const key of keysToInvalidate) {
      await db.cache.delete(key);
      invalidateQuery(key);
    }
    for (const prefix of prefixesToInvalidate) {
      try { invalidateQueryPrefix(JSON.parse(prefix)); } catch { /* skip malformed */ }
    }

    return {
      data: action === 'DELETE' ? null : (result.data?.[0] ?? null),
      error: null,
      isOffline: false
    };

  } catch (err) {

    // R1: skipOnlinePath throws a sentinel — this is intentional routing, not an error.
    // R8: 'Request timeout' is treated identically to a network error.
    if (!skipOnlinePath && err?.message !== 'Request timeout') {
      console.warn('[api] executeMutation offline o error retriable:', err);
    }

    const isNetworkError = !navigator.onLine || err?.message === 'Request timeout';
    const offlineId = action === 'CREATE'
      ? (onlineClientMutationId || generateUUID())
      : targetId;

    // ✅ guardar en outbox SOLO en offline/error retriable
    // Para CREATE: client_mutation_id = offlineId (estable por toda la vida del outbox item).
    // syncOfflineData usa upsert con onConflict:'client_mutation_id' → idempotente en retry.
    try {
      await localOutbox.push({
        table,
        action,
        payload: action === 'CREATE'
          ? { ...sanitizedPayload, id: offlineId, client_mutation_id: offlineId }
          : sanitizedPayload,
        targetId,
        cacheKeys: keysToInvalidate,
        invalidatePrefixes: prefixesToInvalidate,
        retries: 0,
        lastError: null,
        createdAt: Date.now(),
        lastAttemptAt: null
      });
    } catch (storageErr) {
      // IndexedDB quota exceeded or storage unavailable — cannot queue the mutation.
      // Return a distinct error shape so callers can surface a specific message.
      // We do NOT run cache invalidation: the mutation was never applied anywhere.
      return {
        data: null,
        error: storageErr,
        isOffline: false,
        isStorageError: true,
      };
    }

    // Offline path: only notify subscribers — do NOT delete cache.
    // Getters fall back to localCache on network error; deleting it would
    // cause the entire list to disappear and mergeOutboxItems to return only
    // the pending item. After sync, syncOfflineData calls invalidateQuery which
    // triggers a live Supabase fetch that overwrites the cache with fresh data.
    for (const key of keysToInvalidate) {
      invalidateQuery(key);
    }

    return {
      data: action === 'DELETE'
        ? null
        : { ...sanitizedPayload, id: offlineId, _is_offline: true },
      error: (isNetworkError || skipOnlinePath) ? null : err,
      isOffline: true
    };
  }
};

// =====================================================
// IGLESIAS
// =====================================================
export const getChurches = async (districtId) => {
  if (!districtId) return [];
  const cacheKey = `churches_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    const { data, error } = await supabase
      .from('vw_churches_with_stats')
      .select('*')
      .eq('district_id', districtId)
      .order('order_index', { ascending: true });

    if (error) throw error;

    if (data) {
      await localCache.set(cacheKey, data);
      // Write-through: individual church entries so getChurch() always finds a cache hit
      // after the district list has been loaded (avoids a Supabase roundtrip per church).
      data.forEach(c => localCache.set(`church_${c.id}`, c).catch(() => {}));
    }
    return await mergeOutboxItems(data || [], 'churches', (p) => !districtId || p.district_id === districtId);
  } catch (err) {
    if (navigator.onLine) {
      console.error('[API ERROR] getChurches (districtId:', districtId, '):', err);
    } else {
      console.warn('[API] getChurches offline: cargando desde caché');
    }
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'churches', (p) => !districtId || p.district_id === districtId);
  }
}

export const updateChurchesOrder = async (churches) => {
  // Esta es una operación masiva, mejor tratarla de forma individual u optimista
  for (const church of churches) {
    await updateChurch(church.id, { order_index: church.order_index });
  }
};

export const getChurch = async (id) => {
  if (!id) return null;
  const cacheKey = `church_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from('churches').select('*').eq('id', id).single();
    if (error) throw error;
    if (data) await localCache.set(cacheKey, data);
    return data;
  } catch (err) {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList = await localCache.findInLists('churches_', id);
    if (fromList) return fromList;
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'churches' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
}

export const createChurch = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'churches',
    action: 'CREATE',
    payload: {
        name:               payload.name,
        district_id:        payload.district_id,
        status:             payload.status,
        address:            payload.address,
        email:              payload.email,
        phone:              payload.phone,
        latitude:           payload.latitude  ?? null,
        longitude:          payload.longitude ?? null,
        service_saturday:   payload.service_saturday  || null,
        service_wednesday:  payload.service_wednesday || null,
        order_index:        payload.order_index || 0,
    },
    cacheKeyToInvalidate: `churches_${payload.district_id}`
  });
  if (error) throw error;
  return data;
}

export const updateChurch = async (id, updates, currentDistrictId) => {
  const districtId = currentDistrictId || updates.district_id;
  const { data, error, isOffline } = await executeMutation({
    table: 'churches',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeyToInvalidate: districtId ? `churches_${districtId}` : null
  });
  if (error) throw error;
  if (!isOffline) await localCache.delete(`church_${id}`);
  return data;
}

export const deleteChurch = async (id, districtId) => {
  const { error, isOffline } = await executeMutation({
    table: 'churches',
    action: 'DELETE',
    targetId: id,
    cacheKeyToInvalidate: districtId ? `churches_${districtId}` : null
  });
  if (error) throw error;
  if (!isOffline) await localCache.delete(`church_${id}`);
  return true;
}

// =====================================================
// FAMILIAS
// =====================================================
export const getFamilies = async (churchId) => {
  if (!churchId) return [];

  const cacheKey = `families_full_${churchId}`;
  const cached = await localCache.get(cacheKey);

  try {
    // 1. Si estamos offline, devolvemos el caché inmediatamente con los cambios pendientes
    if (!navigator.onLine) throw Object.assign(new Error("Offline mode"), { __skipOnline: true });

    const { data: families, error } = await supabase
      .from('vw_families_with_stats')
      .select('*')
      .eq('church_id', churchId);
      
    if (error) throw error;
    
    // Si no hay familias en red, revisamos si hay alguna creada en el outbox
    if (!families || families.length === 0) {
      return await mergeOutboxItems([], 'families', (p) => p.church_id === churchId);
    }

    await localCache.set(cacheKey, families || []);
    // Write-through: individual detail entries so getFamily() always finds a cache hit
    // after the church list has been loaded (avoids a Supabase roundtrip per family).
    (families || []).forEach(f => localCache.set(`family_detail_${f.id}`, f).catch(() => {}));
    return await mergeOutboxItems(families || [], 'families', (p) => p.church_id === churchId);
  } catch (err) {
    console.warn("Cargando familias desde caché local (IndexedDB)");
    return await mergeOutboxItems(cached || [], 'families', (p) => p.church_id === churchId);
  }
}

export const getFamily = async (id) => {
  const cacheKey = `family_detail_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    const { data: family, error } = await supabase
      .from('families')
      .select('*, people!family_id(id)')
      .eq('id', id)
      .single();

    if (error) throw error;

    const memberIds = family.people?.map(p => p.id) || [];

    // 1. Obtener visitas a la familia O a cualquier miembro
    let query = supabase.from('visits').select('date, status, target_person_id, target_family_id');
    
    if (memberIds.length > 0) {
      query = query.or(`target_family_id.eq.${id},target_person_id.in.(${memberIds.join(',')})`);
    } else {
      query = query.eq('target_family_id', id);
    }

    const { data: visits } = await query.order('date', { ascending: false }).limit(200);

    // 2. Integrar visitas del outbox (creaciones pendientes offline)
    const outbox = await localOutbox.get();
    const pendingVisits = outbox
      .filter(i => 
        i.table === 'visits' && 
        i.action === 'CREATE' && 
        (i.payload.target_family_id === id || memberIds.includes(i.payload.target_person_id))
      )
      .map(i => ({ date: i.payload.date, status: i.payload.status || 'pendiente' }));

    const allVisits = [...(visits || []), ...pendingVisits];

    let lastVisitDate = null;
    allVisits.forEach(v => {
      if (v.status === 'realizada') {
        if (!lastVisitDate || v.date > lastVisitDate) lastVisitDate = v.date;
      }
    });

    const enriched = {
      ...family,
      people_count: memberIds.length,
      total_visits: allVisits.length,
      last_visit_days: lastVisitDate ? getDaysSince(lastVisitDate) : 999
    };

    // 🔥 ACTUALIZAR CACHÉ: Importante para que no lea datos viejos
    await localCache.set(cacheKey, enriched);
    
    return enriched;

  } catch (err) {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList = await localCache.findInLists('families_', id);
    if (fromList) return fromList;
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'families' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
}

export const createFamily = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'families',
    action: 'CREATE',
    payload,
    cacheKeyToInvalidate: `families_full_${payload.church_id}`
  });
  if (error) throw error;
  return data;
}

export const updateFamily = async (id, updates, currentChurchId) => {
  const oldChurchId = currentChurchId;
  const newChurchId = updates.church_id;
  const cacheKeys = [`family_detail_${id}`];
  if (oldChurchId) cacheKeys.push(`families_full_${oldChurchId}`);
  if (newChurchId && newChurchId !== oldChurchId) cacheKeys.push(`families_full_${newChurchId}`);
  const { data, error, isOffline } = await executeMutation({
    table: 'families',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeysToInvalidate: cacheKeys,
  });
  if (error) throw error;
  if (!isOffline) await localCache.delete(`family_detail_${id}`);
  return data;
}

export const deleteFamily = async (id, churchId) => {
  const { error } = await executeMutation({
    table: 'families',
    action: 'DELETE',
    targetId: id,
    cacheKeysToInvalidate: [
      churchId ? `families_full_${churchId}` : null,
      `family_detail_${id}`,
    ].filter(Boolean),
  });
  if (error) throw error;
  return true;
};

// =====================================================
// PERSONAS
// =====================================================
export const createPerson = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'people',
    action: 'CREATE',
    payload,
    cacheKeyToInvalidate: `people_fam_${payload.family_id}`
  });
  if (error) throw error;
  return data;
};

export const getPerson = async (id) => {
  const cacheKey = `person_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from('people').select('*').eq('id', id).single();
    if (error) throw error;
    if (data) await localCache.set(cacheKey, data);
    return data;
  } catch (err) {
    // 1. Direct entity cache (written by getPeople write-through)
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    // 2. Indexed Dexie table — O(1), populated by getPeople write-through
    const fromIndex = await db.people.get(id).catch(() => null);
    if (fromIndex) return fromIndex;
    // 3. Linear scan of list caches (last resort for legacy / unusual paths)
    const fromList = await localCache.findInLists('people_', id);
    if (fromList) return fromList;
    // 4. Outbox: created offline and not yet synced
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'people' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
}

export const updatePerson = async (id, updates, familyId) => {
  // 🔥 FIX #5: aceptar familyId como parámetro explícito (tercer argumento, opcional).
  // Sin esto, updates parciales como { status: 'inactive' } (sin family_id) no invalidaban
  // la lista de personas — la UI mostraba datos obsoletos hasta el próximo refetch completo.
  // Backward compatible: callers que pasen solo 2 args siguen funcionando (familyId=undefined).
  const resolvedFamilyId = familyId || updates.family_id;

  // Domain rule: detect any eligibility loss and schedule ministry role cleanup.
  // Compares pre-update person (from cache) against the projected post-update state.
  // Single authoritative place — covers PersonForm, updatePersonDiscipleshipStage,
  // and any future caller. Adding a new rule to isEligibleForMinistry() is sufficient.
  let eligibilityLost = false;
  const touchesEligibility = updates.baptism_status !== undefined ||
                             updates.spiritual_status !== undefined;
  if (touchesEligibility) {
    let prevPerson = await localCache.get(`person_${id}`).catch(() => null);
    if (!prevPerson && resolvedFamilyId) {
      const familyPpl = await localCache.get(`people_fam_${resolvedFamilyId}`).catch(() => null);
      prevPerson = Array.isArray(familyPpl)
        ? (familyPpl.find(p => String(p.id) === String(id)) ?? null)
        : null;
    }
    if (prevPerson && isEligibleForMinistry(prevPerson)) {
      const projected = { ...prevPerson, ...updates };
      if (!isEligibleForMinistry(projected)) eligibilityLost = true;
    }
  }

  const { data, error, isOffline } = await executeMutation({
    table: 'people',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeysToInvalidate: [
      resolvedFamilyId ? `people_fam_${resolvedFamilyId}` : null,
      `person_${id}`,
    ].filter(Boolean),
  });
  if (error) throw error;
  if (!isOffline) await localCache.delete(`person_${id}`);

  // Auto-remove all ministry roles when eligibility is lost (online only).
  if (eligibilityLost && !isOffline) {
    removeAllRolesForPerson(id).catch(err =>
      console.error('[updatePerson] auto-remove roles after eligibility loss:', err)
    );
  }

  return data;
};

export const movePerson = async (personId, fromFamilyId, toFamilyId) => {
  if (!personId || !toFamilyId) return null;

  const keys = [
    `people_fam_${toFamilyId}`,
    ...(fromFamilyId ? [`people_fam_${fromFamilyId}`] : []),
  ];

  const { data, error } = await executeMutation({
    table: 'people',
    action: 'UPDATE',
    payload: { family_id: toFamilyId },
    targetId: personId,
    cacheKeysToInvalidate: keys,
  });

  if (error) throw error;
  return data;
};

export const getPeople = async (input) => {
  const cacheKey = typeof input === 'string'
    ? `people_fam_${input}`
    : input?.family_id
      ? `people_fam_${input.family_id}`
      : `people_${JSON.stringify(input)}`;

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    let query = supabase.from('people').select(`
      *,
      families!family_id!inner (
        id,
        church_id,
        churches!church_id!inner (
          id,
          district_id
        )
      )
    `);

    if (typeof input === "string") query = query.eq('family_id', input);
    else if (typeof input === "object") {
      if (input?.family_id) query = query.eq('family_id', input.family_id);
      if (input?.district_id) query = query.eq('families.churches.district_id', input.district_id);
      if (input?.church_id) query = query.eq('families.church_id', input.church_id);
    }

    const { data, error } = await query;

    if (error) throw error;

    const freshData = data || [];

    await localCache.set(cacheKey, freshData);
    // Write-through: individual person_<id> entries + indexed db.people table.
    // Populating both ensures getPerson() finds a cache hit (O(1)) without a
    // Supabase roundtrip after any list page has been visited.
    if (freshData.length > 0) {
      freshData.forEach(p => localCache.set(`person_${p.id}`, p).catch(() => {}));
      db.people.bulkPut(freshData).catch(() => {});
    }

    return await mergeOutboxItems(freshData, 'people', (p) =>
      !input?.family_id || p.family_id === input.family_id
    );

  } catch (err) {
    console.warn("Offline: usando cache");

    const cached = await localCache.get(cacheKey);

    return await mergeOutboxItems(
      cached || [],
      'people',
      (p) => !input?.family_id || p.family_id === input.family_id
    );
  }
};

export const deletePerson = async (id, familyId) => {
  const { error, isOffline } = await executeMutation({
    table: 'people',
    action: 'DELETE',
    targetId: id,
    cacheKeyToInvalidate: familyId ? `people_fam_${familyId}` : null,
  });
  if (error) throw error;
  if (!isOffline) await localCache.delete(`person_${id}`);
  return true;
};

// ── EQUIPO DISTRITAL — personas reales con district_role asignado ────────────

export const getDistrictTeam = async (districtId) => {
  if (!districtId) return [];
  const cacheKey = `district_team_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('people')
      .select('id, name, phone, district_role, family_id, families!inner(church_id, churches!inner(id, name, district_id))')
      .not('district_role', 'is', null)
      .eq('families.churches.district_id', districtId)
      .order('district_role', { nullsFirst: false })
      .order('name');
    if (error) throw error;
    const fresh = (data || []).map((p) => ({
      ...p,
      church_name: p.families?.churches?.name   ?? null,
      church_id:   p.families?.church_id        ?? null,
    }));
    await localCache.set(cacheKey, fresh);
    fresh.forEach((p) => localCache.set(`person_${p.id}`, p).catch(() => {}));
    db.people.bulkPut(fresh).catch(() => {});
    return fresh;
  } catch {
    const cached = await localCache.get(cacheKey);
    const local  = await db.people.filter((p) => p.district_role != null).toArray().catch(() => []);
    return cached || local;
  }
};

// Assign or update a district-level role on an existing church-scoped person.
// Passing null for role removes the designation.
export const assignDistrictRole = async (personId, role, districtId) => {
  const { error } = await executeMutation({
    table: 'people',
    action: 'UPDATE',
    payload: { district_role: role || null },
    targetId: personId,
    cacheKeysToInvalidate: [`district_team_${districtId}`, `person_${personId}`],
  });
  if (error) throw error;
};

// =====================================================
// VISITAS
// =====================================================
export const getVisits = async (input) => {
  const filters = typeof input === "string" ? { district_id: input } : (input || {});
  const { district_id, church_id, status, family_id, date_from, date_to } = filters;

  const baseKey = church_id
    ? `visits_church_${church_id}`
    : `visits_district_${district_id}`;
  // Month-scoped requests get their own Dexie slot so each month is cached independently.
  const cacheKey = (date_from && date_to) ? `${baseKey}_${date_from}_${date_to}` : baseKey;

  const filterFn = (p) => {
    if (church_id && p.church_id !== church_id) return false;
    if (!church_id && district_id && p.district_id !== district_id) return false;
    if (status && status !== "todas" && p.status !== status) return false;
    if (family_id && p.target_family_id !== family_id) return false;
    if (date_from && p.date < date_from) return false;
    if (date_to   && p.date > date_to)   return false;
    return true;
  };

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    if (!district_id && !church_id) return [];

    let query = supabase.from("visits").select("*");
    if (church_id) query = query.eq("church_id", church_id);
    else if (district_id) query = query.eq("district_id", district_id);
    if (status && status !== "todas") query = query.eq("status", status);
    if (family_id) query = query.eq("target_family_id", family_id);
    if (date_from) query = query.gte("date", date_from);
    if (date_to)   query = query.lte("date", date_to);

    const { data, error } = await Promise.race([
      query.order("date", { ascending: true }).limit(500),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Network timeout')), 8000)),
    ]);
    if (error) throw error;
    // Cache under a status-scoped key when filtered so offline can serve any filter variant.
    // Family-filtered results are excluded — they'd corrupt the shared district/church key.
    if (data && !family_id) {
      const scopedKey = (status && status !== "todas") ? `${cacheKey}_${status}` : cacheKey;
      await localCache.set(scopedKey, data).catch(() => {});
      if (!status || status === "todas") {
        data.forEach(v => localCache.set(`visit_${v.id}`, v).catch(() => {}));
      }
    }
    return await mergeOutboxItems(data || [], 'visits', filterFn);
  } catch (err) {
    console.warn("Offline: Cargando visitas desde caché");
    const baseCached = await localCache.get(cacheKey);
    const scopedCached = (status && status !== "todas") ? await localCache.get(`${cacheKey}_${status}`) : null;
    return await mergeOutboxItems(baseCached || scopedCached || [], 'visits', filterFn);
  }
}

export const getVisit = async (id) => {
  const cacheKey = `visit_${id}`;
  // filterFn scoped to this specific id: CREATE items are only injected if they share
  // this exact id (offline-created visit being viewed before sync). () => true was unsafe:
  // if this visit was DELETEd offline, merged[0] would return a random pending CREATE.
  const filterFn = (p) => p.id === id;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from('visits').select('*').eq('id', id).single();
    if (error) throw error;
    await localCache.set(cacheKey, data);
    const merged = await mergeOutboxItems([data], 'visits', filterFn);
    return merged[0] ?? null;
  } catch (err) {
    const cached = await localCache.get(cacheKey);
    if (cached) {
      const merged = await mergeOutboxItems([cached], 'visits', filterFn);
      return merged[0] ?? null;
    }
    const fromList = await localCache.findInLists('visits_', id);
    if (fromList) {
      const merged = await mergeOutboxItems([fromList], 'visits', filterFn);
      return merged[0] ?? null;
    }
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'visits' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
}

export const createVisit = async (payload) => {
  if (!payload?.district_id) return null;

  if (!payload?.target_family_id && !payload?.target_person_id) {
    throw new Error("La visita debe tener una familia o persona como destino");
  }

  if (payload?.target_family_id && payload?.target_person_id) {
    throw new Error("La visita no puede tener familia y persona al mismo tiempo");
  }

  const { data, error } = await executeMutation({
    table: 'visits',
    action: 'CREATE',
    payload,
    cacheKeysToInvalidate: [
      `visits_district_${payload.district_id}`,
      payload.church_id ? `visits_church_${payload.church_id}` : null,
    ].filter(Boolean),
    invalidatePrefixesToInvalidate: [
      JSON.stringify(["visits-month", payload.district_id]),
    ],
  });
  if (error) throw error;
  if (data && !data._is_offline) logVisit(data, payload.district_id); // best effort
  return data;
}

export const updateVisit = async (id, payload) => {
  const { data, error, isOffline } = await executeMutation({
    table: 'visits',
    action: 'UPDATE',
    payload: {
      ...payload,
      time: payload.time || null,
      next_visit_date: payload.next_visit_date || null,
      conducted_by: payload.conducted_by || null,
    },
    targetId: id,
    cacheKeysToInvalidate: [
      payload.district_id ? `visits_district_${payload.district_id}` : null,
      payload.church_id ? `visits_church_${payload.church_id}` : null,
      `visit_${id}`,
    ].filter(Boolean),
    invalidatePrefixesToInvalidate: payload.district_id
      ? [JSON.stringify(["visits-month", payload.district_id])]
      : [],
  });
  if (error) throw error;
  if (!isOffline) await localCache.delete(`visit_${id}`);
  return data;
}

export const completeVisit = async (id, districtId) => {
  return updateVisit(id, { status: "realizada", district_id: districtId, completed_at: new Date().toISOString() });
}

export const revertVisit = async (id, districtId) => {
  return updateVisit(id, { status: "pendiente", district_id: districtId, completed_at: null });
}

export const rescheduleVisit = async (id, { newDate, newTime, reason, districtId }) => {
  return updateVisit(id, {
    date: newDate,
    time: newTime || null,
    status: "rescheduled",
    rescheduled_reason: reason || null,
    district_id: districtId,
  });
};

export const deleteVisit = async (id, { districtId, churchId } = {}) => {
  const keys = [
    districtId ? `visits_district_${districtId}` : null,
    churchId ? `visits_church_${churchId}` : null,
    `visit_${id}`,
  ].filter(Boolean);

  const { error } = await executeMutation({
    table: 'visits',
    action: 'DELETE',
    targetId: id,
    cacheKeysToInvalidate: keys,
    invalidatePrefixesToInvalidate: districtId
      ? [JSON.stringify(["visits-month", districtId])]
      : [],
  });
  if (error) throw error;
  return true;
};

// =====================================================
// EVENTOS
// =====================================================
export const getChurchEvents = async ({ church_id, from_date }) => {
  const cacheKey = `events_church_${church_id}`;
  if (!church_id) return []; // 🔥 evita queries rotas
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    let query = supabase
      .from('events')
      .select('*')
      .eq('church_id', church_id)
      .or('type.eq.church,type.is.null');

    if (from_date) query = query.gte('date', from_date);

    const { data, error } = await query.order('date', { ascending: true });
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      data.forEach(e => localCache.set(`event_${e.id}`, e).catch(() => {}));
    }
    return await mergeOutboxItems(data || [], 'events', (p) => p.church_id === church_id && (p.type === 'church' || !p.type));
  } catch (err) {
    return await mergeOutboxItems((await localCache.get(cacheKey)) || [], 'events', (p) => p.church_id === church_id && (p.type === 'church' || !p.type));
  }
};

export const getChurchEventsByDistrict = async ({ district_id, from_date }) => {
  const cacheKey = `events_church_dist_v2_${district_id}`;
  try {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
  }

  let query = supabase
    .from('events')
    .select('*')
    .eq('district_id', district_id)
    .eq('type', 'church');

  if (from_date) {
    query = query.gte('date', from_date);
  }
  const { data, error } = await query.order('date', { ascending: true });
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      data.forEach(e => localCache.set(`event_${e.id}`, e).catch(() => {}));
    }
    return await mergeOutboxItems(data || [], 'events', (p) => p.district_id === district_id && p.type === 'church');
  } catch (err) {
    return await mergeOutboxItems((await localCache.get(cacheKey)) || [], 'events', (p) => p.district_id === district_id && p.type === 'church');
  }
};

export const getFieldEvents = async ({ district_id, from_date }) => {
  const cacheKey = `events_field_${district_id}`;
  try {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
  }

  let query = supabase
    .from('events')
    .select('*')
    .eq('district_id', district_id)
    .eq('type', 'field'); // 🔥 IMPORTANTE

  if (from_date) query = query.gte('date', from_date);
  const { data, error } = await query.order('date', { ascending: true });
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      data.forEach(e => localCache.set(`event_${e.id}`, e).catch(() => {}));
    }
    return await mergeOutboxItems(data || [], 'events', (p) => p.district_id === district_id && p.type === 'field');
  } catch (err) {
    return await mergeOutboxItems((await localCache.get(cacheKey)) || [], 'events', (p) => p.district_id === district_id && p.type === 'field');
  }
};
export const getPersonalEvents = async ({ district_id, from_date }) => {
  const cacheKey = `events_personal_${district_id}`;
  try {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
  }

  let query = supabase
    .from('events')
    .select('*')
    .eq('district_id', district_id)
    .eq('type', 'personal');

  // 🔥 SOLO FILTRAR SI VIENE DEFINIDO
  if (from_date !== undefined && from_date !== null) {
    query = query.gte('date', from_date);
  }
  const { data, error } = await query.order('date', { ascending: true });
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      data.forEach(e => localCache.set(`event_${e.id}`, e).catch(() => {}));
    }
    return await mergeOutboxItems(data || [], 'events', (p) => p.district_id === district_id && p.type === 'personal');
  } catch (err) {
    return await mergeOutboxItems((await localCache.get(cacheKey)) || [], 'events', (p) => p.district_id === district_id && p.type === 'personal');
  }
};

export const createChurchEvent = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'CREATE',
    payload: { ...payload, type: 'church' },
    cacheKeysToInvalidate: [
      `events_church_${payload.church_id}`,
      payload.district_id ? `events_church_dist_${payload.district_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  if (data && !data._is_offline) logEvent(data, payload.district_id); // best effort
  return data;
};

export const createFieldEvent = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'CREATE',
    payload: { ...payload, type: 'field' },
    cacheKeyToInvalidate: `events_field_${payload.district_id}`
  });
  if (error) throw error;
  if (data && !data._is_offline) logEvent(data, payload.district_id); // best effort
  return data;
};

// =====================================================
// ADMINISTRACIÓN DE USUARIOS
// =====================================================

export const getAccessRequests = async () => {
  const { data, error } = await supabase
    .from('access_requests')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error("GET ACCESS REQUESTS ERROR:", error); return []; }
  return data;
};

export const updateAccessRequestStatus = async (id, status) => {
  const { data, error } = await supabase
    .from('access_requests')
    .update({ status })
    .eq('id', id)
    .select();
  if (error) throw error;
  return data[0];
};

export const inviteUser = async (email, name) => {
  const { data, error } = await supabase
    .from('access_requests')
    .insert([{ email: email.toLowerCase().trim(), name, status: 'approved' }])
    .select();
  if (error) throw error;
  return data[0];
};

// Calls the invite-user Edge Function.
// When requestId is provided the Edge Function also updates access_requests atomically,
// so invite + approval are never split across two separate operations.
export const sendUserInvite = async (email, name, requestId = null) => {
  const body = requestId
    ? { email, name, request_id: requestId }
    : { email, name };

  const { data, error } = await supabase.functions.invoke('invite-user', { body });

  if (error) {
    // FunctionsHttpError has a `context` Response — try to extract the JSON body
    // so callers see "Missing service role key" instead of "Edge Function returned
    // a non-2xx status code". FunctionsFetchError (network/CORS) has no context.
    let message = error.message ?? 'Error al invocar la función';
    try {
      if (typeof error.context?.json === 'function') {
        const errBody = await error.context.json();
        if (errBody?.error) message = errBody.error;
      }
    } catch { /* body already consumed or not JSON */ }

    console.error('[sendUserInvite] Error:', message, error);
    const enriched = new Error(message);
    enriched.cause = error;
    throw enriched;
  }

  return data;
};

export const assignCoordinator = async (profileId, churchId) => {
  const { data, error } = await supabase.rpc('assign_coordinator', {
    p_profile_id: profileId,
    p_church_id:  churchId,
  });
  if (error) throw error;
  return data;
};

export const removeCoordinator = async (profileId) => {
  const { error } = await supabase.rpc('remove_coordinator', { p_profile_id: profileId });
  if (error) throw error;
};

// Records a pending coordinator invitation so the invite-onboarding flow
// auto-assigns coordinator role when the user sets their password.
export const upsertCoordinatorInvitation = async (email, churchId, personId = null) => {
  const cleanEmail = email.toLowerCase().trim();
  // Remove any existing pending invite for this email first (upsert via delete + insert)
  await supabase
    .from('coordinator_invitations')
    .delete()
    .eq('email', cleanEmail)
    .is('used_at', null);

  const { data: church } = await supabase
    .from('churches').select('district_id').eq('id', churchId).maybeSingle();
  if (!church?.district_id) throw new Error('Iglesia sin distrito asignado');

  const row = {
    email:       cleanEmail,
    church_id:   churchId,
    district_id: church.district_id,
  };
  if (personId) row.person_id = personId;

  const { error } = await supabase.from('coordinator_invitations').insert(row);
  if (error) throw error;
};

export const getProfileByEmail = async (email) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email, role, church_id, district_id, person_id')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

export const transferPastorDistrict = async (profileId, newDistrictId, notes = null) => {
  const { data, error } = await supabase.rpc('transfer_pastor_district', {
    p_profile_id:      profileId,
    p_new_district_id: newDistrictId,
    p_notes:           notes,
  });
  if (error) throw error;
  return data;
};

export const releasePastorDistrict = async (profileId, notes = null) => {
  const { data, error } = await supabase.rpc('release_pastor_district', {
    p_profile_id: profileId,
    p_notes:      notes,
  });
  if (error) throw error;
  return data;
};

export const completeTransferOnboarding = async (churchId) => {
  const { error } = await supabase.rpc('complete_transfer_onboarding', {
    p_church_id: churchId,
  });
  if (error) throw error;
};

export const getPeopleByDistrict = async (districtId) => {
  if (!districtId) return [];
  const { data, error } = await supabase
    .from('people')
    .select('id, name, email, family_id, families!people_family_id_fkey(id, name, church_id, district_id)')
    .eq('families.district_id', districtId)
    .order('name');
  if (error) { console.error('[getPeopleByDistrict]', error); return []; }
  return data || [];
};

export const linkProfileToPerson = async (profileId, personId) => {
  const { error } = await supabase.rpc('link_profile_to_person', {
    p_profile_id: profileId,
    p_person_id:  personId,
  });
  if (error) throw error;
};

export const createPastoralIdentity = async ({ familyName, personName, phone, email, churchId, districtId }) => {
  const { data, error } = await supabase.rpc('create_pastoral_identity', {
    p_family_name:  familyName,
    p_person_name:  personName,
    p_person_phone: phone    ?? null,
    p_person_email: email    ?? null,
    p_church_id:    churchId,
    p_district_id:  districtId,
  });
  if (error) throw error;
  return data; // { person_id, family_id, church_id, district_id, family_name, person_name }
};

// Operational cases
export const createOperationalCase = async (params) => {
  const { data, error } = await supabase.rpc('create_operational_case', {
    p_case_type:           params.case_type,
    p_severity:            params.severity,
    p_title:               params.title,
    p_description:         params.description         ?? null,
    p_district_id:         params.district_id         ?? null,
    p_related_entity_type: params.related_entity_type ?? null,
    p_related_entity_id:   params.related_entity_id   ?? null,
    p_assigned_to:         params.assigned_to         ?? null,
    p_metadata:            params.metadata            ?? {},
  });
  if (error) throw error;
  return data; // uuid of created case
};

export const updateOperationalCase = async (caseId, updates) => {
  const { error } = await supabase.rpc('update_operational_case', {
    p_case_id:          caseId,
    p_status:           updates.status           ?? null,
    p_assigned_to:      updates.assigned_to      ?? null,
    p_resolution_notes: updates.resolution_notes ?? null,
    p_description:      updates.description      ?? null,
  });
  if (error) throw error;
};

export const getOperationalCases = async ({ status, severity, case_type, limit = 20, offset = 0 } = {}) => {
  const { data, error } = await supabase.rpc('get_operational_cases', {
    p_status:    status    ?? null,
    p_severity:  severity  ?? null,
    p_case_type: case_type ?? null,
    p_limit:     limit,
    p_offset:    offset,
  });
  if (error) { console.error('[getOperationalCases]', error); return { cases: [], total: 0 }; }
  return data ?? { cases: [], total: 0 };
};

// Operations overview (admin only) — single aggregated payload
export const getOperationsOverview = async () => {
  const { data, error } = await supabase.rpc('get_operations_overview');
  if (error) { console.error('[getOperationsOverview]', error); return null; }
  return data;
};

export const getIntegrityChecks = async () => {
  const { data, error } = await supabase.rpc('get_integrity_checks');
  if (error) { console.error('[getIntegrityChecks]', error); return []; }
  return data || [];
};

export const getRecentTransfers = async (limit = 15) => {
  const { data, error } = await supabase.rpc('get_recent_transfers', { p_limit: limit });
  if (error) { console.error('[getRecentTransfers]', error); return []; }
  return data || [];
};

export const getRecentAdminActivity = async (limit = 20) => {
  const { data, error } = await supabase.rpc('get_recent_admin_activity', { p_limit: limit });
  if (error) { console.error('[getRecentAdminActivity]', error); return []; }
  return data || [];
};

export const getPastorLastAccess = async () => {
  const { data, error } = await supabase.rpc('get_pastor_last_access');
  if (error) { console.error('[getPastorLastAccess]', error); return []; }
  return data || [];
};

export const getMemberAdoption = async () => {
  const { data, error } = await supabase.rpc('get_member_adoption');
  if (error) { console.error('[getMemberAdoption]', error); return []; }
  return data || [];
};

// Audit events — paginated, newest first
// filters: { severity, event_type, actor_profile_id, from_date, to_date }
export const getAuditEvents = async ({ page = 0, pageSize = 20, severity, event_type, actor_profile_id, from_date, to_date } = {}) => {
  let q = supabase
    .from('audit_events')
    .select('id, created_at, event_type, entity_type, entity_id, severity, title, description, metadata, actor_profile_id, district_id, profiles:actor_profile_id(full_name, email)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);

  if (severity)         q = q.eq('severity', severity);
  if (event_type)       q = q.eq('event_type', event_type);
  if (actor_profile_id) q = q.eq('actor_profile_id', actor_profile_id);
  if (from_date)        q = q.gte('created_at', from_date);
  if (to_date)          q = q.lte('created_at', to_date);

  const { data, error, count } = await q;
  if (error) { console.error('[getAuditEvents]', error); return { events: [], total: 0 }; }
  return { events: data || [], total: count ?? 0 };
};

// Pastoral assignment history — ordered newest first
export const getPastoralAssignments = async (profileId) => {
  const { data, error } = await supabase
    .from('pastoral_assignments')
    .select('id, district_id, started_at, ended_at, assignment_status, transfer_notes, districts(name)')
    .eq('profile_id', profileId)
    .order('started_at', { ascending: false });
  if (error) { console.error('[getPastoralAssignments]', error); return []; }
  return data || [];
};

// Fetch all profiles in the same district as the caller (available to all district members)
export const getDistrictProfiles = async (districtId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email, account_status, ministry_status, role, church_id, district_id, person_id')
    .eq('district_id', districtId)
    .order('full_name', { ascending: true });
  if (error) { console.error('[getDistrictProfiles]', error); return []; }
  return data || [];
};

export const updateProfile = async (id, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', id)
    .select();
  if (error) throw error;
  return data[0];
};

export const getAllProfiles = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('email', { ascending: true });
  if (error) { console.error("GET PROFILES ERROR:", error); return []; }
  return data;
};

// IDs de auth.users que tienen member_profile (RPC admin-only; member_profiles tiene RLS
// auth.uid()=user_id, así que el admin no puede leerlas directo). Para ocultar de AdminUsers
// a huérfanos de miembro que cayeron en profiles. Devuelve [] para no-admins.
export const getMemberUserIds = async () => {
  const { data, error } = await supabase.rpc('get_member_user_ids');
  if (error) { console.error('[getMemberUserIds]', error); return []; }
  return Array.isArray(data) ? data : [];
};

export const getOrLinkProfile = async (user) => {
  const cleanEmail = user.email?.toLowerCase().trim();

  // 1. Buscar por user.id
  const { data: profileById, error: errorById } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  if (errorById) throw new Error('Error al buscar perfil: ' + errorById.message);

  let profile = profileById;

  // 2. Fallback: buscar por email
  if (!profile) {
    const { data: profileByEmail, error: errorByEmail } = await supabase
      .from('profiles')
      .select('*')
      .eq('email', cleanEmail)
      .maybeSingle();

    if (errorByEmail) throw new Error('Error al buscar perfil por email: ' + errorByEmail.message);

    // 3. Vincular el id de Auth al perfil existente
    if (profileByEmail) {
      const { data: linked, error: errorLink } = await supabase
        .from('profiles')
        .update({ id: user.id })
        .eq('email', cleanEmail)
        .select()
        .single();

      if (errorLink) throw new Error('Error al vincular perfil: ' + errorLink.message);
      profile = linked;
    }
  }

  // 4. Crear perfil si no existe
  if (!profile) {
    const { data: approvedReq } = await supabase
      .from('access_requests')
      .select('id')
      .eq('email', cleanEmail)
      .eq('status', 'approved')
      .maybeSingle();

    const { data: newProfile, error: errorCreate } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || null,
        // role es la fuente: el trigger deriva account_status desde role (staff -> 'active',
        // resto -> 'pending'). Un pastor pre-aprobado nace role='pastor' (cierra el hueco
        // "aprobado sin rol"). status retirado (Etapa 6: columna inerte, sin escritores).
        role: approvedReq ? 'pastor' : 'user',
        district_id: null,
      })
      .select()
      .single();

    if (errorCreate) throw new Error('Error al crear perfil: ' + errorCreate.message);
    profile = newProfile;
  }

  // 5. Apply coordinator invite if pending (must run before status check)
  // Invited coordinators start with status='pending' — the SECURITY DEFINER
  // RPC upgrades them to role='coordinator' + status='approved' atomically.
  if (profile && profile.role !== 'coordinator') {
    const { data: applied } = await supabase.rpc('apply_coordinator_invite', {
      p_profile_id: profile.id,
      p_email:      cleanEmail,
    });
    if (applied) {
      const { data: refreshed } = await supabase
        .from('profiles').select('*').eq('id', profile.id).maybeSingle();
      if (refreshed) profile = refreshed;
    }
  }

  // 6. Validar aprobación: solo se bloquea el alta de cuentas pendientes de aprobación.
  // (account_status es la fuente; suspended/inactive pasan aquí pero RLS los limita vía
  // current_user_status(). status sigue dual-escrito hasta Etapa 6.)
  if (profile.account_status === 'pending') {
    throw new Error('Acceso no autorizado: la cuenta está pendiente de aprobación');
  }

  return profile;
};

// EVENTOS PERSONAL (FIX REAL)
// =====================================================

// CREATE
export const createPersonalEvent = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'CREATE',
    payload: { ...payload, type: 'personal' },
    cacheKeyToInvalidate: `events_personal_${payload.district_id}`
  });
  if (error) throw error;
  if (data && !data._is_offline) logEvent(data, payload.district_id); // best effort
  return data;
};

// UPDATE
export const updatePersonalEvent = async (id, updates, { district_id } = {}) => {
  // 🔥 FIX #5: aceptar district_id para invalidar la caché del listado de eventos personales.
  // Backward compatible: callers sin tercer argumento siguen funcionando sin invalidación.
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeyToInvalidate: district_id ? `events_personal_${district_id}` : null,
  });
  if (error) throw error;
  return data;
};

// DELETE
export const deletePersonalEvent = async (id, { district_id } = {}) => {
  // 🔥 FIX #5: invalidar caché al eliminar un evento personal.
  const { error } = await executeMutation({
    table: 'events',
    action: 'DELETE',
    targetId: id,
    cacheKeyToInvalidate: district_id ? `events_personal_${district_id}` : null,
  });
  if (error) throw error;
  return true;
};
export const updateChurchEvent = async (id, updates, { church_id, district_id } = {}) => {
  // 🔥 FIX #5: invalidar caché de iglesia y de distrito al actualizar un evento de iglesia.
  // Un evento de iglesia puede aparecer en getChurchEvents y en getChurchEventsByDistrict.
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeysToInvalidate: [
      church_id ? `events_church_${church_id}` : null,
      district_id ? `events_church_dist_${district_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const updateFieldEvent = async (id, updates, { district_id } = {}) => {
  // 🔥 FIX #5: invalidar caché del distrito al actualizar un evento de campo.
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeyToInvalidate: district_id ? `events_field_${district_id}` : null,
  });
  if (error) throw error;
  return data;
};

export const deleteChurchEvent = async (id, { church_id, district_id } = {}) => {
  // 🔥 FIX #5: invalidar caché de iglesia y de distrito al eliminar un evento de iglesia.
  const { error } = await executeMutation({
    table: 'events',
    action: 'DELETE',
    targetId: id,
    cacheKeysToInvalidate: [
      church_id ? `events_church_${church_id}` : null,
      district_id ? `events_church_dist_${district_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return true;
};

export const deleteFieldEvent = async (id, { district_id } = {}) => {
  // 🔥 FIX #5: invalidar caché del distrito al eliminar un evento de campo.
  const { error } = await executeMutation({
    table: 'events',
    action: 'DELETE',
    targetId: id,
    cacheKeyToInvalidate: district_id ? `events_field_${district_id}` : null,
  });
  if (error) throw error;
  return true;
};
// =====================================================
// ROLES
// =====================================================
export const getRoles = async (church_id) => {
  if (!church_id) return [];
  const cacheKey = `roles_${church_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from('department_roles').select('*').eq('church_id', church_id);
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
}

export const createRole = async (payload) => {
  const { data, error } = await supabase.from('department_roles').insert([payload]).select()
  if (error) return null
  return data[0]
}

export const deleteRole = async (id) => {
  await supabase.from('department_roles').delete().eq('id', id)
}

export const renameDepartment = async (oldName, newName, church_id) => {
  await supabase.from('department_roles')
    .update({ department: newName })
    .eq('department', oldName)
    .eq('church_id', church_id)
}
// =====================================================
// MINISTERIOS (NUEVO)
// =====================================================

// Obtener ministerios asignados a una iglesia
export const getChurchMinistries = async ({ church_id }) => {
  if (!church_id) return [];
  const cacheKey = `church_ministries_${church_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from("church_ministries")
      .select("*")
      .eq("church_id", church_id);
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const getChurchDepartments = async (church_id) => {
  if (!church_id) return [];
  try {
    const { data, error } = await supabase
      .from('department_roles')
      .select('department')
      .eq('church_id', church_id)
      .not('department', 'is', null);
    if (error) throw error;
    const unique = [...new Set((data || []).map(r => r.department))];
    return unique.filter(Boolean).sort();
  } catch {
    return [];
  }
};

// Asignar ministerio
export const assignMinistry = async ({ church_id, name, department }) => {
  const { data, error } = await supabase
    .from("church_ministries")
    .insert([{ church_id, name, department }])
    .select();

  if (error) {
    console.error("ASSIGN MINISTRY ERROR:", error);
    return null;
  }

  return data[0];
};

// Quitar ministerio
export const removeMinistry = async ({ church_id, name }) => {
  const { error } = await supabase
    .from("church_ministries")
    .delete()
    .eq("church_id", church_id)
    .eq("name", name);

  if (error) {
    console.error("REMOVE MINISTRY ERROR:", error);
    return false;
  }

  return true;
};

// Obtener roles de un departamento
export const getDepartmentRoles = async (church_id, department) => {
  if (!church_id) return [];
  const cacheKey = `dept_roles_${church_id}_${department}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from("department_roles")
      .select("*")
      .eq("church_id", church_id)
      .eq("department", department);
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached !== undefined && cached !== null) return cached;
    // Fallback: bulk cache seeded by getRoles (called by ChurchDetail)
    const bulk = await localCache.get(`roles_${church_id}`).catch(() => null);
    return bulk?.filter(r => r.department === department) ?? [];
  }
};

// Crear o actualizar rol en un departamento
export const updateDepartmentRole = async (payload) => {
  const { church_id, department, person_id, role_name } = payload;

  try {
    // Regla eclesiástica: isEligibleForMinistry — cache-first, Supabase fallback.
    if (person_id) {
      let person = await localCache.get(`person_${person_id}`).catch(() => null);
      if (!person && (typeof navigator === 'undefined' || navigator.onLine)) {
        const { data } = await supabase
          .from('people')
          .select('baptism_status, spiritual_status')
          .eq('id', person_id)
          .maybeSingle();
        person = data ?? null;
      }
      if (person && !isEligibleForMinistry(person)) {
        const reason = !_BAPTIZED_MINISTRY.has(person.baptism_status)
          ? 'Solo miembros bautizados pueden ocupar cargos ministeriales'
          : 'Personas en proceso de restauración no pueden ocupar cargos ministeriales';
        throw Object.assign(new Error(reason), { code: 'NOT_ELIGIBLE' });
      }
    }

    // 🔥 1. Buscar si ya existe
    const { data: existing, error: findError } = await supabase
      .from("department_roles")
      .select("id")
      .eq("church_id", church_id)
      .eq("department", department)
      .eq("person_id", person_id)
      .maybeSingle();

    if (findError) throw findError;

    // 🔥 2. Si existe → UPDATE
    if (existing) {
      const { data, error } = await supabase
        .from("department_roles")
        .update({ role_name })
        .eq("id", existing.id)
        .select();

      if (error) throw error;
      return data[0];
    }

    // 🔥 3. Si no existe → INSERT
    const { data, error } = await supabase
      .from("department_roles")
      .insert([{ church_id, department, person_id, role_name }])
      .select();

    if (error) throw error;
    return data[0];

  } catch (error) {
    console.error("UPDATE DEPARTMENT ROLE ERROR:", error);
    throw error;
  }
};

// Eliminar rol
export const removeDepartmentRole = async (roleId) => {
  const { error } = await supabase
    .from("department_roles")
    .delete()
    .eq("id", roleId);

  if (error) {
    console.error("REMOVE DEPARTMENT ROLE ERROR:", error);
    return false;
  }

  return true;
};

// Eliminar TODOS los cargos ministeriales de una persona (al quitar el bautismo)
export const removeAllRolesForPerson = async (person_id) => {
  if (!person_id) return 0;
  const { data, error } = await supabase
    .from('department_roles')
    .delete()
    .eq('person_id', person_id)
    .select('id');
  if (error) {
    console.error('REMOVE ALL ROLES FOR PERSON ERROR:', error);
    return 0;
  }
  return data?.length ?? 0;
};

// Obtener miembros de la Junta Directiva (is_board_member = true)
export const getBoardMembers = async (church_id) => {
  if (!church_id) return [];
  const cacheKey = `board_members_${church_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from("department_roles")
      .select("*")
      .eq("church_id", church_id)
      .eq("is_board_member", true);
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    const cached = await localCache.get(cacheKey);
    return cached ?? [];
  }
};

// Marcar/desmarcar rol como miembro de la Junta Directiva
export const toggleBoardMember = async (roleId, isBoardMember) => {
  const { data, error } = await supabase
    .from("department_roles")
    .update({ is_board_member: isBoardMember })
    .eq("id", roleId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
};

// =====================================================
// DISTRITOS
// =====================================================
export const activateDistrict = async (id) => {
  await supabase.from('districts').update({ is_active: false }).neq('id', id)

  const { data, error } = await supabase
    .from('districts')
    .update({ is_active: true })
    .eq('id', id)
    .select()

  if (error) return null
  return data[0]
}

export const deleteDistrict = async (id) => {
  const { error } = await supabase.from('districts').delete().eq('id', id)
  if (error) throw error
  return true
}

export const getDistricts = async () => {
  const { data, error } = await supabase.from('districts').select('*')
  if (error) return []
  return data || []
}

export const getActiveDistrict = async () => {
  const { data, error } = await supabase.from('districts').select('*').eq('is_active', true).single()
  if (error) return null
  return data
}

export const createDistrict = async (payload) => {
  const { data, error } = await supabase
    .from('districts')
    .insert([{ name: payload.name, pastor_name: payload.pastor_name }])
    .select()

  if (error) return null
  return data[0]
}

// ── Admin billing controls ─────────────────────────────────────────────────────
// Requires admin status — RLS on districts table enforces this.

export async function adminActivatePlan(districtId) {
  const renewalAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const { error } = await supabase
    .from('districts')
    .update({
      plan:                'pro',
      subscription_status: 'active',
      trial_ends_at:       null,
      renewal_at:          renewalAt,
      grace_period_until:  null,
    })
    .eq('id', districtId);
  if (error) throw error;
}

export async function adminCancelPlan(districtId) {
  const { error } = await supabase
    .from('districts')
    .update({
      plan:                'free',
      subscription_status: 'cancelled',
    })
    .eq('id', districtId);
  if (error) throw error;
}

// =====================================================
// OTROS
// =====================================================
export const getResponsibilities = async (personId) => {
  return withOfflineCache(`responsibilities_${personId}`, async () => {
    const { data, error } = await supabase
      .from('department_roles')
      .select('id, department, role_name, churches!church_id ( name )')
      .eq('person_id', personId);
    if (error) { console.error('[API] getResponsibilities error:', error); throw error; }
    return (data || []).map(r => ({
      id: r.id,
      role: r.role_name,
      department: r.department,
      church_name: r.churches?.name || "Iglesia desconocida"
    }));
  }, []);
}

// =====================================================
// GET EVENTOS POR ID (FIX)
// =====================================================
const getEventById = async (id, context) => {
  if (!id) return null;
  const cacheKey = `event_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from('events').select('*').eq('id', id).single();
    if (error) { console.error(`GET ${context} ERROR:`, error); throw error; }
    if (data) await localCache.set(cacheKey, data);
    return data;
  } catch (err) {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList =
      await localCache.findInLists('events_church_', id) ||
      await localCache.findInLists('events_field_', id) ||
      await localCache.findInLists('events_personal_', id) ||
      await localCache.findInLists('events_church_dist_', id);
    if (fromList) return fromList;
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'events' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
};
export const getChurchEvent = (id) => getEventById(id, "CHURCH");
export const getFieldEvent = (id) => getEventById(id, "FIELD");
export const getPersonalEvent = (id) => getEventById(id, "PERSONAL");

// Obtener todas
export const getPrayers = async (input) => {
  const cacheKey = `prayers_${stableStringify(
    typeof input === 'string' ? { district_id: input } : (input || {})
  )}`;
  
  // Normalizamos los filtros para usarlos tanto en la query como en el merge local
  const filters = typeof input === "string" ? { district_id: input } : (input || {});
  const { district_id, family_id, person_id, status, church_id } = filters;

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error("Offline mode"), { __skipOnline: true });
    }

    let query = supabase.from("prayers").select("*, people!fk_prayers_person(id, name), families!fk_prayers_family(id, name)");

    if (district_id) query = query.eq("district_id", district_id);
    if (church_id) query = query.eq("church_id", church_id);
    if (family_id) query = query.eq("family_id", family_id);
    if (person_id) query = query.eq("person_id", person_id);
    if (status && status !== "todas") query = query.eq("status", status);

    // ⚠️ IMPORTANTE: Verifica que la columna se llame 'date' en Supabase.
    // Si el error que aparece en consola dice "column prayers.date does not exist", 
    // cámbialo aquí por 'created_at'.
    const { data, error } = await query.order("date", { ascending: false }).limit(200);

    if (error) {
      console.error("❌ SUPABASE ERROR (getPrayers):", error.message, error.details);
      throw error;
    }

    if (data) {
      await localCache.set(cacheKey, data);
      data.forEach(p => localCache.set(`prayer_${p.id}`, p).catch(() => {}));
    }

    const filterFn = (p) => {
      if (district_id && p.district_id !== district_id) return false;
      if (church_id && p.church_id !== church_id) return false;
      if (family_id && p.family_id !== family_id) return false;
      if (person_id && p.person_id !== person_id) return false;
      if (status && status !== "todas" && p.status !== status) return false;
      return true;
    };

    return await mergeOutboxItems(data || [], "prayers", filterFn);
  } catch (err) {
    console.warn("⚠️ Modo Offline o Error Crítico: Cargando oraciones desde caché", err);
    const cached = await localCache.get(cacheKey);

    const filterFn = (p) => {
      if (district_id && p.district_id !== district_id) return false;
      if (church_id && p.church_id !== church_id) return false;
      if (family_id && p.family_id !== family_id) return false;
      if (person_id && p.person_id !== person_id) return false;
      if (status && status !== "todas" && p.status !== status) return false;
      return true;
    };

    return await mergeOutboxItems(cached || [], "prayers", filterFn);
  }
};

// Obtener una
export const getPrayer = async (id) => {
  if (!id) return null;
  const cacheKey = `prayer_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from("prayers").select("*").eq("id", id).single();
    if (error) { console.error("GET PRAYER ERROR:", error); throw error; }
    if (data) await localCache.set(cacheKey, data);
    return data;
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList = await localCache.findInLists('prayers_', id);
    if (fromList) return fromList;
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'prayers' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
};

// Crear
export const createPrayer = async (payload) => {
  if (!payload?.district_id) return null;
  const { data, error } = await executeMutation({
    table: 'prayers',
    action: 'CREATE',
    payload,
    // Invalidamos la caché general del distrito y la específica de la iglesia si existe
    cacheKeysToInvalidate: [
      `prayers_${stableStringify({ district_id: payload.district_id })}`,
      payload.church_id ? `prayers_${stableStringify({ church_id: payload.church_id })}` : null,
    ].filter(Boolean)
  });
  if (error) throw error;
  if (data && !data._is_offline) logPrayer(data, payload.district_id); // best effort
  return data;
}

// Actualizar
export const updatePrayer = async (id, updates, { district_id, church_id } = {}) => {
  const { data, error } = await executeMutation({
    table: 'prayers',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeysToInvalidate: [
      district_id ? `prayers_${stableStringify({ district_id })}` : null,
      church_id ? `prayers_${stableStringify({ church_id })}` : null,
      `prayer_${id}`,
      JSON.stringify(["prayer-detail", id]),
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

// Oraciones aprobadas por el pastor visibles para los miembros
export const getCommunityPrayers = async (church_id) => {
  if (!church_id) return [];
  const cacheKey = `community_prayers_${church_id}`;
  try {
    const { data, error } = await supabase
      .from('prayers')
      .select('id, text, title, category, date, community_visible, people!fk_prayers_person(id, name), families!fk_prayers_family(id, name)')
      .eq('church_id', church_id)
      .eq('community_visible', true)
      .eq('status', 'activa')
      .order('date', { ascending: false });
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return await localCache.get(cacheKey) || [];
  }
};

// Marcar como respondida
export const answerPrayer = async (id, { district_id, church_id } = {}) => {
  return updatePrayer(id, { status: "respondida" }, { district_id, church_id });
}

// Eliminar
export const deletePrayer = async (id, { district_id, church_id } = {}) => {
  const { error, count } = await supabase
    .from('prayers')
    .delete({ count: 'exact' })
    .eq('id', id);

  if (error) throw error;
  if (count === 0) throw new Error('No se pudo eliminar: sin permisos o fila no encontrada');

  const keysToInvalidate = [
    district_id ? `prayers_${stableStringify({ district_id })}` : null,
    church_id   ? `prayers_${stableStringify({ church_id })}` : null,
    `prayer_${id}`,
  ].filter(Boolean);

  for (const key of keysToInvalidate) {
    await db.cache.delete(key).catch(() => {});
    invalidateQuery(key);
  }

  return true;
};
export const revertPrayer = async (id, { district_id, church_id } = {}) => {
  return updatePrayer(id, { status: "activa" }, { district_id, church_id });
};

// Peticiones pendientes de miembros para revisión del pastor
export const getPendingPrayerRequests = async (districtId, churchId) => {
  let q = supabase
    .from('prayer_requests')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });
  if (churchId) {
    q = q.eq('church_id', churchId);
  } else if (districtId) {
    const { data: churches } = await supabase
      .from('churches')
      .select('id')
      .eq('district_id', districtId);
    const ids = (churches || []).map(c => c.id);
    if (!ids.length) return [];
    q = q.in('church_id', ids);
  }
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
};

// Peticiones aprobadas por el pastor (ya publicadas en el mural)
export const getApprovedPrayerRequests = async (districtId, churchId) => {
  if (!districtId) return [];
  try {
    let churchIds = [];
    if (churchId) {
      churchIds = [churchId];
    } else {
      const { data: churches } = await supabase
        .from('churches')
        .select('id')
        .eq('district_id', districtId);
      churchIds = (churches || []).map(c => c.id);
    }
    if (!churchIds.length) return [];
    const { data, error } = await supabase
      .from('prayer_requests')
      .select('*')
      .eq('status', 'approved')
      .in('church_id', churchIds)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch { return []; }
};

// Aprobar petición → actualiza prayer_requests y crea oración en prayers
export const approvePrayerRequest = async (request, districtId) => {
  try {
    // 1. Actualizar status en prayer_requests
    const { error } = await supabase
      .from('prayer_requests')
      .update({ status: 'approved' })
      .eq('id', request.id);
    if (error) throw error;

    // 2. Crear oración en prayers si hay districtId disponible
    if (districtId) {
      await createPrayer({
        text:              request.content,
        district_id:       districtId,
        church_id:         request.church_id,
        status:            'activa',
        community_visible: true,
        title: request.is_anonymous
          ? 'Petición anónima'
          : request._displayName
          ? `Petición de ${request._displayName}`
          : null,
      });
    }
  } catch (e) {
    console.error('approvePrayerRequest:', e);
    throw e;
  }
};

// Descartar petición de oración
export const discardPrayerRequest = async (id) => {
  const { error } = await supabase
    .from('prayer_requests')
    .update({ status: 'discarded' })
    .eq('id', id);
  if (error) throw error;
};

// Toggle community_visible en prayers; churchId opcional para asignar iglesia al mismo tiempo
export const toggleCommunityVisible = async (id, value, churchId = undefined) => {
  const update = { community_visible: value };
  if (churchId !== undefined) update.church_id = churchId;
  const { error } = await supabase
    .from('prayers')
    .update(update)
    .eq('id', id);
  if (error) throw error;
};

// =====================================================

// =====================================================
// VISITAS - ÚLTIMA VISITA POR FAMILIA
// =====================================================
export const getLastVisitByFamily = async (family_id) => {
  if (!family_id) return null;
  return withOfflineCache(`last_visit_family_${family_id}`, async () => {
    const { data, error } = await supabase
      .from("visits")
      .select("*")
      .eq("target_family_id", family_id)
      .order("date", { ascending: false })
      .limit(1);
    if (error) throw error;
    return data?.[0] || null;
  });
};
// =====================================================
// FAMILIAS CON ÚLTIMA VISITA (RANKING)
// =====================================================
export const getFamiliesWithLastVisit = async (district_id, church_id = null) => {
  if (!district_id) return [];

  const cacheKey = church_id
    ? `families_rank_${district_id}_${church_id}`
    : `families_rank_${district_id}`;

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    let query = supabase.from("vw_families_rank").select("*").eq("district_id", district_id);
    if (church_id) query = query.eq("church_id", church_id);

    const { data: families, error: fErr } = await query;
    if (fErr) throw fErr;

    const enriched = (families || []).map((f) => ({
      ...f,
      last_visit: f.last_visit_date ? { date: f.last_visit_date } : null,
      days_since: f.last_visit_days ?? 999,
    }));

    await localCache.set(cacheKey, enriched);
    const outboxFilter = church_id
      ? (p) => p.church_id === church_id
      : (p) => p.district_id === district_id || p.churches?.district_id === district_id;
    return await mergeOutboxItems(enriched, 'families', outboxFilter);
  } catch (err) {
    console.warn('[API] getFamiliesWithLastVisit offline: cargando desde caché');
    const outboxFilter = church_id
      ? (p) => p.church_id === church_id
      : (p) => p.district_id === district_id || p.churches?.district_id === district_id;
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'families', outboxFilter);
  }
};
export const copyFieldEventToChurch = async (event, church_id) => {
  const payload = {
    title: event.title,
    date: event.date,
    end_date: event.end_date,
    time: event.time,
    event_type: event.event_type,
    notes: event.notes,
    district_id: event.district_id,
    church_id,
    type: "church",
  };
  const { data, error } = await executeMutation({
    table: 'events',
    action: 'CREATE',
    payload,
    cacheKeysToInvalidate: [
      `events_church_${church_id}`,
      event.district_id ? `events_church_dist_${event.district_id}` : null,
    ].filter(Boolean),
  });
  if (error) return null;
  return data;
};
export async function getLastVisitByPerson(personId) {
  const cacheKey = `last_visit_person_${personId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from("visits")
      .select("*")
      .eq("target_person_id", personId)
      .order("date", { ascending: false })
      .limit(1);

    if (error) throw error;
    const result = data?.[0] || null;
    if (result) await localCache.set(cacheKey, result);
    return result;
  } catch (err) {
    return await localCache.get(cacheKey) || null;
  }
}

// =====================================================
// MATERIALES
// =====================================================

export const getMaterials = async ({ district_id }) => {
  if (!district_id) return [];
  const cacheKey = `materials_district_${district_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('materials')
      .select('*, material_categories(id,name,icon,color), material_stock(quantity)')
      .eq('district_id', district_id)
      .is('deleted_at', null)
      .order('name', { ascending: true });
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    (data || []).forEach(m => localCache.set(`material_${m.id}`, m).catch(() => {}));
    return await mergeOutboxItems(data || [], 'materials', (p) => p.district_id === district_id);
  } catch (err) {
    if (navigator.onLine) console.error('[API] getMaterials:', err);
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'materials', (p) => p.district_id === district_id);
  }
};

export const getMaterialCategories = async ({ district_id }) => {
  if (!district_id) return [];
  const cacheKey = `material_categories_${district_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    // RPC seeds base categories for the district if none exist (idempotent),
    // migrates legacy department→category_id on first run, then returns all.
    const { data, error } = await supabase
      .rpc('get_or_seed_material_categories', { p_district_id: district_id });
    if (error) throw error;
    const categories = data || [];
    await localCache.set(cacheKey, categories);
    categories.forEach(c => localCache.set(`material_category_${c.id}`, c).catch(() => {}));
    return categories;
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const getMaterial = async (id) => {
  if (!id) return null;
  const cacheKey = `material_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('materials')
      .select('*, material_categories(id,name,icon,color), material_stock(quantity)')
      .eq('id', id)
      .single();
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data;
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList = await localCache.findInLists('materials_district_', id);
    if (fromList) return fromList;
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'materials' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
};

export const getMaterialMovements = async ({ material_id, district_id }) => {
  if (!material_id) return [];
  const cacheKey = `material_movements_${material_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('material_movements')
      .select('*')
      .eq('material_id', material_id)
      .eq('district_id', district_id)
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};


export const getLoanDetail = async (loanId) => {
  if (!loanId) return null;
  const cacheKey = `loan_detail_${loanId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('material_loans')
      .select('*, materials(id,name,unit)')
      .eq('id', loanId)
      .is('deleted_at', null)
      .single();
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data;
  } catch {
    return (await localCache.get(cacheKey)) || null;
  }
};

export const createMaterial = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'materials',
    action: 'CREATE',
    payload: {
      name: payload.name,
      description: payload.description || null,
      district_id: payload.district_id,
      category_id: payload.category_id || null,
      department: payload.department || 'general',
      unit_cost: payload.unit_cost ?? null,
      is_subsidized: Boolean(payload.is_subsidized),
      unit: payload.unit || null,
      type: payload.type || 'consumable',
      status: payload.status || 'active',
      min_stock: payload.min_stock || 0,
      image_url: payload.image_url || null,
    },
    cacheKeyToInvalidate: `materials_district_${payload.district_id}`,
  });
  if (error) throw error;
  return data;
};

export const updateMaterial = async (id, payload, { district_id } = {}) => {
  const { data, error, isOffline } = await executeMutation({
    table: 'materials',
    action: 'UPDATE',
    payload,
    targetId: id,
    cacheKeysToInvalidate: [
      `material_${id}`,
      district_id ? `materials_district_${district_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const checkMaterialCanDelete = async (id) => {
  const [{ data: allocs }, { data: subs }] = await Promise.all([
    supabase.from('church_material_allocations').select('id').eq('material_id', id).in('status', ['pending', 'delivered']).limit(1),
    supabase.from('church_subscriptions').select('id').eq('material_id', id).eq('status', 'active').limit(1),
  ]);
  if ((allocs && allocs.length > 0) || (subs && subs.length > 0)) {
    throw new Error('Este material tiene distribuciones o agendas activas. Cancélalas primero.');
  }
};

export const deleteMaterial = async (id, { district_id } = {}) => {
  const { error } = await executeMutation({
    table: 'materials',
    action: 'UPDATE',
    payload: { deleted_at: new Date().toISOString() },
    targetId: id,
    cacheKeysToInvalidate: [
      `material_${id}`,
      district_id ? `materials_district_${district_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
};

export const createMaterialMovement = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'material_movements',
    action: 'CREATE',
    payload: {
      material_id: payload.material_id,
      district_id: payload.district_id,
      movement_type: payload.movement_type,
      quantity: payload.quantity,
      reason: payload.reason || null,
      reference_type: payload.reference_type || null,
      reference_id: payload.reference_id || null,
      loan_id: payload.loan_id || null,
    },
    cacheKeysToInvalidate: [
      `material_movements_${payload.material_id}`,
      `material_${payload.material_id}`,
      `materials_district_${payload.district_id}`,
    ],
  });
  if (error) throw error;
  return data;
};


export const createMaterialCategory = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'material_categories',
    action: 'CREATE',
    payload: {
      name: payload.name,
      district_id: payload.district_id,
      icon: payload.icon || null,
      color: payload.color || null,
      order_index: payload.order_index || 0,
    },
    cacheKeyToInvalidate: `material_categories_${payload.district_id}`,
  });
  if (error) throw error;
  return data;
};

// =====================================================
// DISTRIBUCIÓN DE MATERIALES — church_material_allocations
// =====================================================

export const getChurchAllocations = async ({ district_id, church_id, status } = {}) => {
  if (!district_id) return [];
  const cacheKey = `church_alloc::${district_id}::${church_id || 'all'}::${status || 'all'}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    let query = supabase
      .from('church_material_allocations')
      .select('*, churches(id,name), materials(id,name,unit,type), church_material_payments(id,amount_paid)')
      .eq('district_id', district_id)
      .is('archived_at', null)
      .order('allocation_date', { ascending: false });
    if (church_id) query = query.eq('church_id', church_id);
    if (status)    query = query.eq('status', status);
    const { data, error } = await query;
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return await mergeOutboxItems(data || [], 'church_material_allocations', (p) => p.district_id === district_id);
  } catch {
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'church_material_allocations', (p) => p.district_id === district_id);
  }
};

export const getAllocationDetail = async (id) => {
  if (!id) return null;
  const cacheKey = `allocation_detail_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('church_material_allocations')
      .select('*, churches(id,name), materials(id,name,unit,type,unit_cost), church_material_payments(id,amount_paid,payment_date,payment_method,payment_route,notes,created_at)')
      .eq('id', id)
      .is('archived_at', null)
      .single();
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data;
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList = await localCache.findInLists('church_alloc::', id);
    if (fromList) return fromList;
    const outbox = await localOutbox.get();
    const pending = outbox.find(i => i.table === 'church_material_allocations' && i.action === 'CREATE' && i.payload?.id === id);
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
};

export const createChurchAllocation = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'church_material_allocations',
    action: 'CREATE',
    payload: {
      district_id:     payload.district_id,
      church_id:       payload.church_id,
      material_id:     payload.material_id,
      quantity:        payload.quantity,
      unit_price:      payload.unit_price ?? 0,
      allocation_date: payload.allocation_date || getLocalDateString(),
      status:          'pending',
      notes:           payload.notes || null,
      subscription_id: payload.subscription_id || null,
    },
    cacheKeysToInvalidate: [
      `church_alloc::${payload.district_id}::all::all`,
      `church_alloc::${payload.district_id}::${payload.church_id}::all`,
    ],
  });
  if (error) {
    if (navigator.onLine && error.message?.includes('Stock insuficiente')) {
      logInventoryIncident({
        district_id:   payload.district_id,
        incident_type: 'over_allocation_attempt',
        severity:      'warning',
        material_id:   payload.material_id,
        metadata: { requested_qty: payload.quantity, error: error.message },
      });
    }
    throw error;
  }
  return data;
};

export const updateChurchAllocation = async (id, payload, { district_id, church_id } = {}) => {
  const mutationPayload = {
    church_id:       payload.church_id,
    material_id:     payload.material_id,
    quantity:        payload.quantity,
    unit_price:      payload.unit_price ?? 0,
    allocation_date: payload.allocation_date,
    notes:           payload.notes || null,
  };
  const { data, error } = await executeMutation({
    table: 'church_material_allocations',
    action: 'UPDATE',
    payload: mutationPayload,
    targetId: id,
    cacheKeysToInvalidate: [
      `allocation_detail_${id}`,
      `church_alloc::${district_id}::all::all`,
      church_id ? `church_alloc::${district_id}::${church_id}::all` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const updateAllocationStatus = async (id, status, { district_id, church_id } = {}) => {
  const { data, error } = await executeMutation({
    table: 'church_material_allocations',
    action: 'UPDATE',
    payload: { status },
    targetId: id,
    cacheKeysToInvalidate: [
      `allocation_detail_${id}`,
      `church_alloc::${district_id}::all::all`,
      church_id ? `church_alloc::${district_id}::${church_id}::all` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

// Atomic cancel via DB RPC (handles stock correction internally).
// Offline path: queues UPDATE in outbox; if cached status was 'delivered',
// also queues a correction movement so stock is eventually returned on sync.
export const cancelChurchAllocation = async (id, { district_id, church_id, reason } = {}) => {
  const keysToInvalidate = [
    `allocation_detail_${id}`,
    `church_alloc::${district_id}::all::all`,
    church_id ? `church_alloc::${district_id}::${church_id}::all` : null,
  ].filter(Boolean);

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { error } = await supabase.rpc('cancel_material_allocation', {
      p_allocation_id: id,
      p_reason: reason || null,
    });
    if (error) throw error;
    const cached = await localCache.get(`allocation_detail_${id}`);
    const stockKeys = cached ? [
      `material_movements_${cached.material_id}`,
      `material_${cached.material_id}`,
      `materials_district_${cached.district_id}`,
    ] : [];
    for (const key of [...keysToInvalidate, ...stockKeys]) {
      await db.cache.delete(key).catch(() => {});
      invalidateQuery(key);
    }
    return { error: null };
  } catch (err) {
    if (!navigator.onLine || err?.__skipOnline) {
      const cached = await localCache.get(`allocation_detail_${id}`);
      await localOutbox.push({
        table: 'church_material_allocations',
        action: 'UPDATE',
        payload: { status: 'cancelled' },
        targetId: id,
        cacheKeys: keysToInvalidate,
        retries: 0,
        lastError: null,
        createdAt: Date.now(),
        lastAttemptAt: null,
      });
      // Stock was already deducted at delivery — queue a correction movement to return it.
      if (cached && cached.status === 'delivered') {
        const movementKeys = [
          `material_movements_${cached.material_id}`,
          `material_${cached.material_id}`,
          `materials_district_${cached.district_id}`,
        ];
        const correctionId = generateUUID();
        await localOutbox.push({
          table: 'material_movements',
          action: 'CREATE',
          payload: {
            id:                  correctionId,
            client_mutation_id:  correctionId,
            material_id:    cached.material_id,
            district_id:    cached.district_id,
            movement_type:  'correction',
            quantity:       cached.quantity,
            reason:         reason || `Cancelación de asignación ${id}`,
            reference_type: 'church_allocation',
            reference_id:   id,
          },
          cacheKeys: movementKeys,
          retries: 0,
          lastError: null,
          createdAt: Date.now(),
          lastAttemptAt: null,
        });
        for (const key of movementKeys) invalidateQuery(key);
      }
      for (const key of keysToInvalidate) invalidateQuery(key);
      return { error: null, isOffline: true };
    }
    return { error: err };
  }
};

// Reactivate a cancelled allocation back to pending. No stock movement — stock was already
// returned when it was cancelled, and will only be deducted again when delivered.
export const reactivateChurchAllocation = async (id, { district_id, church_id } = {}) => {
  const keysToInvalidate = [
    `allocation_detail_${id}`,
    `church_alloc::${district_id}::all::all`,
    church_id ? `church_alloc::${district_id}::${church_id}::all` : null,
  ].filter(Boolean);

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { error } = await supabase
      .from('church_material_allocations')
      .update({ status: 'pending' })
      .eq('id', id)
      .eq('status', 'cancelled');
    if (error) throw error;
    for (const key of keysToInvalidate) {
      await db.cache.delete(key).catch(() => {});
      invalidateQuery(key);
    }
    return { error: null };
  } catch (err) {
    if (!navigator.onLine || err?.__skipOnline) {
      await localOutbox.push({
        table: 'church_material_allocations',
        action: 'UPDATE',
        payload: { status: 'pending' },
        targetId: id,
        cacheKeys: keysToInvalidate,
        retries: 0,
        lastError: null,
        createdAt: Date.now(),
        lastAttemptAt: null,
      });
      for (const key of keysToInvalidate) invalidateQuery(key);
      return { error: null };
    }
    throw err;
  }
};

// Soft-delete a cancelled allocation: sets archived_at = NOW(). Preserves audit/ledger history.
export const archiveChurchAllocation = async (id, { district_id, church_id } = {}) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión. Conéctate para archivar esta distribución.');
  }
  const keysToInvalidate = [
    `allocation_detail_${id}`,
    district_id ? `church_alloc::${district_id}::all::all` : null,
    church_id   ? `church_alloc::${district_id}::${church_id}::all` : null,
  ].filter(Boolean);
  const { error } = await supabase
    .from('church_material_allocations')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id)
    .eq('status', 'cancelled');
  if (error) throw error;
  for (const key of keysToInvalidate) {
    await db.cache.delete(key).catch(() => {});
    invalidateQuery(key);
  }
};

// Hard delete for pending allocations only (no stock was deducted yet).
export const deleteChurchAllocation = async (id, { district_id, church_id } = {}) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión. Conéctate para eliminar esta distribución.');
  }

  const keysToInvalidate = [
    `allocation_detail_${id}`,
    district_id ? `church_alloc::${district_id}::all::all` : null,
    church_id   ? `church_alloc::${district_id}::${church_id}::all` : null,
  ].filter(Boolean);

  const { data: deleted, error } = await supabase
    .from('church_material_allocations')
    .delete()
    .eq('id', id)
    .eq('status', 'pending')
    .select('id');
  if (error) throw error;
  if (!deleted || deleted.length === 0) {
    throw new Error('No se pudo eliminar la distribución. Intenta de nuevo.');
  }
  for (const key of keysToInvalidate) {
    await db.cache.delete(key).catch(() => {});
    invalidateQuery(key);
  }
};

// Atomic deliver via DB RPC: updates status='delivered' AND creates movement_type='out'
// in a single transaction. The update_material_stock_on_movement trigger deducts stock
// and raises 'Stock insuficiente' if stock would go below zero.
//
// Offline path: queues the allocation UPDATE + movement CREATE separately.
// The outbox dependency graph (allocation_id in RELATIONAL_ID_FIELDS) ensures
// the movement CREATE waits for the allocation UPDATE to sync first.
// material_id and quantity are read from the allocation_detail cache if available.
export const deliverChurchAllocation = async (id, { district_id, church_id, reason } = {}) => {
  const allocationKeys = [
    `allocation_detail_${id}`,
    `church_alloc::${district_id}::all::all`,
    church_id ? `church_alloc::${district_id}::${church_id}::all` : null,
  ].filter(Boolean);

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { error } = await supabase.rpc('deliver_material_allocation', {
      p_allocation_id: id,
      p_reason: reason || null,
    });
    if (error) throw error;

    // Invalidate allocation + stock caches. material_id read from Dexie for movement-level
    // keys. materials_district is always invalidated even on cache miss so stock UI refreshes.
    const cached = await localCache.get(`allocation_detail_${id}`);
    const mat_id  = cached?.material_id;
    const dist_id = cached?.district_id || district_id;
    const movementKeys = [
      mat_id ? `material_movements_${mat_id}` : null,
      mat_id ? `material_${mat_id}` : null,
      `materials_district_${dist_id}`,
    ].filter(Boolean);

    for (const key of [...allocationKeys, ...movementKeys]) {
      await db.cache.delete(key);
      invalidateQuery(key);
    }
    return { error: null };
  } catch (err) {
    if (!navigator.onLine || err?.__skipOnline) {
      // Read allocation from cache so offline path can queue both writes.
      const cached = await localCache.get(`allocation_detail_${id}`);

      // Guard: already delivered — don't queue a second delivery.
      if (cached?.status === 'delivered') {
        return { error: null, isOffline: false };
      }

      let mat_id  = cached?.material_id;
      let qty     = cached?.quantity;
      const dist_id = cached?.district_id || district_id;

      // Fallback: scan district allocations list if detail cache is cold (first open).
      if ((!mat_id || !qty) && district_id) {
        const distList = (await localCache.get(`church_alloc::${district_id}::all::all`)) || [];
        const match = distList.find(a => a.id === id);
        if (match) {
          mat_id = mat_id || match.material_id;
          qty    = qty    || match.quantity;
        }
      }

      await localOutbox.push({
        table: 'church_material_allocations',
        action: 'UPDATE',
        payload: { status: 'delivered' },
        targetId: id,
        cacheKeys: allocationKeys,
        retries: 0,
        lastError: null,
        createdAt: Date.now(),
        lastAttemptAt: null,
      });

      if (mat_id && qty) {
        const movementKeys = [
          `material_movements_${mat_id}`,
          `material_${mat_id}`,
          `materials_district_${dist_id}`,
        ];
        // Stable ID so syncOfflineData upserts on client_mutation_id UNIQUE — no duplicate on retry.
        const movementId = generateUUID();
        await localOutbox.push({
          table: 'material_movements',
          action: 'CREATE',
          payload: {
            id:                 movementId,
            client_mutation_id: movementId,
            material_id:    mat_id,
            district_id:    dist_id,
            movement_type:  'out',
            quantity:       -Math.abs(qty),
            reason:         reason || `Entrega de asignación ${id}`,
            reference_type: 'church_allocation',
            reference_id:   id,
          },
          cacheKeys: movementKeys,
          retries: 0,
          lastError: null,
          createdAt: Date.now(),
          lastAttemptAt: null,
        });
        for (const key of movementKeys) invalidateQuery(key);
      }

      // Always invalidate district-level materials cache even if material_id is unknown.
      invalidateQuery(`materials_district_${dist_id}`);
      for (const key of allocationKeys) invalidateQuery(key);
      return { error: null, isOffline: true };
    }
    return { error: err };
  }
};

// Atomic delivered→paid via RPC (enforces state machine).
// Offline: queues direct UPDATE — no stock change, so the raw update is safe.
export const payChurchAllocation = async (id, { district_id, church_id } = {}) => {
  const keysToInvalidate = [
    `allocation_detail_${id}`,
    `church_alloc::${district_id}::all::all`,
    church_id ? `church_alloc::${district_id}::${church_id}::all` : null,
    // Also invalidate the per-church account view cache
    church_id ? `church_materials_account_${church_id}` : null,
  ].filter(Boolean);

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { error } = await supabase.rpc('pay_material_allocation', {
      p_allocation_id: id,
    });
    if (error) throw error;
    for (const key of keysToInvalidate) {
      await db.cache.delete(key).catch(() => {});
      invalidateQuery(key);
    }
    return { error: null };
  } catch (err) {
    if (!navigator.onLine || err?.__skipOnline) {
      const cached = await localCache.get(`allocation_detail_${id}`);
      if (cached?.status === 'paid') return { error: null, isOffline: false };

      await localOutbox.push({
        table: 'church_material_allocations',
        action: 'UPDATE',
        payload: { status: 'paid' },
        targetId: id,
        cacheKeys: keysToInvalidate,
        retries: 0,
        lastError: null,
        createdAt: Date.now(),
        lastAttemptAt: null,
      });
      for (const key of keysToInvalidate) invalidateQuery(key);
      return { error: null, isOffline: true };
    }
    return { error: err };
  }
};

// =====================================================
// PAGOS DE ASIGNACIONES — church_material_payments
// =====================================================

export const getChurchMaterialPayments = async ({ allocation_id }) => {
  if (!allocation_id) return [];
  const cacheKey = `allocation_payments_${allocation_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('church_material_payments')
      .select('*')
      .eq('allocation_id', allocation_id)
      .order('payment_date', { ascending: false });
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return await mergeOutboxItems(data || [], 'church_material_payments', (p) => p.allocation_id === allocation_id);
  } catch {
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'church_material_payments', (p) => p.allocation_id === allocation_id);
  }
};

// Idempotent: executeMutation uses upsert onConflict:'client_mutation_id'.
// Repeated submits within 10 s reuse the same UUID (window.__kevesMutationCache).
// Offline retries reuse the UUID stamped onto the outbox item at queue time.
export const createChurchMaterialPayment = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'church_material_payments',
    action: 'CREATE',
    payload: {
      allocation_id:  payload.allocation_id,
      district_id:    payload.district_id,
      amount_paid:    payload.amount_paid,
      payment_date:   payload.payment_date || getLocalDateString(),
      payment_method: payload.payment_method || 'cash',
      payment_route:  payload.payment_route  || 'to_district',
      notes:          payload.notes || null,
    },
    cacheKeysToInvalidate: [
      `allocation_payments_${payload.allocation_id}`,
      `allocation_detail_${payload.allocation_id}`,
      // Invalidate list-level caches so balances update immediately
      payload.district_id ? `church_alloc::${payload.district_id}::all::all` : null,
      payload.church_id   ? `church_alloc::${payload.district_id}::${payload.church_id}::all` : null,
      payload.district_id ? `district_payments_${payload.district_id}` : null,
      payload.district_id ? `district_remittances_${payload.district_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  // Invalidate React query layer (array key format used by useDistrictPayments)
  if (payload.district_id) invalidateQuery(["district-payments", payload.district_id]);
  return data;
};

// All payments for a district — used in financial dashboard totals.
export const getDistrictPayments = async (district_id) => {
  if (!district_id) return [];
  const cacheKey = `district_payments_${district_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('church_material_payments')
      .select('id,amount_paid,payment_route,payment_date,payment_method')
      .eq('district_id', district_id)
      .order('payment_date', { ascending: false });
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return await localCache.get(cacheKey) || [];
  }
};

// District remittances list.
export const getDistrictRemittances = async (district_id) => {
  if (!district_id) return [];
  const cacheKey = `district_remittances_${district_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('district_remittances')
      .select('*')
      .eq('district_id', district_id)
      .order('remittance_date', { ascending: false });
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return await localCache.get(cacheKey) || [];
  }
};

export const createDistrictRemittance = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'district_remittances',
    action: 'CREATE',
    payload: {
      district_id:    payload.district_id,
      amount:         payload.amount,
      remittance_date: payload.remittance_date || getLocalDateString(),
      status:         'pending',
      notes:          payload.notes || null,
    },
    cacheKeysToInvalidate: [
      `district_remittances_${payload.district_id}`,
    ],
  });
  if (error) throw error;
  // Invalidate React query layer (array key format used by useDistrictRemittances)
  if (payload.district_id) invalidateQuery(["district-remittances", payload.district_id]);
  return data;
};

export const confirmDistrictRemittance = async (id, district_id) => {
  const { data, error } = await executeMutation({
    table: 'district_remittances',
    action: 'UPDATE',
    payload: { status: 'confirmed' },
    targetId: id,
    cacheKeysToInvalidate: [
      `district_remittances_${district_id}`,
    ],
  });
  if (error) throw error;
  // Invalidate React query layer
  if (district_id) invalidateQuery(["district-remittances", district_id]);
  return data;
};

// =====================================================
// INVENTORY OBSERVABILITY
// =====================================================

// Log an inventory incident (fire-and-forget from catch blocks)
export const logInventoryIncident = async (payload) => {
  if (!navigator.onLine) return; // offline: skip logging silently
  try {
    await supabase.from('inventory_incidents').insert({
      district_id:   payload.district_id,
      incident_type: payload.incident_type,
      severity:      payload.severity || 'warning',
      material_id:   payload.material_id   || null,
      allocation_id: payload.allocation_id || null,
      metadata:      payload.metadata      || null,
    });
  } catch { /* swallow — logging must never break the main flow */ }
};

// Returns jsonb health snapshot via RPC
export const getInventoryHealth = async () => {
  try {
    const { data, error } = await supabase.rpc('get_inventory_health');
    if (error) throw error;
    return data;
  } catch {
    return null;
  }
};

// Returns all DRIFT rows from v_inventory_reconciliation for this district
export const getInventoryDrift = async (district_id) => {
  if (!district_id) return [];
  try {
    const { data, error } = await supabase
      .from('v_inventory_reconciliation')
      .select('*')
      .eq('district_id', district_id)
      .order('drift_qty', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch { return []; }
};

// Returns reservation anomalies (drift != 0) from v_reservation_balance
export const getReservationAnomalies = async (district_id) => {
  if (!district_id) return [];
  try {
    const { data, error } = await supabase
      .from('v_reservation_balance')
      .select('*')
      .eq('district_id', district_id)
      .neq('reservation_drift', 0);
    if (error) throw error;
    return data || [];
  } catch { return []; }
};

// Returns materials with effective_stock <= 0 AND physical_stock > 0 (over-reserved)
export const getOverReservedMaterials = async (district_id) => {
  if (!district_id) return [];
  try {
    const { data, error } = await supabase
      .from('v_material_stock_summary')
      .select('*')
      .eq('district_id', district_id)
      .lte('effective_stock', 0)
      .gt('physical_stock', 0);
    if (error) throw error;
    return data || [];
  } catch { return []; }
};

// Ledger history for a specific allocation (timeline events)
export const getAllocationLedgerHistory = async (allocation_id, district_id) => {
  if (!allocation_id) return [];
  try {
    let query = supabase
      .from('inventory_ledger')
      .select('*')
      .eq('allocation_id', allocation_id)
      .order('created_at', { ascending: true });
    if (district_id) query = query.eq('district_id', district_id);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch { return []; }
};

// =====================================================
// SUSCRIPCIONES — church_subscriptions
// =====================================================

export const getChurchSubscriptions = async ({ district_id, church_id, status } = {}) => {
  if (!district_id) return [];
  const cacheKey = `church_subscriptions_${district_id}${church_id ? `_c_${church_id}` : ''}${status ? `_s_${status}` : ''}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    let query = supabase
      .from('church_subscriptions')
      .select('*, churches(id,name), materials(id,name,unit,type)')
      .eq('district_id', district_id)
      .order('start_date', { ascending: false });
    if (!status) query = query.neq('status', 'cancelled');
    if (church_id) query = query.eq('church_id', church_id);
    if (status)    query = query.eq('status', status);
    const { data, error } = await query;
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return await mergeOutboxItems(data || [], 'church_subscriptions', (p) => p.district_id === district_id);
  } catch {
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'church_subscriptions', (p) => p.district_id === district_id);
  }
};

export const createChurchSubscription = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'church_subscriptions',
    action: 'CREATE',
    payload: {
      district_id:           payload.district_id,
      church_id:             payload.church_id,
      material_id:           payload.material_id,
      frequency:             payload.frequency || 'monthly',
      quantity_per_delivery: payload.quantity_per_delivery,
      unit_price:            payload.unit_price ?? 0,
      status:                'active',
      start_date:            payload.start_date || getLocalDateString(),
      next_delivery_date:    payload.next_delivery_date || null,
      notes:                 payload.notes || null,
    },
    cacheKeysToInvalidate: [
      `church_subscriptions_${payload.district_id}`,
      `church_subscriptions_${payload.district_id}_c_${payload.church_id}`,
    ],
  });
  if (error) throw error;
  return data;
};

export const getSubscriptionDetail = async (id) => {
  if (!id) return null;
  const cacheKey = `subscription_detail_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('church_subscriptions')
      .select('*, churches(id,name), materials(id,name,unit,type,unit_cost)')
      .eq('id', id)
      .single();
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data;
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached) return cached;
    const fromList = await localCache.findInLists('church_subscriptions_', id);
    if (fromList) return fromList;
    return null;
  }
};

export const updateChurchSubscription = async (id, payload, { district_id, church_id } = {}) => {
  const { data, error } = await executeMutation({
    table: 'church_subscriptions',
    action: 'UPDATE',
    payload,
    targetId: id,
    cacheKeysToInvalidate: [
      `church_subscriptions_${district_id}`,
      church_id ? `church_subscriptions_${district_id}_c_${church_id}` : null,
      `subscription_detail_${id}`,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const deleteChurchSubscription = async (id, { district_id, church_id } = {}) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión. Conéctate para eliminar esta suscripción.');
  }
  const { error: delErr } = await supabase
    .from('subscription_deliveries')
    .delete()
    .eq('subscription_id', id);
  if (delErr) throw delErr;
  const { error } = await supabase
    .from('church_subscriptions')
    .delete()
    .eq('id', id);
  if (error) throw error;
  const keysToInvalidate = [
    `subscription_detail_${id}`,
    district_id ? `church_subscriptions_${district_id}` : null,
    church_id ? `church_subscriptions_${district_id}_c_${church_id}` : null,
  ].filter(Boolean);
  for (const key of keysToInvalidate) {
    await db.cache.delete(key).catch(() => {});
    invalidateQuery(key);
  }
  invalidateQuery(["subscription-detail", id]);
  if (district_id) invalidateQuery(["church-subscriptions", district_id]);
  if (district_id && church_id) invalidateQuery(["church-subscriptions", district_id, church_id]);
};

// =====================================================
// ENTREGAS DE SUSCRIPCIÓN — subscription_deliveries
// =====================================================

export const getSubscriptionDeliveries = async ({ subscription_id }) => {
  if (!subscription_id) return [];
  const cacheKey = `subscription_deliveries_${subscription_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('subscription_deliveries')
      .select('*, church_material_allocations(id,status)')
      .eq('subscription_id', subscription_id)
      .order('delivery_date', { ascending: false });
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return await mergeOutboxItems(data || [], 'subscription_deliveries', (p) => p.subscription_id === subscription_id);
  } catch {
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'subscription_deliveries', (p) => p.subscription_id === subscription_id);
  }
};

export const createSubscriptionDelivery = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'subscription_deliveries',
    action: 'CREATE',
    payload: {
      subscription_id: payload.subscription_id,
      district_id:     payload.district_id,
      allocation_id:   payload.allocation_id || null,
      delivery_date:   payload.delivery_date || getLocalDateString(),
      quantity:        payload.quantity,
      status:          'pending',
      notes:           payload.notes || null,
    },
    cacheKeyToInvalidate: `subscription_deliveries_${payload.subscription_id}`,
  });
  if (error) throw error;
  return data;
};

export const markSubscriptionDeliveryDelivered = async (id, { subscription_id } = {}) => {
  const { data, error } = await executeMutation({
    table: 'subscription_deliveries',
    action: 'UPDATE',
    payload: { status: 'delivered' },
    targetId: id,
    cacheKeyToInvalidate: `subscription_deliveries_${subscription_id}`,
  });
  if (error) throw error;
  return data;
};

// Mark a subscription period as delivered — pure compliance record, no inventory impact.
// Creates a subscription_delivery with status='delivered' and allocation_id=null.
export const markSubscriptionPeriodDelivered = async (subscriptionId, { districtId, quantity, period, notes } = {}) => {
  if (!subscriptionId) throw new Error('subscription_id requerido');
  if (!period) throw new Error('period requerido');

  // Idempotency: if this period is already marked, return silently (online only)
  if (navigator.onLine) {
    try {
      const { data: existing } = await supabase
        .from('subscription_deliveries')
        .select('id, status')
        .eq('subscription_id', subscriptionId)
        .eq('period', period)
        .neq('status', 'cancelled')
        .maybeSingle();
      if (existing) return existing;
    } catch { /* ignore — proceed to insert */ }
  }

  const { data, error } = await executeMutation({
    table: 'subscription_deliveries',
    action: 'CREATE',
    payload: {
      subscription_id: subscriptionId,
      district_id:     districtId,
      allocation_id:   null,
      period,
      delivery_date:   getLocalDateString(),
      quantity:        Number(quantity) || 1,
      status:          'delivered',
      notes:           notes || null,
    },
    cacheKeyToInvalidate: `subscription_deliveries_${subscriptionId}`,
  });
  // 23505 = unique constraint violation: already marked for this period
  if (error?.code === '23505') return null;
  if (error) throw error;
  return data;
};

// Load subscription_deliveries for a subscription's active year.
// Returns all non-cancelled records for the given subscriptionIds so callers can
// match by explicit period key instead of inferring from date ranges.
export const getSubscriptionDeliveriesForMonth = async (subscriptionIds, districtId, month) => {
  if (!subscriptionIds?.length || !districtId || !month) return [];
  if (!navigator.onLine) return [];
  const year = month.slice(0, 4);
  try {
    const { data } = await supabase
      .from('subscription_deliveries')
      .select('id, subscription_id, status, delivery_date, period')
      .in('subscription_id', subscriptionIds)
      .gte('delivery_date', `${year}-01-01`)
      .lte('delivery_date', `${year}-12-31`);
    return data || [];
  } catch {
    return [];
  }
};

// =====================================================
// INVENTARIO LOCAL / PASTORAL — local_inventory_*
// Completamente desacoplado del flujo financiero.
// No toca: material_stock, material_movements, inventory_ledger,
//          church_material_allocations ni dashboards financieros.
// =====================================================

const _plog = process.env.NODE_ENV === 'development'
  ? (ev, data) => console.log('%c[pastoral-inv]', 'color:#0d9f9f;font-weight:bold', ev, data ?? '')
  : () => {};

// Returns the local inventory item for a material, plus all its movements.
// Null item means no local stock has been registered yet.
export const getLocalInventoryItem = async (materialId, districtId) => {
  if (!materialId || !districtId) return { item: null, movements: [] };
  const cacheKey = `local_inv_${materialId}_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline'), { __skipOnline: true });
    }
    const [itemRes, movRes] = await Promise.all([
      supabase
        .from('local_inventory_items')
        .select('*')
        .eq('material_id', materialId)
        .eq('district_id', districtId)
        .maybeSingle(),
      supabase
        .from('local_inventory_movements')
        .select('*')
        .eq('material_id', materialId)
        .eq('district_id', districtId)
        .order('movement_date', { ascending: false })
        .order('created_at',    { ascending: false })
        .limit(50),
    ]);
    const result = { item: itemRes.data ?? null, movements: movRes.data ?? [] };
    _plog('fetch:item', { materialId, movements: result.movements.length });
    await localCache.set(cacheKey, result);
    return result;
  } catch (err) {
    if (err?.__skipOnline || !navigator.onLine) {
      const cached = await localCache.get(cacheKey);
      if (cached) {
        _plog('cache:hit', { materialId, movements: cached.movements?.length });
        return cached;
      }
    }
    return { item: null, movements: [] };
  }
};

// Creates or updates the local inventory item for a material, then inserts
// a 'received' movement. The DB trigger keeps item.quantity in sync.
//
// Online:  upsert item → insert movement (two sequential calls)
// Offline: queue item CREATE + movement CREATE (FIFO in outbox ensures ordering)
export const createLocalInventoryEntry = async ({
  district_id, material_id, quantity, source_type, notes, movement_date,
}) => {
  if (!district_id || !material_id) throw new Error('district_id y material_id requeridos');
  if (!quantity || Number(quantity) <= 0) throw new Error('Cantidad inválida');

  const cacheKeys = [
    `local_inv_${material_id}_${district_id}`,
    `local_inv_district_${district_id}`,
    `material_${material_id}`,
  ];

  // Step 1: upsert the item (sets source_type / notes; quantity managed by trigger).
  // Always uses executeMutation so online timeouts get client_mutation_id idempotency
  // and offline enqueues to the outbox — no raw supabase.from() on this path.
  _plog('entry:enqueue', { material_id, quantity });
  await executeMutation({
    table: 'local_inventory_items',
    action: 'CREATE',
    payload: { district_id, material_id, quantity: 0, source_type: source_type || 'other', notes: notes || null },
    cacheKeysToInvalidate: cacheKeys,
  });

  // Step 2: insert movement — trigger updates item.quantity
  const { data, error } = await executeMutation({
    table: 'local_inventory_movements',
    action: 'CREATE',
    payload: {
      district_id,
      material_id,
      quantity:      Math.abs(Number(quantity)),
      movement_type: 'received',
      notes:         notes || null,
      movement_date: movement_date || getLocalDateString(),
    },
    cacheKeysToInvalidate: cacheKeys,
  });
  if (error) throw error;
  return data;
};

// Inserts a 'given', 'used', or 'adjusted' movement against an existing local inventory item.
// The DB trigger keeps item.quantity in sync.
// recipient_name is optional future-prep field (UI not yet wired, stored when provided).
export const createLocalInventoryMovement = async ({
  district_id, material_id, item_id, quantity, movement_type, notes, movement_date,
  recipient_name,
}) => {
  if (!district_id || !material_id) throw new Error('district_id y material_id requeridos');
  if (!quantity || Number(quantity) === 0) throw new Error('Cantidad inválida');
  if (!['given', 'used', 'adjusted'].includes(movement_type)) throw new Error('Tipo de movimiento inválido');

  // given and used both deduct stock (negative); adjusted preserves caller's sign
  const signedQty = ['given', 'used'].includes(movement_type)
    ? -Math.abs(Number(quantity))
    : Number(quantity);

  const cacheKeys = [
    `local_inv_${material_id}_${district_id}`,
    `local_inv_district_${district_id}`,
    `material_${material_id}`,
  ];

  _plog('movement:enqueue', { material_id, movement_type, signedQty });
  const { data, error } = await executeMutation({
    table: 'local_inventory_movements',
    action: 'CREATE',
    payload: {
      district_id,
      material_id,
      item_id:        item_id || null,
      quantity:       signedQty,
      movement_type,
      notes:          notes || null,
      movement_date:  movement_date || getLocalDateString(),
      recipient_name: recipient_name || null,
    },
    cacheKeysToInvalidate: cacheKeys,
  });
  if (error) {
    _plog('movement:error', { movement_type, error: error.message });
    throw error;
  }
  return data;
};

// Returns all local inventory items for the district (joined with material info)
// plus the 20 most recent movements across all materials.
// Used by the pastoral overview and Materials hub block.
export const getDistrictLocalInventory = async (districtId) => {
  if (!districtId) return { items: [], recentMovements: [] };
  const cacheKey = `local_inv_district_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline'), { __skipOnline: true });
    }
    const [itemsRes, movRes] = await Promise.all([
      supabase
        .from('local_inventory_items')
        .select('*, materials(id, name, unit)')
        .eq('district_id', districtId)
        .order('quantity', { ascending: false }),
      supabase
        .from('local_inventory_movements')
        .select('*, materials(id, name, unit)')
        .eq('district_id', districtId)
        .order('movement_date', { ascending: false })
        .order('created_at',    { ascending: false })
        .limit(20),
    ]);
    const result = {
      items:           itemsRes.data ?? [],
      recentMovements: movRes.data   ?? [],
    };
    _plog('district:fetch', { districtId, items: result.items.length });
    await localCache.set(cacheKey, result);
    return result;
  } catch (err) {
    if (err?.__skipOnline || !navigator.onLine) {
      const cached = await localCache.get(cacheKey);
      if (cached) {
        _plog('district:cache-hit', { districtId, items: cached.items?.length });
        return cached;
      }
    }
    return { items: [], recentMovements: [] };
  }
};

// Creates a 'pending' allocation (no stock deduction) and a linked subscription_delivery
// in two sequential executeMutation calls.
//
// Offline ordering: subscription_delivery.allocation_id is in RELATIONAL_ID_FIELDS, so
// the outbox dependency graph ensures the delivery CREATE syncs after the allocation
// CREATE resolves and its tempId is remapped to the real server ID.
//
// Rollback: if the delivery CREATE fails online (network error between the two calls),
// we best-effort cancel the orphan allocation so the ledger stays clean.
export const generateSubscriptionAllocation = async (subscriptionId) => {
  if (!subscriptionId) throw new Error('subscription_id requerido');
  if (process.env.NODE_ENV === 'development') console.log('[generateSubscriptionAllocation] start', { subscriptionId });

  // Try detail cache first, fall back to list cache on cold start.
  let sub = await localCache.get(`subscription_detail_${subscriptionId}`);
  if (!sub) sub = await localCache.findInLists('church_subscriptions_', subscriptionId);
  if (!sub) throw new Error('Suscripción no encontrada. Abre el detalle primero para cargar los datos.');

  const districtId = sub.district_id;
  const churchId   = sub.church_id;
  const today      = getLocalDateString();

  // Idempotency guard: reject if an active allocation already exists for this
  // subscription this month (mirrors idx_unique_subscription_month in DB).
  // Only possible online — offline the unique index on the server will reject it.
  if (navigator.onLine) {
    const thisMonth = today.slice(0, 7); // "YYYY-MM"
    const { data: existing } = await supabase
      .from('church_material_allocations')
      .select('id, status')
      .eq('subscription_id', subscriptionId)
      .gte('allocation_date', `${thisMonth}-01`)
      .lt('allocation_date', `${thisMonth}-31`)
      .neq('status', 'cancelled')
      .limit(1);
    if (existing && existing.length > 0) {
      throw new Error('Ya existe una distribución activa para esta suscripción este mes.');
    }
  }

  const allocKeys = [
    `church_alloc::${districtId}::all::all`,
    `church_alloc::${districtId}::${churchId}::all`,
  ];
  const deliveryKey  = `subscription_deliveries_${subscriptionId}`;
  const subDetailKey = `subscription_detail_${subscriptionId}`;
  const subListKey   = `church_subscriptions_${districtId}`;
  const subChurchKey = `church_subscriptions_${districtId}_c_${churchId}`;

  // Step 1: allocation — status 'pending', no stock movement
  const { data: alloc, error: allocError } = await executeMutation({
    table: 'church_material_allocations',
    action: 'CREATE',
    payload: {
      district_id:       districtId,
      church_id:         churchId,
      material_id:       sub.material_id,
      quantity:          sub.quantity_per_delivery,
      unit_price:        sub.unit_price ?? 0,
      allocation_date:   today,
      status:            'pending',
      subscription_id:   subscriptionId,
      distribution_type: 'subscription',
      notes:             null,
    },
    cacheKeysToInvalidate: allocKeys,
  });
  if (allocError) throw allocError;

  const allocationId    = alloc?.id ?? null;
  const isOfflineAlloc  = !!alloc?._is_offline;
  if (process.env.NODE_ENV === 'development') console.log('[generateSubscriptionAllocation] allocation created', { allocationId, isOfflineAlloc });

  // Step 2: subscription_delivery linked to the new allocation
  const { error: deliveryError } = await executeMutation({
    table: 'subscription_deliveries',
    action: 'CREATE',
    payload: {
      subscription_id: subscriptionId,
      district_id:     districtId,
      allocation_id:   allocationId,
      delivery_date:   today,
      quantity:        sub.quantity_per_delivery,
      status:          'pending',
      notes:           null,
    },
    cacheKeysToInvalidate: [deliveryKey, subDetailKey, subListKey, subChurchKey],
  });

  if (deliveryError) {
    // Online only: cancel the orphan allocation (best-effort). Offline items are already
    // paired in the outbox via the dependency graph, so no cleanup is needed there.
    if (!isOfflineAlloc && allocationId) {
      try {
        await cancelChurchAllocation(allocationId, { district_id: districtId, church_id: churchId });
      } catch { /* best-effort — swallow cleanup errors */ }
    }
    throw deliveryError;
  }

  if (process.env.NODE_ENV === 'development') console.log('[generateSubscriptionAllocation] done', { allocationId });
  return { allocationId };
};

// =====================================================
// REVISIÓN ADMINISTRATIVA
// =====================================================

const DEFAULT_ADMIN_TASKS = [
  'Visitación', 'Junta Directiva', 'Junta Administrativa',
  'Tesorería', 'Construcción', 'Reunión de Ancianos', 'Clubes', 'Secretaría',
];

const DEFAULT_DISTRICT_ADMIN_TASKS = [
  'Reunión de ancianos distrital',
  'Reunión de club juvenil',
  'Reunión de discípulos distrital',
  'Junta de juntas',
];

// Normalize a task name: NFC compose → trim edges → collapse internal whitespace.
// NFC ensures "e + combining accent" and "é" map to the same code point before
// the DB unaccent index compares them.
function normalizeTaskName(raw) {
  return raw.normalize('NFC').trim().replace(/\s+/g, ' ');
}

export const getAdministrativeTasks = async (districtId) => {
  if (!districtId) return [];
  const cacheKey = `admin_tasks_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('administrative_tasks')
      .select('*')
      .eq('district_id', districtId)
      .eq('is_district_task', false)
      .order('is_system', { ascending: false })
      .order('sort_order', { ascending: true });
    if (error) throw error;

    const systemTasks = (data || []).filter(t => t.is_system !== false);
    if (systemTasks.length === 0) {
      // First access — seed default tasks.  Each name is normalized before
      // insert.  The functional index (district_id, lower(name)) catches any
      // concurrent seed race at the DB level; we ignore 23505 violations.
      const seeds = DEFAULT_ADMIN_TASKS.map((taskName, i) => ({
        district_id:      districtId,
        name:             normalizeTaskName(taskName),
        sort_order:       i,
        is_system:        true,
        is_district_task: false,
      }));
      await supabase
        .from('administrative_tasks')
        .insert(seeds)
        .select()
        // A unique-violation (code 23505) means a concurrent seed already ran —
        // that is the desired outcome, so we swallow it silently.
        .then(({ error: e }) => { if (e && e.code !== '23505') throw e; });

      // Re-fetch to return the canonical server list (includes any custom tasks).
      const { data: fresh } = await supabase
        .from('administrative_tasks')
        .select('*')
        .eq('district_id', districtId)
        .eq('is_district_task', false)
        .order('is_system', { ascending: false })
        .order('sort_order', { ascending: true });
      const result = fresh || [];
      await localCache.set(cacheKey, result);
      return result;
    }
    await localCache.set(cacheKey, data);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

// Returns { data, duplicate } instead of throwing on expected uniqueness violations.
// duplicate=true means a task with the same accent+case-insensitive name already
// exists — callers should surface a friendly message rather than an exception.
export const createCustomAdminTask = async (districtId, name) => {
  if (!districtId || !name?.trim()) throw new Error('districtId and name required');
  const normalized = normalizeTaskName(name);
  const { data, error } = await supabase
    .from('administrative_tasks')
    .insert({ district_id: districtId, name: normalized, is_system: false, sort_order: 999 })
    .select()
    .single();
  if (error) {
    if (error.code === '23505') return { data: null, duplicate: true };
    throw error;
  }
  await localCache.delete(`admin_tasks_${districtId}`);
  return { data, duplicate: false };
};

export const deleteAdminTask = async (taskId, districtId) => {
  if (!taskId || !districtId) throw new Error('taskId and districtId required');
  const { error } = await supabase
    .from('administrative_tasks')
    .delete()
    .eq('id', taskId)
    .eq('district_id', districtId);
  if (error) throw error;
  await localCache.delete(`admin_tasks_${districtId}`);
};

export const getDistrictAdministrativeTasks = async (districtId) => {
  if (!districtId) return [];
  const cacheKey = `district_admin_tasks_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('administrative_tasks')
      .select('*')
      .eq('district_id', districtId)
      .eq('is_district_task', true)
      .order('is_system', { ascending: false })
      .order('sort_order', { ascending: true });
    if (error) throw error;

    const systemTasks = (data || []).filter(t => t.is_system !== false);
    if (systemTasks.length === 0) {
      const seeds = DEFAULT_DISTRICT_ADMIN_TASKS.map((taskName, i) => ({
        district_id:      districtId,
        name:             normalizeTaskName(taskName),
        sort_order:       i,
        is_system:        true,
        is_district_task: true,
      }));
      await supabase
        .from('administrative_tasks')
        .insert(seeds)
        .select()
        .then(({ error: e }) => { if (e && e.code !== '23505') throw e; });

      const { data: fresh } = await supabase
        .from('administrative_tasks')
        .select('*')
        .eq('district_id', districtId)
        .eq('is_district_task', true)
        .order('is_system', { ascending: false })
        .order('sort_order', { ascending: true });
      const result = fresh || [];
      await localCache.set(cacheKey, result);
      return result;
    }
    await localCache.set(cacheKey, data);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const createCustomDistrictAdminTask = async (districtId, name) => {
  if (!districtId || !name?.trim()) throw new Error('districtId and name required');
  const normalized = normalizeTaskName(name);
  const { data, error } = await supabase
    .from('administrative_tasks')
    .insert({ district_id: districtId, name: normalized, is_system: false, sort_order: 999, is_district_task: true })
    .select()
    .single();
  if (error) {
    if (error.code === '23505') return { data: null, duplicate: true };
    throw error;
  }
  await localCache.delete(`district_admin_tasks_${districtId}`);
  return { data, duplicate: false };
};

export const deleteDistrictAdminTask = async (taskId, districtId) => {
  if (!taskId || !districtId) throw new Error('taskId and districtId required');
  const { error } = await supabase
    .from('administrative_tasks')
    .delete()
    .eq('id', taskId)
    .eq('district_id', districtId);
  if (error) throw error;
  await localCache.delete(`district_admin_tasks_${districtId}`);
};

export const getAdministrativeReviewsForDistrict = async (districtId, year, month) => {
  if (!districtId) return [];
  const cacheKey = `district_admin_reviews_${districtId}_${year}_${month}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('administrative_reviews')
      .select('*')
      .eq('district_id', districtId)
      .is('church_id', null)
      .eq('year', year)
      .eq('month', month);
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const toggleDistrictAdministrativeReview = async ({
  id, districtId, taskId, year, month, completed, userId,
}) => {
  const now = new Date().toISOString();
  const cacheKey = `district_admin_reviews_${districtId}_${year}_${month}`;
  if (id) {
    return executeMutation({
      table: 'administrative_reviews',
      action: 'UPDATE',
      targetId: id,
      payload: {
        completed,
        completed_at: completed ? now : null,
        completed_by: completed ? (userId || null) : null,
      },
      cacheKeysToInvalidate: [cacheKey],
    });
  }
  return executeMutation({
    table: 'administrative_reviews',
    action: 'CREATE',
    payload: {
      district_id:  districtId,
      church_id:    null,
      task_id:      taskId,
      year,
      month,
      completed,
      completed_at: completed ? now : null,
      completed_by: completed ? (userId || null) : null,
    },
    cacheKeysToInvalidate: [cacheKey],
  });
};

export const getAdministrativeReviewsByMonth = async (districtId, year, month) => {
  if (!districtId) return [];
  const cacheKey = `admin_reviews_${districtId}_${year}_${month}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('administrative_reviews')
      .select('*')
      .eq('district_id', districtId)
      .not('church_id', 'is', null)
      .eq('year', year)
      .eq('month', month);
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const getAdministrativeReviewsForChurch = async (districtId, churchId, year, month) => {
  if (!districtId || !churchId) return [];
  const cacheKey = `admin_reviews_${districtId}_${churchId}_${year}_${month}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('administrative_reviews')
      .select('*')
      .eq('district_id', districtId)
      .eq('church_id', churchId)
      .eq('year', year)
      .eq('month', month);
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const toggleAdministrativeReview = async ({
  id, districtId, churchId, taskId, year, month, completed, userId,
}) => {
  const now = new Date().toISOString();
  if (id) {
    return executeMutation({
      table: 'administrative_reviews',
      action: 'UPDATE',
      targetId: id,
      payload: {
        completed,
        completed_at: completed ? now : null,
        completed_by: completed ? (userId || null) : null,
      },
      cacheKeysToInvalidate: [
        `admin_reviews_${districtId}_${year}_${month}`,
        `admin_reviews_${districtId}_${churchId}_${year}_${month}`,
      ],
    });
  }
  return executeMutation({
    table: 'administrative_reviews',
    action: 'CREATE',
    payload: {
      district_id: districtId,
      church_id:   churchId,
      task_id:     taskId,
      year,
      month,
      completed,
      completed_at: completed ? now : null,
      completed_by: completed ? (userId || null) : null,
    },
    cacheKeysToInvalidate: [
      `admin_reviews_${districtId}_${year}_${month}`,
      `admin_reviews_${districtId}_${churchId}_${year}_${month}`,
    ],
  });
};

// =====================================================
// PREDICACIÓN
// =====================================================
export const getPreachingRosters = async (districtId) => {
  if (!districtId) return [];
  const cacheKey = `preaching_rosters_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('preaching_rosters')
      .select('*')
      .eq('district_id', districtId)
      .order('date_start', { ascending: false });
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      data.forEach(r => localCache.set(`preaching_roster_${r.id}`, r).catch(() => {}));
    }
    return await mergeOutboxItems(data || [], 'preaching_rosters', (p) => p.district_id === districtId);
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached) return await mergeOutboxItems(cached, 'preaching_rosters', (p) => p.district_id === districtId);
    const local = await db.preaching_rosters.where('district_id').equals(districtId).reverse().sortBy('created_at');
    return await mergeOutboxItems(local || [], 'preaching_rosters', (p) => p.district_id === districtId);
  }
};

export const getPreachingRosterById = async (id) => {
  if (!id) return null;
  const cacheKey = `preaching_roster_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase.from('preaching_rosters').select('*').eq('id', id).single();
    if (error) throw error;
    if (data) await localCache.set(cacheKey, data);
    return data;
  } catch {
    return (await localCache.get(cacheKey)) ?? (await db.preaching_rosters.get(id)) ?? null;
  }
};

export const getPreachingRosterAssignments = async (rosterId) => {
  if (!rosterId) return [];
  const cacheKey = `preaching_assignments_${rosterId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('preaching_assignments')
      .select('*')
      .eq('roster_id', rosterId)
      .order('date', { ascending: true })
      .limit(500);
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      // Write to Dexie so assignPreacherWithConflictCheck can find server-fetched assignments.
      // Skip rows that have pending outbox mutations — don't overwrite optimistic local writes.
      if (data.length > 0) {
        const pendingIds = new Set(
          (await db.outbox.where('table').equals('preaching_assignments').toArray())
            .map(e => e.targetId).filter(Boolean)
        );
        const toWrite = data.filter(row => !pendingIds.has(row.id));
        if (toWrite.length > 0) await db.preaching_assignments.bulkPut(toWrite).catch(() => {});
      }
    }
    return await mergeOutboxItems(data || [], 'preaching_assignments', (p) => p.roster_id === rosterId);
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached) return await mergeOutboxItems(cached, 'preaching_assignments', (p) => p.roster_id === rosterId);
    const local = await db.preaching_assignments.where('roster_id').equals(rosterId).toArray();
    return await mergeOutboxItems(local || [], 'preaching_assignments', (p) => p.roster_id === rosterId);
  }
};

// Online: single upsert for roster + bulk upsert for assignments.
// Offline: push roster + assignments to outbox individually (dependency graph handles ordering).
export const createPreachingRosterWithSync = async (roster, assignments) => {
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    try {
      const { id: tempId, ...rosterPayload } = roster;
      const { data: rData, error: rErr } = await withTimeout(supabase
        .from('preaching_rosters')
        .upsert([{ ...rosterPayload, client_mutation_id: tempId }], { onConflict: 'client_mutation_id', ignoreDuplicates: false })
        .select(), 15000);
      if (rErr) throw rErr;
      const savedRoster = rData?.[0];
      if (!savedRoster?.id) throw new Error('Roster sin ID de servidor');

      const realId = savedRoster.id;
      // Replace tempId with real ID in Dexie
      await db.transaction('rw', db.preaching_rosters, db.preaching_assignments, async () => {
        if (realId !== tempId) {
          await db.preaching_rosters.delete(tempId);
          const tempAssignments = await db.preaching_assignments.where('roster_id').equals(tempId).toArray();
          if (tempAssignments.length > 0) {
            await db.preaching_assignments.bulkDelete(tempAssignments.map(a => a.id));
            await db.preaching_assignments.bulkAdd(tempAssignments.map(a => ({ ...a, roster_id: realId })));
          }
        }
        await db.preaching_rosters.put({ ...roster, ...savedRoster });
      });

      // Bulk upsert assignments — client_mutation_id uses the local assignment ID as a
      // stable idempotency key so retries (migration re-run, reconnect) are no-ops.
      const assignmentPayloads = assignments.map(({ id: localId, ...a }) => ({
        ...a,
        roster_id: realId,
        client_mutation_id: localId,
      }));
      if (assignmentPayloads.length > 0) {
        const { error: aErr } = await withTimeout(supabase
          .from('preaching_assignments')
          .upsert(assignmentPayloads, { onConflict: 'client_mutation_id', ignoreDuplicates: false }), 30000);
        if (aErr) {
          // Roster saved but assignments failed — push each assignment to outbox individually
          // so syncOfflineData can retry them with the real roster ID already known.
          for (const { client_mutation_id: localId, ...a } of assignmentPayloads) {
            await localOutbox.push({
              table: 'preaching_assignments',
              action: 'CREATE',
              payload: { ...a, roster_id: realId, client_mutation_id: localId },
              targetId: null,
              cacheKeys: [`preaching_assignments_${realId}`],
              retries: 0, lastError: aErr.message || null, createdAt: Date.now(), lastAttemptAt: null,
            });
          }
        }
      }

      await localCache.delete(`preaching_rosters_${roster.district_id}`);
      invalidateQuery(['preaching-rosters', roster.district_id]);
      return savedRoster;
    } catch (err) {
      console.warn('[preaching] Online create failed, queuing offline', err);
    }
  }

  // Offline path: outbox with dependency graph (assignments depend on roster CREATE)
  const rosterCacheKey = `preaching_rosters_${roster.district_id}`;
  await localOutbox.push({
    table: 'preaching_rosters',
    action: 'CREATE',
    payload: { ...roster, client_mutation_id: roster.id },
    targetId: null,
    cacheKeys: [rosterCacheKey, JSON.stringify(['preaching-rosters', roster.district_id])],
    retries: 0, lastError: null, createdAt: Date.now(), lastAttemptAt: null,
  });
  for (const assignment of assignments) {
    await localOutbox.push({
      table: 'preaching_assignments',
      action: 'CREATE',
      payload: { ...assignment, client_mutation_id: assignment.id },
      targetId: null,
      cacheKeys: [`preaching_assignments_${roster.id}`],
      retries: 0, lastError: null, createdAt: Date.now(), lastAttemptAt: null,
    });
  }
  invalidateQuery(['preaching-rosters', roster.district_id]);
  return roster;
};

export const createPreachingAssignment = async (assignment, { rosterId } = {}) => {
  const { id: localId, ...payload } = assignment;
  const { data, error } = await executeMutation({
    table:                 'preaching_assignments',
    action:                'CREATE',
    payload:               { ...payload, client_mutation_id: localId },
    targetId:              null,
    cacheKeysToInvalidate: [
      rosterId ? `preaching_assignments_${rosterId}` : null,
      rosterId ? `preaching_detail_${rosterId}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const updatePreachingAssignment = async (id, updates, { rosterId } = {}) => {
  const { data, error } = await executeMutation({
    table: 'preaching_assignments',
    action: 'UPDATE',
    payload: updates,
    targetId: id,
    cacheKeysToInvalidate: [
      rosterId ? `preaching_assignments_${rosterId}` : null,
      rosterId ? `preaching_detail_${rosterId}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const getPreachingsByPreacher = async (personId, districtId) => {
  const today = getLocalDateString(new Date());
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    try {
      const { data, error } = await withTimeout(
        supabase
          .from('preaching_assignments')
          .select('id, date, church_id, status, context_note')
          .eq('preacher_id', personId)
          .gte('date', today)
          .order('date', { ascending: true })
          .limit(8),
        3000
      );
      if (!error && data) {
        const churches = (await localCache.get(`churches_${districtId}`)) || [];
        const churchMap = Object.fromEntries(churches.map(c => [c.id, c.name]));
        return data.map(a => ({ ...a, church_name: churchMap[a.church_id] || null }));
      }
    } catch (_) { /* fall through */ }
  }
  const all = await db.preaching_assignments.toArray();
  const future = all.filter(a => a.preacher_id === personId && (a.date || '') >= today);
  future.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const churches = (await localCache.get(`churches_${districtId}`)) || [];
  const churchMap = Object.fromEntries(churches.map(c => [c.id, c.name]));
  return future.slice(0, 8).map(a => ({ ...a, church_name: churchMap[a.church_id] || null }));
};

// Returns the most recent past preaching date at a specific church for a given preacher.
export const getLastPreachingAtChurch = async (personId, churchId) => {
  if (!personId || !churchId) return null;
  const today = getLocalDateString();
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    try {
      const { data } = await withTimeout(
        supabase
          .from('preaching_assignments')
          .select('date')
          .eq('preacher_id', personId)
          .eq('church_id', churchId)
          .lte('date', today)
          .order('date', { ascending: false })
          .limit(1),
        5000
      );
      if (data?.[0]?.date) return data[0].date;
    } catch (_) { /* fall through to offline */ }
  }
  const all = await db.preaching_assignments.where('church_id').equals(churchId).toArray();
  const past = all
    .filter((a) => a.preacher_id === personId && a.date && a.date <= today)
    .sort((a, b) => b.date.localeCompare(a.date));
  return past[0]?.date ?? null;
};

export const deletePreachingAssignment = async (id, { rosterId } = {}) => {
  const { error } = await executeMutation({
    table: 'preaching_assignments',
    action: 'DELETE',
    targetId: id,
    cacheKeysToInvalidate: [
      rosterId ? `preaching_assignments_${rosterId}` : null,
      rosterId ? `preaching_detail_${rosterId}` : null,
    ].filter(Boolean),
  });
  if (error) console.warn('[preaching] deleteAssignment sync failed:', error);
};

export const updateRosterStatus = async (id, status, districtId) => {
  const { data, error } = await executeMutation({
    table: 'preaching_rosters',
    action: 'UPDATE',
    payload: { status },
    targetId: id,
    cacheKeysToInvalidate: [
      districtId ? `preaching_rosters_${districtId}` : null,
      districtId ? JSON.stringify(['preaching-rosters', districtId]) : null,
      `preaching_roster_${id}`,
      `preaching_detail_${id}`,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const deletePreachingRoster = async (id, districtId) => {
  // Cancel any pending outbox operations for this roster and its assignments.
  // Prevents orphan CREATE→UPDATE→DELETE round-trips in Supabase when a roster
  // that was never synced is deleted before it leaves the outbox.
  const localAssignmentIds = new Set(
    (await db.preaching_assignments.where('roster_id').equals(id).toArray()).map(a => a.id)
  );
  const allOutboxItems = await db.outbox.toArray();
  const orphanOutboxIds = allOutboxItems
    .filter(item =>
      (item.table === 'preaching_rosters' &&
        (item.payload?.id === id || item.targetId === id)) ||
      (item.table === 'preaching_assignments' &&
        (item.payload?.roster_id === id ||
          localAssignmentIds.has(item.targetId) ||
          localAssignmentIds.has(item.payload?.id)))
    )
    .map(item => item.id);
  if (orphanOutboxIds.length > 0) {
    await db.outbox.bulkDelete(orphanOutboxIds);
    notifyOutboxChange();
  }

  const { error } = await executeMutation({
    table: 'preaching_rosters',
    action: 'DELETE',
    targetId: id,
    cacheKeysToInvalidate: [
      districtId ? `preaching_rosters_${districtId}` : null,
      districtId ? JSON.stringify(['preaching-rosters', districtId]) : null,
      `preaching_roster_${id}`,
      `preaching_assignments_${id}`,
      `preaching_detail_${id}`,
    ].filter(Boolean),
  });
  if (error) throw error;
  await db.transaction('rw', db.preaching_rosters, db.preaching_assignments, async () => {
    await db.preaching_assignments.where('roster_id').equals(id).delete();
    await db.preaching_rosters.delete(id);
  });
  return true;
};

// One-time migration: push all Dexie-only rosters to Supabase.
// Guarded by localStorage flag so it runs once per device per district.
export const migrateLocalPreachings = async (districtId) => {
  if (!districtId) return;
  const MIGRATE_FLAG = `preaching_migrated_${districtId}`;
  if (safeLocalStorage.getItem(MIGRATE_FLAG)) return;
  try {
    const localRosters = await db.preaching_rosters
      .where('district_id').equals(districtId).toArray();
    if (localRosters.length === 0) {
      safeLocalStorage.setItem(MIGRATE_FLAG, '1');
      return;
    }
    for (const roster of localRosters) {
      const assignments = await db.preaching_assignments
        .where('roster_id').equals(roster.id).toArray();
      await createPreachingRosterWithSync(roster, assignments);
    }
    safeLocalStorage.setItem(MIGRATE_FLAG, '1');
  } catch (err) {
    console.warn('[preaching] Migration error, will retry next session:', err);
  }
};

// =====================================================
// DISTRICT SETTINGS
// Persists per-district key/value config (weekly_plan, custom_lists, etc.).
// Last-write-wins semantics — no dependency graph needed.
// Offline writes are queued in localStorage and flushed on reconnect.
// =====================================================

const DIRTY_SETTINGS_KEY = 'keves_dirty_settings';

function _readDirtySettings() {
  try { return JSON.parse(safeLocalStorage.getItem(DIRTY_SETTINGS_KEY) || '[]'); }
  catch { return []; }
}
function _writeDirtySettings(items) {
  safeLocalStorage.setItem(DIRTY_SETTINGS_KEY, JSON.stringify(items));
}

export const getDistrictSetting = async (districtId, key, defaultValue = null) => {
  if (!districtId || !key) return defaultValue;
  // Semantic key for baptism_goal_<year> → 'baptism_goal_<districtId>_<year>'
  // Generic key for all other settings    → 'district_setting_<districtId>_<key>'
  const cacheKey = key.startsWith('baptism_goal_')
    ? `baptism_goal_${districtId}_${key.slice('baptism_goal_'.length)}`
    : `district_setting_${districtId}_${key}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('district_settings')
      .select('value')
      .eq('district_id', districtId)
      .eq('key', key)
      .maybeSingle();
    if (error) throw error;
    if (data?.value !== undefined && data.value !== null) {
      await localCache.set(cacheKey, data.value);
      return data.value;
    }
    return defaultValue;
  } catch {
    const cached = await localCache.get(cacheKey);
    return (cached !== undefined && cached !== null) ? cached : defaultValue;
  }
};

export const setDistrictSetting = async (districtId, key, value) => {
  if (!districtId || !key) return;
  const cacheKey = key.startsWith('baptism_goal_')
    ? `baptism_goal_${districtId}_${key.slice('baptism_goal_'.length)}`
    : `district_setting_${districtId}_${key}`;
  await localCache.set(cacheKey, value);
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline'), { __skipOnline: true });
    }
    await supabase
      .from('district_settings')
      .upsert([{ district_id: districtId, key, value }], {
        onConflict: 'district_id,key',
        ignoreDuplicates: false,
      });
    // Clear stale dirty entry for this setting (sync succeeded)
    _writeDirtySettings(
      _readDirtySettings().filter(i => !(i.districtId === districtId && i.key === key))
    );
  } catch {
    // Queue for reconnect flush
    const rest = _readDirtySettings().filter(i => !(i.districtId === districtId && i.key === key));
    _writeDirtySettings([...rest, { districtId, key, value }]);
  }
};

export const syncDirtySettings = async () => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  const dirty = _readDirtySettings();
  if (!dirty.length) return;
  const succeeded = [];
  for (const item of dirty) {
    try {
      await supabase
        .from('district_settings')
        .upsert([{ district_id: item.districtId, key: item.key, value: item.value }], {
          onConflict: 'district_id,key',
          ignoreDuplicates: false,
        });
      succeeded.push(item);
    } catch {
      // keep for next retry
    }
  }
  if (succeeded.length > 0) {
    _writeDirtySettings(
      dirty.filter(i => !succeeded.some(s => s.districtId === i.districtId && s.key === i.key))
    );
  }
};

// =====================================================
// BAUTISMOS (baptism_records)
// =====================================================

// Construye la lista de claves para invalidación.
// Las Dexie keys (strings) se usan para localCache.delete.
// Las serialized query keys (JSON.stringify de arrays) se almacenan en el outbox
// para que syncOfflineData dispare invalidateQuery con la clave exacta que usan
// los suscriptores de useQueryCore (subscriben por JSON.stringify(key)).
const _baptismInvalidateKeys = (district_id, person_id) => ({
  dexieKeys: [
    `baptisms_${district_id}`,
    `baptism_person_${person_id}`,
    `people_district_${district_id}`,
  ],
  // Serialized array keys → invalidateQuery los reconoce y dispara suscriptores correctos
  queryKeys: [
    JSON.stringify(['baptism-records', district_id]),
    JSON.stringify(['district-people', district_id]),
    JSON.stringify(['person-detail', person_id]),
  ],
});

// Aplica invalidaciones completas (online path).
const _invalidateBaptismCaches = async (dexieKeys, queryKeys) => {
  for (const k of dexieKeys) await db.cache.delete(k);
  for (const k of queryKeys) invalidateQuery(k);
};

// Lista todos los registros de bautismo activos (deleted_at IS NULL) del distrito.
// Write-through: escribe baptism_<id> y baptism_person_<person_id> por cada registro.
export const getBaptismRecords = async ({ district_id, church_id = null }) => {
  if (!district_id) return [];
  const cacheKey = church_id
    ? `baptisms_${district_id}_${church_id}`
    : `baptisms_${district_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('baptism_records')
      .select('*, people!baptism_records_person_id_fkey(id,name,birth_date,baptism_status,spiritual_status,family_id,families!people_family_id_fkey(church_id))')
      .eq('district_id', district_id)
      .is('deleted_at', null)
      .order('baptism_date', { ascending: false })
      .limit(200);
    if (error) throw error;
    const filtered = church_id
      ? (data || []).filter(r => r.people?.families?.church_id === church_id)
      : (data || []);
    await localCache.set(cacheKey, filtered);
    filtered.forEach(r => {
      localCache.set(`baptism_${r.id}`, r).catch(() => {});
      if (r.person_id) localCache.set(`baptism_person_${r.person_id}`, r).catch(() => {});
    });
    return await mergeOutboxItems(filtered, 'baptism_records', (p) => p.district_id === district_id);
  } catch (err) {
    if (typeof navigator !== 'undefined' && navigator.onLine) console.error('[API] getBaptismRecords:', err);
    return await mergeOutboxItems(await localCache.get(cacheKey) || [], 'baptism_records', (p) => p.district_id === district_id);
  }
};

// Obtiene el registro de bautismo de una persona específica (null si no está bautizada).
// Fallback chain: baptism_person_<id> → scan manual de baptisms_* (por person_id) → outbox.
// NOTA: findInLists busca por item.id — no aplica aquí porque necesitamos item.person_id.
export const getBaptismRecordByPerson = async (person_id) => {
  if (!person_id) return null;
  const cacheKey = `baptism_person_${person_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('baptism_records')
      .select('*')
      .eq('person_id', person_id)
      .is('deleted_at', null)
      .maybeSingle();
    if (error) throw error;
    if (data) {
      await localCache.set(cacheKey, data);
      localCache.set(`baptism_${data.id}`, data).catch(() => {});
    } else {
      await localCache.set(cacheKey, null);
    }
    return data ?? null;
  } catch {
    const cached = await localCache.get(cacheKey);
    if (cached !== undefined) return cached ?? null;
    // Scan manual de todas las listas de distrito cacheadas.
    // findInLists(prefix, id) busca item.id === id — no sirve aquí porque buscamos person_id.
    const districtEntries = await db.cache.where('key').startsWith('baptisms_').toArray();
    for (const entry of districtEntries) {
      if (Array.isArray(entry.data)) {
        const found = entry.data.find(r => r.person_id === person_id && !r.deleted_at);
        if (found) return found;
      }
    }
    // Outbox: CREATE pendiente (bautismo aún no synced)
    const outbox = await localOutbox.get();
    const pending = outbox.find(i =>
      i.table === 'baptism_records' && i.action === 'CREATE' && i.payload?.person_id === person_id
    );
    return pending ? { ...pending.payload, _is_offline: true } : null;
  }
};

// Registra un bautismo (INSERT idempotente vía RPC).
// Online: llama register_baptism RPC (atómico: baptism_records + people.baptism_status).
// Offline: push al outbox + 4 optimistic updates de caches locales.
export const registerBaptism = async ({ person_id, district_id, church_id, baptism_date, notes, created_by }) => {
  const tempId = generateUUID();
  const offlinePayload = {
    id:            tempId,
    person_id,
    district_id,
    church_id:     church_id  || null,
    baptism_date,
    notes:         notes      || null,
    created_by:    created_by || null,
    deleted_at:    null,
    deleted_by:    null,
  };
  const { dexieKeys, queryKeys } = _baptismInvalidateKeys(district_id, person_id);

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }

    // RPC atómico: INSERT baptism_records ON CONFLICT + UPDATE people.baptism_status
    const { data, error } = await withTimeout(supabase.rpc('register_baptism', {
      p_id:           tempId,
      p_person_id:    person_id,
      p_district_id:  district_id,
      p_church_id:    church_id   || null,
      p_baptism_date: baptism_date,
      p_notes:        notes       || null,
      p_created_by:   created_by  || null,
    }), 15000);
    if (error) throw error;

    if (data) {
      await localCache.set(`baptism_${data.id}`, data);
      await localCache.set(`baptism_person_${person_id}`, data);
    }

    // Optimistic list update: prepend new record to the district baptism list BEFORE
    // triggering the subscriber refetch. This prevents a window where the list is empty
    // (deleted Dexie + slow network) and ensures setData always has something to show.
    // The subscriber-triggered getBaptismRecords will overwrite with the full joined record.
    const listKey    = `baptisms_${district_id}`;
    const cachedList = await localCache.get(listKey);
    const newEntry   = data ?? offlinePayload;
    const nextList   = Array.isArray(cachedList)
      ? [newEntry, ...cachedList.filter(r => r.id !== newEntry.id && r.person_id !== person_id)]
      : [newEntry];
    await localCache.set(listKey, nextList);

    // Invalidate the people and person caches (status changed), then notify all subscribers.
    // The baptism list is NOT deleted — it was just updated above.
    await db.cache.delete(`baptism_person_${person_id}`);
    await db.cache.delete(`people_district_${district_id}`);
    for (const k of queryKeys) invalidateQuery(k);

    return { data, error: null, isOffline: false };

  } catch (err) {
    const isOfflinePath = (typeof navigator !== 'undefined' && !navigator.onLine) ||
      err?.__skipOnline || err?.message === 'Request timeout';

    // cacheKeys del outbox incluye tanto Dexie keys (para tempId remapping en sync)
    // como serialized query keys (para disparar suscriptores via invalidateQuery tras sync).
    try {
      await localOutbox.push({
        table:        'baptism_records',
        action:       'CREATE',
        payload:      { ...offlinePayload, client_mutation_id: tempId },
        targetId:     null,
        cacheKeys:    [...dexieKeys, ...queryKeys],
        retries:      0,
        lastError:    null,
        createdAt:    Date.now(),
        lastAttemptAt: null,
      });
    } catch (storageErr) {
      return { data: null, error: storageErr, isOffline: false, isStorageError: true };
    }

    // Optimistic #1: baptism_records individual
    await localCache.set(`baptism_${tempId}`,           { ...offlinePayload, _is_offline: true });
    await localCache.set(`baptism_person_${person_id}`, { ...offlinePayload, _is_offline: true });

    // Optimistic #2: people_district_<id> — useBaptismCandidates / useBaptismMetrics
    const cachedPeople = await localCache.get(`people_district_${district_id}`);
    if (Array.isArray(cachedPeople)) {
      await localCache.set(`people_district_${district_id}`, cachedPeople.map(p =>
        p.id !== person_id ? p : {
          ...p,
          baptism_status: p.baptism_status === 'rebautizado' ? 'rebautizado' : 'bautizado',
          baptism_date,
        }
      ));
    }

    // Optimistic #3: person_detail_<id> compound (PersonDetail)
    const personCompound = await localCache.get(`person_detail_${person_id}`);
    if (personCompound?.person) {
      await localCache.set(`person_detail_${person_id}`, {
        ...personCompound,
        person: {
          ...personCompound.person,
          baptism_status: personCompound.person.baptism_status === 'rebautizado' ? 'rebautizado' : 'bautizado',
          baptism_date,
        },
      });
    }

    // Optimistic #4: fam_detail_compound_<familyId> (FamilyDetail badges)
    const familyId = personCompound?.person?.family_id ?? personCompound?.family?.id;
    if (familyId) {
      const familyCompound = await localCache.get(`fam_detail_compound_${familyId}`);
      if (familyCompound?.people) {
        await localCache.set(`fam_detail_compound_${familyId}`, {
          ...familyCompound,
          people: familyCompound.people.map(p =>
            p.id !== person_id ? p : {
              ...p,
              baptism_status: p.baptism_status === 'rebautizado' ? 'rebautizado' : 'bautizado',
              baptism_date,
            }
          ),
        });
      }
    }

    for (const k of queryKeys) invalidateQuery(k);

    return {
      data:      { ...offlinePayload, _is_offline: true },
      error:     isOfflinePath ? null : err,
      isOffline: true,
    };
  }
};

// Soft-delete de un registro de bautismo (NUNCA elimina físicamente).
// Online: llama remove_baptism RPC (atómico: deleted_at + people.baptism_status revert).
// Offline edge cases:
//   A) Hay un CREATE pendiente en outbox → cancelarlo. register→remove→register = 1 CREATE final.
//   B) Bautismo ya synced → push UPDATE con deleted_at.
export const removeBaptism = async ({ person_id, district_id, deleted_by }) => {
  const { dexieKeys, queryKeys } = _baptismInvalidateKeys(district_id, person_id);

  const existingRecord = await localCache.get(`baptism_person_${person_id}`);
  const recordId = existingRecord?.id;

  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline mode'), { __skipOnline: true });
    }

    // RPC atómico: soft-delete baptism_records + revert people.baptism_status
    const { error } = await withTimeout(supabase.rpc('remove_baptism', {
      p_person_id:  person_id,
      p_deleted_by: deleted_by || null,
    }), 15000);
    if (error) throw error;

    await localCache.set(`baptism_person_${person_id}`, null);
    if (recordId) localCache.set(`baptism_${recordId}`, null).catch(() => {});
    await _invalidateBaptismCaches(dexieKeys, queryKeys);
    return { error: null, isOffline: false };

  } catch (err) {
    const isOfflinePath = (typeof navigator !== 'undefined' && !navigator.onLine) ||
      err?.__skipOnline || err?.message === 'Request timeout';

    // Leer el outbox para detectar el caso A (CREATE pendiente sin sincronizar).
    // Edge case: register→remove→register offline → outbox debe terminar con 1 CREATE.
    const outbox = await localOutbox.get();
    const pendingCreate = outbox.find(i =>
      i.table === 'baptism_records' && i.action === 'CREATE' && i.payload?.person_id === person_id
    );

    if (pendingCreate) {
      // Caso A: cancelar el CREATE pendiente. No push de UPDATE — el server nunca vio ese id.
      await localOutbox.remove(pendingCreate.id);
    } else if (recordId) {
      // Caso B: soft-delete vía outbox UPDATE
      try {
        await localOutbox.push({
          table:     'baptism_records',
          action:    'UPDATE',
          payload:   { deleted_at: new Date().toISOString(), deleted_by: deleted_by || null },
          targetId:  recordId,
          cacheKeys: [...dexieKeys, ...queryKeys],
          retries:   0,
          lastError: null,
          createdAt: Date.now(),
          lastAttemptAt: null,
        });
      } catch (storageErr) {
        return { error: storageErr, isOffline: false, isStorageError: true };
      }
    }

    // Optimistic #1: baptism_records individual
    await localCache.set(`baptism_person_${person_id}`, null);
    if (recordId && existingRecord) {
      localCache.set(`baptism_${recordId}`, {
        ...existingRecord, deleted_at: new Date().toISOString(), _is_offline: true,
      }).catch(() => {});
    }

    // Optimistic #2: people_district_<id>
    const cachedPeople = await localCache.get(`people_district_${district_id}`);
    if (Array.isArray(cachedPeople)) {
      await localCache.set(`people_district_${district_id}`, cachedPeople.map(p =>
        p.id !== person_id ? p : { ...p, baptism_status: 'no_bautizado', baptism_date: null }
      ));
    }

    // Optimistic #3: person_detail_<id> compound
    const personCompound = await localCache.get(`person_detail_${person_id}`);
    if (personCompound?.person) {
      await localCache.set(`person_detail_${person_id}`, {
        ...personCompound,
        person: { ...personCompound.person, baptism_status: 'no_bautizado', baptism_date: null },
      });
    }

    // Optimistic #4: fam_detail_compound_<familyId>
    const familyId = personCompound?.person?.family_id ?? personCompound?.family?.id;
    if (familyId) {
      const familyCompound = await localCache.get(`fam_detail_compound_${familyId}`);
      if (familyCompound?.people) {
        await localCache.set(`fam_detail_compound_${familyId}`, {
          ...familyCompound,
          people: familyCompound.people.map(p =>
            p.id !== person_id ? p : { ...p, baptism_status: 'no_bautizado', baptism_date: null }
          ),
        });
      }
    }

    for (const k of queryKeys) invalidateQuery(k);

    return { error: isOfflinePath ? null : err, isOffline: true };
  }
};

// Actualiza la etapa discipular de una persona sin crear un baptism_record.
// Diseñado para: no_bautizado → en_proceso → de_blanco (nunca bautizado/rebautizado).
// Reutiliza el path offline/outbox de updatePerson y agrega invalidación del caché distrital.
export const updatePersonDiscipleshipStage = async ({ person_id, family_id, district_id, stage }) => {
  // Optimistic: actualizar caché distrital para feedback instantáneo en useBaptismCandidates/Metrics
  const districtCacheKey = `people_district_${district_id}`;
  const cachedPeople = await localCache.get(districtCacheKey);
  if (Array.isArray(cachedPeople)) {
    await localCache.set(districtCacheKey, cachedPeople.map((p) =>
      p.id !== person_id ? p : { ...p, baptism_status: stage }
    ));
  }

  // Delegar al path online/offline existente de updatePerson (outbox, family cache, etc.)
  await updatePerson(person_id, { baptism_status: stage }, family_id);

  // Invalidar queries distritales para que useBaptismCandidates/Metrics refetch
  invalidateQuery(JSON.stringify(['district-people', district_id]));
  invalidateQuery(JSON.stringify(['person-detail', person_id]));
};

// =====================================================
// BIBLE STUDY TRACKING

export const getBibleStudies = async ({ district_id, church_id } = {}) => {
  if (!district_id) return [];
  const cacheKey = church_id
    ? `bible_studies_district_${district_id}_church_${church_id}`
    : `bible_studies_district_${district_id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('bible_studies')
      .select(`
        id, person_id, district_id, course_name, total_lessons, current_lesson,
        teacher_name, teacher_person_id, start_date, updated_at,
        people!person_id (
          id, name, birth_date, baptism_status, spiritual_status, family_id,
          families!family_id ( church_id )
        )
      `)
      .eq('district_id', district_id)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    let result = data || [];
    if (church_id) {
      result = result.filter(s => s.people?.families?.church_id === church_id);
    }
    await localCache.set(cacheKey, result).catch(() => {});
    return result;
  } catch {
    const cached = (await localCache.get(cacheKey)) || [];
    return cached;
  }
};

export const getBibleStudy = async ({ person_id, district_id }) => {
  const cacheKey = `bible_study_${person_id}`;
  const cached = await localCache.get(cacheKey);
  if (cached) return cached;

  if (!navigator.onLine) return null;

  let query = supabase
    .from("bible_studies")
    .select("*")
    .eq("person_id", person_id);
  if (district_id) query = query.eq("district_id", district_id);
  const { data, error } = await query.maybeSingle();

  if (error) throw error;
  if (data) await localCache.set(cacheKey, data).catch(() => {});
  return data ?? null;
};

export const upsertBibleStudy = async (payload) => {
  const cacheKey = `bible_study_${payload.person_id}`;

  // Optimistic cache update for instant UI feedback
  const existing = (await localCache.get(cacheKey)) ?? {};
  const optimistic = { ...existing, ...payload, updated_at: new Date().toISOString() };
  await localCache.set(cacheKey, optimistic).catch(() => {});

  if (!navigator.onLine) return { ...optimistic, _is_offline: true };

  const { data, error } = await supabase
    .from("bible_studies")
    .upsert({ ...payload, updated_at: new Date().toISOString() }, { onConflict: "person_id" })
    .select()
    .single();

  if (error) throw error;
  await localCache.set(cacheKey, data).catch(() => {});
  return data;
};

export const deleteBibleStudy = async ({ person_id, district_id }) => {
  const cacheKey = `bible_study_${person_id}`;
  await localCache.delete(cacheKey).catch(() => {});

  if (!navigator.onLine) throw new Error("offline");

  let query = supabase
    .from("bible_studies")
    .delete()
    .eq("person_id", person_id);
  if (district_id) query = query.eq("district_id", district_id);
  const { error } = await query;

  if (error) throw error;
};

// =====================================================
// GRUPOS PEQUEÑOS
// =====================================================

export const getSmallGroups = async ({ district_id, church_id } = {}) => {
  if (!district_id) return [];
  const cacheKey = `small_groups_${district_id}${church_id ? `_c_${church_id}` : ''}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    let query = supabase
      .from('small_groups')
      .select('*, people!leader_person_id(id,name), churches(id,name), small_group_members(id,person_id)')
      .eq('district_id', district_id)
      .is('deleted_at', null)
      .order('name');
    if (church_id) query = query.eq('church_id', church_id);
    const { data, error } = await query;
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return (await localCache.get(cacheKey)) || [];
  }
};

export const getSmallGroup = async (id) => {
  if (!id) return null;
  const cacheKey = `small_group_${id}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('offline');
    const { data, error } = await supabase
      .from('small_groups')
      .select('*, people!leader_person_id(id,name), churches(id,name), small_group_members(id,person_id,people(id,name,baptism_status,spiritual_status))')
      .eq('id', id)
      .is('deleted_at', null)
      .single();
    if (error) throw error;
    await localCache.set(cacheKey, data);
    return data;
  } catch {
    return (await localCache.get(cacheKey)) || null;
  }
};

export const createSmallGroup = async (payload) => {
  const { data, error } = await executeMutation({
    table: 'small_groups',
    action: 'CREATE',
    payload: {
      district_id:      payload.district_id,
      church_id:        payload.church_id,
      name:             payload.name,
      slogan:           payload.slogan || null,
      leader_person_id: payload.leader_person_id || null,
      leader_name:      payload.leader_name || null,
      address:          payload.address || null,
      meeting_day:      payload.meeting_day || null,
      meeting_time:     payload.meeting_time || null,
      notes:            payload.notes || null,
      latitude:         payload.latitude  ?? null,
      longitude:        payload.longitude ?? null,
    },
    cacheKeysToInvalidate: [
      `small_groups_${payload.district_id}`,
      payload.church_id ? `small_groups_${payload.district_id}_c_${payload.church_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const updateSmallGroup = async (id, payload) => {
  const { data, error } = await executeMutation({
    table: 'small_groups',
    action: 'UPDATE',
    payload: {
      name:             payload.name,
      slogan:           payload.slogan || null,
      leader_person_id: payload.leader_person_id || null,
      leader_name:      payload.leader_name || null,
      address:          payload.address || null,
      meeting_day:      payload.meeting_day || null,
      meeting_time:     payload.meeting_time || null,
      notes:            payload.notes || null,
      latitude:         payload.latitude  ?? null,
      longitude:        payload.longitude ?? null,
      updated_at:       new Date().toISOString(),
    },
    targetId: id,
    cacheKeysToInvalidate: [
      `small_group_${id}`,
      payload.district_id ? `small_groups_${payload.district_id}` : null,
      payload.district_id && payload.church_id ? `small_groups_${payload.district_id}_c_${payload.church_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
  return data;
};

export const deleteSmallGroup = async (id, { district_id, church_id } = {}) => {
  const { error } = await executeMutation({
    table: 'small_groups',
    action: 'UPDATE',
    payload: { deleted_at: new Date().toISOString() },
    targetId: id,
    cacheKeysToInvalidate: [
      `small_group_${id}`,
      district_id ? `small_groups_${district_id}` : null,
      district_id && church_id ? `small_groups_${district_id}_c_${church_id}` : null,
    ].filter(Boolean),
  });
  if (error) throw error;
};

export const addGroupMember = async (group_id, person_id) => {
  const { data, error } = await executeMutation({
    table: 'small_group_members',
    action: 'CREATE',
    payload: {
      id:        generateUUID(),
      group_id,
      person_id,
    },
    cacheKeysToInvalidate: [`small_group_${group_id}`],
  });
  if (error) throw error;
  return data;
};

// member_id — the UUID of the small_group_members row (available from the group detail query).
// Required for the DELETE outbox path. Falls back to online-only composite delete if absent.
export const removeGroupMember = async (group_id, person_id, member_id) => {
  if (member_id) {
    const { error } = await executeMutation({
      table: 'small_group_members',
      action: 'DELETE',
      targetId: member_id,
      cacheKeysToInvalidate: [`small_group_${group_id}`],
    });
    if (error) throw error;
    return;
  }
  // Fallback: composite delete — requires network
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión para remover el miembro del grupo');
  }
  const { error } = await supabase
    .from('small_group_members')
    .delete()
    .eq('group_id', group_id)
    .eq('person_id', person_id);
  if (error) throw error;
  await localCache.delete(`small_group_${group_id}`);
};

// =====================================================
// DEV DEBUG — window.__offlineDebug (development only)
// Usage in browser console:
//   await window.__offlineDebug.outbox()
//   await window.__offlineDebug.cacheKeys()
//   await window.__offlineDebug.forceSync()
// =====================================================
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.__offlineDebug = {
    outbox: () => db.outbox.toArray(),
    cacheKeys: () => db.cache.toCollection().primaryKeys(),
    syncingNow: () => [...syncingIds],
    forceSync: syncOfflineData,
    clearCache: () => db.cache.clear(),
    clearOutbox: () => db.outbox.clear(),
    // Baptism test helpers
    registerBaptism,
    removeBaptism,
    getBaptismRecords,
    getBaptismRecordByPerson,
    cache: { get: (k) => localCache.get(k), keys: () => db.cache.where('key').startsWith('baptism').primaryKeys() },
  };
}


// =====================================================
// GASTOS DEL DISTRITO
// Bounded context aislado. NO tocar: material_stock,
// inventory_ledger, church_material_allocations,
// district_remittances, local_inventory_*.
// =====================================================

const _elog = process.env.NODE_ENV === 'development'
  ? (ev, data) => console.log('%c[district-exp]', 'color:#ea8028;font-weight:bold', ev, data ?? '')
  : () => {};

// Returns all expenses for the district, with their church splits.
export const getDistrictExpenses = async (districtId) => {
  if (!districtId) return [];
  const cacheKey = `exp_district_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('district_expenses')
      .select(`
        *,
        district_expense_churches (
          id, church_id, amount, status, created_at,
          churches (id, name)
        )
      `)
      .eq('district_id', districtId)
      .order('expense_date', { ascending: false })
      .order('created_at',   { ascending: false })
      .limit(100);
    if (error) throw error;
    const result = data ?? [];
    _elog('fetch', { districtId, count: result.length });
    await localCache.set(cacheKey, result);
    return result;
  } catch (err) {
    if (err?.__skipOnline || !navigator.onLine) {
      const cached = await localCache.get(cacheKey);
      if (cached) { _elog('cache-hit', { districtId }); return cached; }
    }
    return [];
  }
};

// Returns pending expense splits for a specific church.
export const getChurchExpenses = async (churchId, districtId) => {
  if (!churchId || !districtId) return [];
  const cacheKey = `exp_church_${churchId}_${districtId}`;
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw Object.assign(new Error('Offline'), { __skipOnline: true });
    }
    const { data, error } = await supabase
      .from('district_expense_churches')
      .select(`
        id, amount, status, created_at,
        district_expenses (id, description, expense_date, expense_type, notes, district_id)
      `)
      .eq('church_id', churchId)
      .eq('district_expenses.district_id', districtId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) throw error;
    // Filter out rows where the join returned null (wrong district)
    const result = (data ?? []).filter((r) => r.district_expenses !== null);
    _elog('church-fetch', { churchId, count: result.length });
    await localCache.set(cacheKey, result);
    return result;
  } catch (err) {
    if (err?.__skipOnline || !navigator.onLine) {
      const cached = await localCache.get(cacheKey);
      if (cached) { _elog('church-cache-hit', { churchId }); return cached; }
    }
    return [];
  }
};

// Creates an expense and its per-church splits in a single transaction.
// churches: [{ church_id, amount }]
export const createDistrictExpense = async ({
  district_id, description, total_amount, expense_date,
  expense_type, notes, churches,
}) => {
  if (!district_id || !description || !total_amount) {
    throw new Error('district_id, description y total_amount son requeridos');
  }
  if (!churches?.length) {
    throw new Error('Debes asociar al menos una iglesia');
  }
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Se requiere conexión para registrar gastos');
  }

  _elog('create', { district_id, description, total_amount, churches: churches.length });

  const { data: expense, error: expErr } = await supabase
    .from('district_expenses')
    .insert({
      district_id,
      description: description.trim(),
      total_amount: Number(total_amount),
      expense_date: expense_date || getLocalDateString(),
      expense_type: expense_type || null,
      notes: notes?.trim() || null,
    })
    .select()
    .single();
  if (expErr) throw expErr;

  const splits = churches.map((c) => ({
    expense_id: expense.id,
    church_id:  c.church_id,
    amount:     Number(c.amount),
    status:     'pending',
  }));

  const { error: splitsErr } = await supabase
    .from('district_expense_churches')
    .insert(splits);
  if (splitsErr) {
    // Best-effort rollback: delete the orphan expense
    await supabase.from('district_expenses').delete().eq('id', expense.id);
    throw splitsErr;
  }

  // Invalidate district and any affected church caches
  await localCache.delete(`exp_district_${district_id}`);
  for (const c of churches) {
    await localCache.delete(`exp_church_${c.church_id}_${district_id}`);
  }

  return expense;
};

// Updates an expense and replaces all its church splits.
export const updateDistrictExpense = async ({
  id, district_id, description, total_amount, expense_date,
  expense_type, notes, churches,
}) => {
  if (!id || !district_id) throw new Error('id y district_id son requeridos');
  if (!churches?.length) throw new Error('Debes asociar al menos una iglesia');
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Se requiere conexión para actualizar gastos');
  }

  const { data: expense, error: updErr } = await supabase
    .from('district_expenses')
    .update({
      description:  description.trim(),
      total_amount: Number(total_amount),
      expense_date: expense_date,
      expense_type: expense_type || null,
      notes:        notes?.trim() || null,
    })
    .eq('id', id)
    .eq('district_id', district_id)
    .select()
    .single();
  if (updErr) throw updErr;

  const { error: delErr } = await supabase
    .from('district_expense_churches')
    .delete()
    .eq('expense_id', id);
  if (delErr) throw delErr;

  const splits = churches.map((c) => ({
    expense_id: id,
    church_id:  c.church_id,
    amount:     Number(c.amount),
    status:     'pending',
  }));
  const { error: insErr } = await supabase
    .from('district_expense_churches')
    .insert(splits);
  if (insErr) throw insErr;

  await localCache.delete(`exp_district_${district_id}`);
  for (const c of churches) {
    await localCache.delete(`exp_church_${c.church_id}_${district_id}`);
  }

  return expense;
};

// Deletes an expense and all its church splits.
export const deleteDistrictExpense = async (expenseId, districtId) => {
  if (!expenseId) throw new Error('expenseId requerido');
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Se requiere conexión para eliminar gastos');
  }

  const { error: delSplits } = await supabase
    .from('district_expense_churches')
    .delete()
    .eq('expense_id', expenseId);
  if (delSplits) throw delSplits;

  const { error: delExp } = await supabase
    .from('district_expenses')
    .delete()
    .eq('id', expenseId);
  if (delExp) throw delExp;

  if (districtId) await localCache.delete(`exp_district_${districtId}`);
};

// Marks a single church split as paid.
export const markExpenseChurchPaid = async (splitId, { districtId, churchId }) => {
  if (!splitId) throw new Error('splitId requerido');
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión. Conéctate para registrar este pago.');
  }

  const { data, error } = await supabase
    .from('district_expense_churches')
    .update({ status: 'paid' })
    .eq('id', splitId)
    .select()
    .single();
  if (error) throw error;

  await localCache.delete(`exp_district_${districtId}`);
  if (churchId) await localCache.delete(`exp_church_${churchId}_${districtId}`);

  _elog('mark-paid', { splitId });
  return data;
};

// ─── PORTAL DE MEMBRESÍA ────────────────────────────────────────────────────

// Genera un código único de 8 chars para una iglesia
export const generateChurchAccessCode = async (churchId, districtId, churchName = '') => {
  // ── Derive 4-letter prefix from church name ───────────────────────────
  const STOP_WORDS = new Set([
    'iglesia', 'adventista', 'del', 'de', 'la', 'el', 'los', 'las',
  ]);

  const stripAccents = (str) =>
    str.normalize('NFD').replace(/[̀-ͯ]/g, '');

  const toAlpha = (str) =>
    stripAccents(str).toUpperCase().replace(/[^A-Z]/g, '');

  const words = stripAccents(churchName || '')
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.replace(/[^a-z]/g, ''))
    .filter((w) => w.length > 0 && !STOP_WORDS.has(w));

  let prefix;
  if (words.length === 0) {
    prefix = 'IGLE';
  } else if (words.length === 1) {
    prefix = toAlpha(words[0]).slice(0, 4).padEnd(4, 'X');
  } else {
    prefix = (toAlpha(words[0]).slice(0, 2) + toAlpha(words[1]).slice(0, 2)).padEnd(4, 'X');
  }

  // ── Find the next available 2-digit suffix (01–99) ────────────────────
  const { data: existing } = await supabase
    .from('churches')
    .select('access_code')
    .like('access_code', `${prefix}%`)
    .neq('id', churchId);

  const usedSuffixes = new Set(
    (existing || [])
      .map((r) => r.access_code)
      .filter((c) => c && c.length === 6 && c.startsWith(prefix))
      .map((c) => c.slice(4))
  );

  let suffix = null;
  for (let i = 1; i <= 99; i++) {
    const candidate = String(i).padStart(2, '0');
    if (!usedSuffixes.has(candidate)) { suffix = candidate; break; }
  }
  if (!suffix) throw new Error('No se pudo generar código único — todos los sufijos están en uso');

  const code = prefix + suffix;

  // ── Persist ───────────────────────────────────────────────────────────
  const { data, error } = await supabase
    .from('churches')
    .update({ access_code: code })
    .eq('id', churchId)
    .eq('district_id', districtId)
    .select('access_code')
    .single();
  if (error) throw error;

  // ── Patch Dexie cache in-place so stale code never shows ─────────────
  try {
    const listKey = `churches_${districtId}`;
    const cached = await localCache.get(listKey);
    if (Array.isArray(cached)) {
      const patched = cached.map(c =>
        c.id === churchId ? { ...c, access_code: code } : c
      );
      await localCache.set(listKey, patched);
    }
    const single = await localCache.get(`church_${churchId}`);
    if (single) {
      await localCache.set(`church_${churchId}`, { ...single, access_code: code });
    }
  } catch {}

  return data.access_code;
};

// Busca iglesia por código de acceso (para miembros)
export const getChurchByAccessCode = async (code) => {
  const { data, error } = await supabase
    .from('churches')
    .select('id, name, district_id, address')
    .eq('access_code', code.toUpperCase().trim())
    .maybeSingle();
  if (error) throw error;
  return data;
};

// Crea un anuncio
export const createAnnouncement = async (payload) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión. Conéctate para publicar el anuncio.');
  }
  const { data, error } = await supabase
    .from('announcements')
    .insert({
      district_id:  payload.district_id,
      church_id:    payload.church_id || null,
      church_ids:   payload.church_ids || null,
      created_by:   payload.created_by,
      title:        payload.title,
      body:         payload.body || null,
      image_url:    payload.image_url || null,
      published_at: payload.published_at || new Date().toISOString(),
      expires_at:   payload.expires_at || null,
      audience:     payload.audience || 'church',
    })
    .select()
    .single();
  if (error) throw error;
  await localCache.delete(`announcements_${payload.district_id}`);
  if (payload.church_id) await localCache.delete(`announcements_${payload.church_id}`);
  return data;
};

export const uploadAnnouncementFlyer = async (file, districtId) => {
  const ext  = file.name.split('.').pop();
  const path = `${districtId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('announcement-flyers')
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage
    .from('announcement-flyers')
    .getPublicUrl(path);
  return data.publicUrl;
};

export const getAnnouncements = async ({ district_id, church_id } = {}) => {
  if (!district_id && !church_id) return [];
  const cacheKey = church_id
    ? `announcements_${church_id}`
    : `announcements_${district_id}`;
  try {
    let query = supabase
      .from('announcements')
      .select('*')
      .is('deleted_at', null)
      .order('published_at', { ascending: false });
    if (church_id) query = query.eq('church_id', church_id);
    else query = query.eq('district_id', district_id);
    const { data, error } = await query;
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return await localCache.get(cacheKey) || [];
  }
};

export const deleteAnnouncement = async (id) => {
  const { error } = await supabase
    .rpc('soft_delete_announcement', { p_id: id });
  if (error) throw error;
};

export const updateAnnouncement = async (id, payload, districtId) => {
  const { data, error } = await executeMutation({
    table: 'announcements',
    action: 'UPDATE',
    payload: {
      title:      payload.title,
      body:       payload.body || null,
      image_url:  payload.image_url || null,
      expires_at: payload.expires_at || null,
      audience:   payload.audience || 'church',
      church_id:  payload.church_id || null,
      church_ids: payload.church_ids || null,
      updated_at: new Date().toISOString(),
    },
    targetId: id,
    cacheKeysToInvalidate: [`announcements_${districtId}`],
  });
  if (error) throw error;
  return data;
};

// Grupos pequeños por iglesia (sin district_id — para portal de miembros)
export const getSmallGroupsByChurch = async (church_id) => {
  if (!church_id) return [];
  const cacheKey = `small_groups_${church_id}`;
  try {
    const { data, error } = await supabase
      .from('small_groups')
      .select('id, name, slogan, meeting_day, meeting_time, address, leader_name, latitude, longitude')
      .eq('church_id', church_id)
      .is('deleted_at', null)
      .order('name');
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return await localCache.get(cacheKey) || [];
  }
};

// Predicaciones del mes actual + siguiente por iglesia
export const getPreachingByChurch = async (church_id) => {
  if (!church_id) return [];
  const now  = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const to   = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().slice(0, 10);
  const cacheKey = `preachings_church_${church_id}`;
  try {
    const { data, error } = await supabase
      .from('preaching_assignments')
      .select('id, date, preacher_id, preacher_name, service_type, ministry_focus, platform_members, deacons, context_note')
      .eq('church_id', church_id)
      .gte('date', from)
      .lte('date', to)
      .order('date');
    if (error) throw error;
    const rows = data || [];

    // Resolve preacher names for registered preachers (preacher_id set, preacher_name empty)
    const unresolvedIds = [...new Set(
      rows.filter(a => a.preacher_id && !a.preacher_name).map(a => a.preacher_id)
    )];
    if (unresolvedIds.length > 0) {
      const { data: people } = await supabase
        .from('people')
        .select('id, name')
        .in('id', unresolvedIds)
        .catch(() => ({ data: null }));
      if (Array.isArray(people)) {
        const nameMap = Object.fromEntries(
          people.map(p => [p.id, p.name ?? ''])
        );
        rows.forEach(a => {
          if (a.preacher_id && !a.preacher_name && nameMap[a.preacher_id]) {
            a.preacher_name = nameMap[a.preacher_id];
          }
        });
      }
    }

    await localCache.set(cacheKey, rows).catch(() => {});
    return rows;
  } catch {
    return (await localCache.get(cacheKey).catch(() => null)) || [];
  }
};

// ─── MENSAJES DE MIEMBROS AL PASTOR ─────────────────────────────────────────

export const sendMemberMessage = async ({ church_id, sender_name, phone, message }) => {
  const { error } = await supabase
    .from('member_messages')
    .insert({ church_id, sender_name: sender_name || null, phone: phone || null, message });
  if (error) throw error;

  fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/on-member-message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      church_id,
      message,
      sender_name: sender_name || null,
    }),
  }).catch(() => {}); // fire and forget
};

export const getMemberMessages = async (church_id) => {
  if (!church_id) return [];
  const { data, error } = await supabase
    .from('member_messages')
    .select('*')
    .eq('church_id', church_id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const markMessageRead = async (id) => {
  const { error } = await supabase
    .from('member_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
    .is('read_at', null);
  if (error) throw error;
};

export const deleteMemberMessage = async (id) => {
  const { error } = await supabase.rpc('delete_member_message', { message_id: id });
  if (error) throw error;
};

// Todos los mensajes del distrito (RLS filtra automáticamente)
export const getAllMemberMessages = async () => {
  const { data, error } = await supabase
    .from('member_messages')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

// Conteo de mensajes no leídos del distrito (para badge)
export const getUnreadMemberMessageCount = async () => {
  const { count, error } = await supabase
    .from('member_messages')
    .select('id', { count: 'exact', head: true })
    .is('read_at', null);
  if (error) return 0;
  return count || 0;
};

export const savePushSubscription = async (subscription, districtId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert(
      { user_id: user.id, district_id: districtId, subscription: subscription.toJSON() },
      { onConflict: 'user_id' }
    );
  if (error) throw error;
};

export const deletePushSubscription = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', user.id);
  if (error) throw error;
};

// Iglesias con coordenadas — sin district_id retorna todas (uso anónimo para descubrimiento)
export const getChurchesWithLocation = async (district_id) => {
  let query = supabase
    .from('churches')
    .select('id, name, address, latitude, longitude, service_schedule')
    .not('latitude', 'is', null);
  if (district_id) query = query.eq('district_id', district_id);
  query = query.limit(50);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// ── Kevia Member History ─────────────────────────────────────────
const KEVIA_HISTORY_LIMIT = 20;

export async function getMemberKeviaHistory(userId) {
  const { data, error } = await supabase
    .from("member_kevia_history")
    .select("id, role, content, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: true })
    .limit(KEVIA_HISTORY_LIMIT);
  if (error) throw error;
  return data || [];
}

export async function saveMemberKeviaMessage(userId, role, content) {
  const { error: insertError } = await supabase
    .from("member_kevia_history")
    .insert({ user_id: userId, role, content });
  if (insertError) throw insertError;

  // Auto-rotar: mantener solo los últimos 20 mensajes
  const { data: rows } = await supabase
    .from("member_kevia_history")
    .select("id, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (rows && rows.length > KEVIA_HISTORY_LIMIT) {
    const toDelete = rows.slice(KEVIA_HISTORY_LIMIT).map(r => r.id);
    await supabase
      .from("member_kevia_history")
      .delete()
      .in("id", toDelete);
  }
}

export async function clearMemberKeviaHistory(userId) {
  const { error } = await supabase
    .from("member_kevia_history")
    .delete()
    .eq("user_id", userId);
  if (error) throw error;
}

// ─── CHALLENGES ───────────────────────────────────────────────────────────────

export const getChallenges = async ({ district_id, church_id } = {}) => {
  if (!district_id && !church_id) return [];
  const cacheKey = church_id
    ? `challenges_${church_id}`
    : `challenges_${district_id}`;
  try {
    let query = supabase
      .from('challenges')
      .select('*')
      .is('deleted_at', null)
      .order('published_at', { ascending: false });
    if (church_id) query = query.eq('district_id', district_id).contains('church_ids', [church_id]);
    else query = query.eq('district_id', district_id);
    const { data, error } = await query;
    if (error) throw error;
    await localCache.set(cacheKey, data || []);
    return data || [];
  } catch {
    return await localCache.get(cacheKey) || [];
  }
};

export const createChallenge = async (payload) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sin conexión. Conéctate para publicar el reto.');
  }
  const { data, error } = await supabase
    .from('challenges')
    .insert({
      district_id:  payload.district_id,
      church_ids:   payload.church_ids || null,
      created_by:   payload.created_by,
      title:        payload.title,
      description:  payload.description || null,
      published_at: payload.published_at || new Date().toISOString(),
      expires_at:   payload.expires_at || null,
    })
    .select()
    .single();
  if (error) throw error;
  await localCache.delete(`challenges_${payload.district_id}`);
  return data;
};

export const updateChallenge = async (id, payload, districtId) => {
  const { data, error } = await executeMutation({
    table: 'challenges',
    action: 'UPDATE',
    payload: {
      title:       payload.title,
      description: payload.description || null,
      expires_at:  payload.expires_at || null,
      church_ids:  payload.church_ids || null,
      updated_at:  new Date().toISOString(),
    },
    targetId: id,
    cacheKeysToInvalidate: [`challenges_${districtId}`],
  });
  if (error) throw error;
  return data;
};

export const deleteChallenge = async (id) => {
  const { error } = await supabase
    .rpc('soft_delete_challenge', { p_id: id });
  if (error) throw error;
};